import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { RefObject } from 'react';
import { ReactAutoExpandTextArea } from './ReactAutoExpandTextArea';
import { ReactDateTimeInput, DateTimeKind } from './ReactDateTimeInput';

// Minimal ambient declaration for ResizeObserver — our TS `dom` lib predates
// it, but the API is universally available in every supported browser. We
// feature-detect with `typeof ResizeObserver !== 'undefined'` before using it.
interface ResizeObserver {
    observe(target: Element): void;
    unobserve(target: Element): void;
    disconnect(): void;
}
interface ResizeObserverConstructor {
    new (callback: (entries: any[], observer: ResizeObserver) => void): ResizeObserver;
}
declare var ResizeObserver: ResizeObserverConstructor;

/**
 * ECPHierarchyList - A specialized hierarchy component for eCP (electronic Cancer Protocol) forms.
 *
 * NOTE: Styles are provided by the parent JSP page or should be included separately.
 * See ECPHierarchyList.css for the required CSS classes.
 *
 * This component replaces ReactJSONHierarchy for the ECP use case, providing:
 * - Fast rendering with React state management instead of jQuery DOM manipulation
 * - Built-in support for ECP-specific elements (Sections, Questions, radio/checkbox options, numeric inputs, textareas, datetime fields)
 * - Keyboard navigation (arrow keys for focus, Tab/Shift+Tab to next/previous unanswered question)
 * - Automatic completion tracking and validation
 * - Support for repeatable sections/questions (minCard/maxCard)
 */

// ─────────────────────────────────────────────────────────���───────────────────
// Interfaces
// ──────────────��─────────────────────────────────�����──���──��────���────────────────

export interface ECPHierarchyProps {
    id?: string;
    jsonData: any;
    disabled?: boolean;
    css?: string;
    style?: React.CSSProperties;
    divContainerCSS?: string;
    divContainerStyle?: React.CSSProperties;
    expandAllOnLoad?: boolean;
    enableKeyboardNavigation?: boolean;
    disableHorizontalScrollOnNavigation?: boolean;
    focusFirstElementOnLoad?: boolean;
    refreshID?: string;
    disableID?: string;
    expandedID?: string;
    collapseID?: string;
    collapseCompleteID?: string;
    hiddenTotalCountID?: string;
    capIconPath?: string;
    eCPFormDebug?: boolean;
    onRefresh?: () => void;
    onSave?: (data: any) => void;
    onComplete?: (isComplete: boolean) => void;
    onValidationChange?: (hasErrors: boolean) => void;
    hiddenFormCompleteID?: string;
    showDateTimeClearButton?: boolean;
    militaryTime?: boolean;
    allowMilitaryTimeEntry?: boolean;
    repeatRemoveConfirmMessage?: string;
    repeatRemoveConfirmMessageTitle?: string;
}

interface ECPHierarchyState {
    translatedJSON: any;
    focusedHierarchyIndex?: number;
    isExpandingOnLoad?: boolean;
    rowCount?: number;
    bHideOptional?: boolean;
    isComplete?: boolean;
    validationErrors?: any[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions (ported from JSP)
// ─────────────────────���───────────────────────────────────────────────────────

const childTypes = ['Section', 'Question', 'Footer', 'ShellItem', 'ResponseField', 'ListItemResponseField', 'ListField', 'ListItem'];

function getWrapperChild(wrapper: any): { key: string; value: any } | null {
    if (!wrapper || typeof wrapper !== 'object') return null;
    for (let i = 0; i < childTypes.length; i++) {
        const key = childTypes[i];
        if (wrapper[key] != null) {
            return { key: key, value: wrapper[key] };
        }
    }
    return null;
}

function resolveWrapperObject(obj: any): any {
    const wrapped = getWrapperChild(obj);
    return wrapped ? wrapped.value : obj;
}

function boolVal(value: any): boolean {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return value === true;
}

function findPropertyByName(obj: any, propName: string): any {
    if (!obj || !propName) return null;
    const candidates: any[] = [];
    if (obj.Property) {
        if (Array.isArray(obj.Property)) candidates.push(...obj.Property);
        else candidates.push(obj.Property);
    }
    if (obj.Properties) {
        const ps = obj.Properties;
        if (Array.isArray(ps)) candidates.push(...ps);
        else if (Array.isArray(ps.Property)) candidates.push(...ps.Property);
        else if (ps.Property) candidates.push(ps.Property);
    }
    for (const p of candidates) {
        if (p && (p.propName === propName || p['@propName'] === propName)) return p;
    }
    return null;
}

function serializeXmlSubtree(node: any): string {
    if (node == null) return '';
    if (typeof node === 'string') return node;
    if (typeof node !== 'object') return String(node);
    if (Array.isArray(node)) return node.map(serializeXmlSubtree).join('');

    const TEXT_KEYS = new Set([
        '#text', '_text', 'text',
        '#cdata', '_cdata', '#cdata-section',
    ]);

    const NON_ATTRIBUTE_KEYS = new Set([
        // schema bookkeeping
        'name', 'order', 'ID', 'propName', 'val', 'shellType',
        'minCard', 'maxCard', 'mustImplement', 'selectionDeselectsSiblings',
        'maxSelections', 'responseRequired', 'TextAfterResponse',
        'ResponseUnits', 'overrideReason',
        // ECPHierarchyList runtime state
        'selected', 'isExpanded', 'complete', 'completeWithoutOverride',
        '_completeWithoutOverride', '_disabled', '_parentSelected', '_hasOptionalParent',
        '_insideShellItem', '_hideChildrenUntilRepeat',
        '_isRepeatInstance', '_isTopLevelRepeat', '_isRequiredMinCardInstance',
        '_instanceNumber', '_originalOrder', '_hasRestoredRepeatInstances',
    ]);

    let out = '';
    // Text and CDATA content sitting alongside child elements.
    if (typeof node['#text']  === 'string') out += node['#text'];
    if (typeof node._text     === 'string') out += node._text;
    if (typeof node.text      === 'string') out += node.text;
    if (typeof node['#cdata'] === 'string') out += '<![CDATA[' + node['#cdata'] + ']]>';
    if (typeof node._cdata    === 'string') out += '<![CDATA[' + node._cdata + ']]>';
    if (typeof node['#cdata-section'] === 'string') {
        out += '<![CDATA[' + node['#cdata-section'] + ']]>';
    }

    for (const key of Object.keys(node)) {
        if (TEXT_KEYS.has(key)) continue;
        if (NON_ATTRIBUTE_KEYS.has(key)) continue;            // runtime / SDC bookkeeping
        if (key.startsWith('@')) continue;                    // prefixed attr
        if (key.startsWith('_') && key !== '_text' && key !== '_cdata') continue;

        const child = (node as any)[key];

        if (typeof child === 'string'
            || typeof child === 'number'
            || typeof child === 'boolean') continue;

        const children = Array.isArray(child) ? child : [child];
        for (const c of children) {
            // Collect attributes off the child element
            let attrs = '';
            if (c && typeof c === 'object' && !Array.isArray(c)) {
                for (const ak of Object.keys(c)) {
                    if (TEXT_KEYS.has(ak)) continue;
                    if (NON_ATTRIBUTE_KEYS.has(ak)) continue; // skip runtime junk
                    const av = (c as any)[ak];
                    if (av != null && typeof av === 'object') continue; // child element, not attr
                    if (ak.startsWith('@')) {
                        attrs += ' ' + ak.substring(1)
                              + '="' + String(av).replace(/"/g, '&quot;') + '"';
                    } else if (ak.startsWith('_')
                            && ak !== '_text' && ak !== '_cdata') {
                        attrs += ' ' + ak.substring(1)
                              + '="' + String(av).replace(/"/g, '&quot;') + '"';
                    } else if (typeof av === 'string'
                            || typeof av === 'number'
                            || typeof av === 'boolean') {
                        // JSP flat shape attribute.
                        attrs += ' ' + ak
                              + '="' + String(av).replace(/"/g, '&quot;') + '"';
                    }
                }
            }
            const inner = serializeXmlSubtree(c);
            if (inner === '') out += '<' + key + attrs + '/>';
            else out += '<' + key + attrs + '>' + inner + '</' + key + '>';
        }
    }
    return out;
}

function extractTypedMarkup(node: any): string {
    if (node == null) return '';
    if (typeof node === 'string') return node;
    if (typeof node !== 'object') return String(node);
    if (Array.isArray(node)) {
        for (const item of node) {
            const m = extractTypedMarkup(item);
            if (m) return m;
        }
        return '';
    }

    // Peel a TypedValue wrapper (the SDC HTML / XML response layering).
    const tv = node.TypedValue || node.typedValue;
    if (tv) {
        if (tv.HTML != null) return extractTypedMarkup(tv.HTML);
        if (tv.html != null) return extractTypedMarkup(tv.html);
        if (tv.XML  != null) return extractTypedMarkup(tv.XML);
        if (tv.xml  != null) return extractTypedMarkup(tv.xml);
        if (tv.string != null) return extractTypedMarkup(tv.string);
        return extractTypedMarkup(tv);
    }

    if (node.HTML != null && node.HTML !== node && typeof node.HTML === 'object') {
        return extractTypedMarkup(node.HTML);
    }
    if (node.html != null && node.html !== node && typeof node.html === 'object') {
        return extractTypedMarkup(node.html);
    }
    if (node.XML  != null && node.XML  !== node && typeof node.XML  === 'object') {
        return extractTypedMarkup(node.XML);
    }
    if (node.xml  != null && node.xml  !== node && typeof node.xml  === 'object') {
        return extractTypedMarkup(node.xml);
    }

    const TEXT_KEYS = new Set([
        '#text', '_text', 'text',
        '#cdata', '_cdata', '#cdata-section',
        'val',
    ]);
    let hasElementChildren = false;
    for (const k of Object.keys(node)) {
        if (TEXT_KEYS.has(k)) continue;
        const v = (node as any)[k];
        if (v != null && typeof v === 'object') { hasElementChildren = true; break; }
    }

    if (!hasElementChildren) {
        if (typeof node['#text']  === 'string') return node['#text'];
        if (typeof node._text     === 'string') return node._text;
        if (typeof node.text      === 'string') return node.text;
        if (typeof node['#cdata'] === 'string') return node['#cdata'];
        if (typeof node._cdata    === 'string') return node._cdata;
        if (typeof node['#cdata-section'] === 'string') return node['#cdata-section'];
        if (typeof node.val === 'string') return node.val;
    }

    // Mixed or element-only content — serialize the subtree.
    return serializeXmlSubtree(node);
}

/** Extract the HTML markup associated with a Property's TypedValue, if any. */
function extractPropertyHtml(prop: any): string {
    if (!prop) return '';
    const tv = prop.TypedValue || prop.typedValue;
    if (tv && (tv.HTML != null || tv.html != null)) {
        return extractTypedMarkup(tv.HTML != null ? tv.HTML : tv.html);
    }
    // Some converters flatten TypedValue away
    if (prop.HTML != null) return extractTypedMarkup(prop.HTML);
    return '';
}

/** Extract the XML markup associated with a Property's TypedValue, if any. */
function extractPropertyXml(prop: any): string {
    if (!prop) return '';
    const tv = prop.TypedValue || prop.typedValue;
    if (tv && (tv.XML != null || tv.xml != null)) {
        return extractTypedMarkup(tv.XML != null ? tv.XML : tv.xml);
    }
    if (prop.XML != null) return extractTypedMarkup(prop.XML);
    return '';
}

/** Tiny allowlist HTML sanitizer used before `dangerouslySetInnerHTML`.
 *  Per the SDC spec the markup MUST be XHTML, so we keep this conservative:
 *  strip <script>/<style>/<iframe>/<object>/<embed>/<link>/<meta>, drop any
 *  on* event-handler attributes, and neutralize `javascript:` URLs.  This
 *  is intentionally simpler than a full sanitizer like DOMPurify — host
 *  pages that need stronger guarantees should wrap their own. */
function sanitizeSdcHtml(html: string): string {
    if (!html) return '';
    let s = String(html);
    // Drop dangerous elements + their content
    s = s.replace(
        /<\s*(script|style|iframe|object|embed|link|meta)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi,
        ''
    );
    // Drop self-closing variants of the same
    s = s.replace(
        /<\s*(script|style|iframe|object|embed|link|meta)\b[^>]*\/?>/gi,
        ''
    );
    // Remove on*= event-handler attributes (e.g. onclick="…", onerror='…')
    s = s.replace(/\son[a-zA-Z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    // Neutralize javascript: in href/src
    s = s.replace(
        /(\s(?:href|src|xlink:href)\s*=\s*['"])\s*javascript:/gi,
        '$1about:blank#blocked-javascript:'
    );
    return s;
}

/** Return the array of ShellItem objects contained in a List (or ListField.List). */
function getShellItemsFromList(listObj: any): any[] {
    if (!listObj) return [];
    const list = listObj.List ? listObj.List : listObj;
    if (!list || !Array.isArray(list.ChildItems)) return [];
    const items: any[] = [];
    list.ChildItems.forEach((wrapper: any) => {
        const child = getWrapperChild(wrapper);
        if (child && child.key === 'ShellItem' && child.value) items.push(child.value);
    });
    return items;
}

/** Parse `order` as a number; items without an order sort to the end.
 *  For repeat-section clones and their trailing "+" placeholder, we prefer
 *  `_originalOrder` so every instance (and the placeholder) sorts to the
 *  same slot that the source section occupied in the author's original
 *  XML order — i.e. if the source was the 3rd accordion, all its repeats
 *  and the "+" appear as the 3rd, 4th, 5th … accordions, not at the end. */
function getOrderNum(item: any): number {
    if (!item) return Number.MAX_SAFE_INTEGER;
    const raw = item._originalOrder != null ? item._originalOrder : item.order;
    if (raw == null) return Number.MAX_SAFE_INTEGER;
    const n = parseInt(raw, 10);
    return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
}

/** Secondary sort key for repeat siblings that share the same _originalOrder:
 *  original section (no _isRepeatInstance) stays first (0), then instances
 *  sorted by _instanceNumber (1, 2, 3, …), and the trailing "+" placeholder
 *  last (Number.MAX_SAFE_INTEGER).
 */
function getRepeatSubOrder(item: any): number {
    if (!item) return 0;
    if (item._hideChildrenUntilRepeat === true) return Number.MAX_SAFE_INTEGER;
    if (item._isRepeatInstance === true) {
        const n = parseInt(item._instanceNumber, 10);
        return isNaN(n) ? 1 : n;
    }
    return 0;
}

/** Recursively sort every ChildItems array by `order`. */
function sortJSONByOrder(obj: any): void {
    const cmp = (a: any, b: any): number => {
        const va = resolveWrapperObject(a);
        const vb = resolveWrapperObject(b);
        const diff = getOrderNum(va) - getOrderNum(vb);
        if (diff !== 0) return diff;
        return getRepeatSubOrder(va) - getRepeatSubOrder(vb);
    };
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
        obj.sort(cmp);
        obj.forEach((item: any) => sortJSONByOrder(item));
        return;
    }
    if (Array.isArray(obj.ChildItems)) {
        obj.ChildItems.sort(cmp);
    }
    for (const k in obj) {
        if (!obj.hasOwnProperty(k)) continue;
        const v = obj[k];
        if (v && typeof v === 'object') sortJSONByOrder(v);
    }
}

// ─────────────────────────────────────────────────���───────────────────────────
// Repeat-section (minCard / maxCard) utilities — ported from apeCPHierarchyList.jsp
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk the tree and return the highest numeric `order` present. Used so a new
 * cloned repeat instance always gets globally-unique orders.
 */
function getMaxOrder(root: any): number {
    let maxOrder = 0;
    const walk = (node: any) => {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) { node.forEach(walk); return; }
        if (node.order != null) {
            const n = parseInt(node.order, 10);
            if (!isNaN(n) && n > maxOrder) maxOrder = n;
        }
        for (const k in node) {
            if (node.hasOwnProperty(k) && node[k] && typeof node[k] === 'object') {
                walk(node[k]);
            }
        }
    };
    walk(root);
    return maxOrder;
}

/**
 * Deep-clone a repeatable Section/Question, reassigning every `order` to a
 * new sequential value starting at `startOrder`. Response `val` fields are
 * cleared (Property.val is preserved), `selected` flags are reset to false,
 * and `complete` is cleared so the new instance shows as unanswered.
 */
function deepCloneWithNewOrders(
    sourceObj: any, key: string, startOrder: number
): { clone: any; nextOrder: number } {
    const clone = JSON.parse(JSON.stringify(sourceObj));
    const baseOrder = Math.floor(startOrder);
    const decimalOffset = startOrder - baseOrder;

    let orderIndex = 0;
    const assignOrders = (obj: any, inheritedDecimal: number) => {
        if (!obj || typeof obj !== 'object') return;
        if (Array.isArray(obj)) {
            obj.forEach(item => assignOrders(item, inheritedDecimal));
            return;
        }

        if (obj.order != null) {
            const originalOrder = parseFloat(obj.order) || 0;
            const originalBase = Math.floor(originalOrder);

            // Apply the parent's decimal offset to all descendants
            obj.order = parseFloat((originalBase + inheritedDecimal).toFixed(2));
        }

        // Clear response values for new instances
        // Clear val field for Response/Question types (but preserve Property.val)
        if (obj.val !== undefined && obj['@type'] !== 'Property') {
            obj.val = '';
        }
        // Clear selected flags
        if (obj.selected !== undefined) {
            obj.selected = false;
        }
        // Clear complete status
        if (obj.complete !== undefined) {
            delete obj.complete;
        }

        for (const k in obj) {
            if (obj.hasOwnProperty(k)) assignOrders(obj[k], inheritedDecimal);
        }
    };

    assignOrders(clone, decimalOffset);
    return { clone, nextOrder: startOrder + 1 };
}

/** Recursively set or clear _hideChildrenUntilRepeat on every descendant. */
function setHideChildrenFlag(obj: any, hide: boolean): void {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) { obj.forEach((i) => setHideChildrenFlag(i, hide)); return; }
    if (hide) obj._hideChildrenUntilRepeat = true;
    else delete obj._hideChildrenUntilRepeat;
    for (const k in obj) {
        if (!obj.hasOwnProperty(k)) continue;
        if (obj[k] && typeof obj[k] === 'object') setHideChildrenFlag(obj[k], hide);
    }
}

/**
 * Returns true if any descendant has a user-entered value (non-empty val or
 * selected=true). Used to detect saved repeat instances on load.
 */
function hasUserEnteredData(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;
    if (obj.val != null && obj.val !== '') return true;
    if (obj.selected === true || obj.selected === 'true') return true;
    if (Array.isArray(obj.ChildItems)) {
        for (let i = 0; i < obj.ChildItems.length; i++) {
            const child = getWrapperChild(obj.ChildItems[i]);
            if (child && child.value && hasUserEnteredData(child.value)) return true;
        }
    }
    for (const k in obj) {
        if (!obj.hasOwnProperty(k)) continue;
        if (k === 'ChildItems' || k === 'Property' || k === 'Link'
            || k === 'TextAfterResponse' || k === 'ResponseUnits') continue;
        const v = obj[k];
        if (v && typeof v === 'object') {
            if (Array.isArray(v)) {
                for (let i = 0; i < v.length; i++) if (hasUserEnteredData(v[i])) return true;
            } else if (hasUserEnteredData(v)) {
                return true;
            }
        }
    }
    return false;
}

/** True when the object is a repeatable Section/Question (maxCard > 1 or 0). */
function isRepeatable(obj: any, key: string): boolean {
    if (key !== 'Section' && key !== 'Question') return false;
    const mc = obj && obj.maxCard;
    if (mc == null || mc === '' || mc === '1' || mc === 1) return false;
    return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Completeness / Answered helpers (ported from apeCPHierarchyList.jsp)
// These match the legacy logic so green ✓ appears only when ALL required
// enterabilities inside a Section/Question (or ShellItem) are answered.
// ─────────────────────────────────────────────────────────────────────────────

function getChildItemsFromChildItems(childItems: any, excludeHiddenRepeats: boolean = true): any[] {
    if (!childItems) return [];
    if (Array.isArray(childItems)) {
        const items: any[] = [];
        childItems.forEach((wrapper: any) => {
            const child = getWrapperChild(wrapper);
            if (child) {
                if (excludeHiddenRepeats && child.value && child.value._hideChildrenUntilRepeat === true) return;
                items.push(child.value);
            }
        });
        return items;
    }
    const items: any[] = [];
    childTypes.forEach(type => {
        const val = childItems[type];
        if (!val) return;
        if (Array.isArray(val)) {
            val.forEach((item: any) => {
                if (excludeHiddenRepeats && item && item._hideChildrenUntilRepeat === true) return;
                items.push(item);
            });
        } else {
            if (excludeHiddenRepeats && val._hideChildrenUntilRepeat === true) return;
            items.push(val);
        }
    });
    return items;
}

function isResponseAnswered(field: any): boolean {
    if (field != null && field.Response != null) {
        const r = field.Response;
        if (r.decimal != null && r.decimal.val != null) return r.decimal.val !== '';
        if (r.integer != null && r.integer.val != null) return r.integer.val !== '';
        if (r.int != null && r.int.val != null) return r.int.val !== '';
        if (r.string != null && r.string.val != null) return String(r.string.val).trim() !== '';
        if (r.HTML != null) return String(r.HTML).trim() !== '';
        if (r.html != null) return String(r.html).trim() !== '';
        if (r.XML != null) return String(r.XML).trim() !== '';
        if (r.xml != null) return String(r.xml).trim() !== '';
        if (r.dateTime != null) return r.dateTime.val != null && r.dateTime.val !== '';
        if (r.date != null) return r.date.val != null && r.date.val !== '';
        if (r.boolean != null) return r.boolean.val === true || r.boolean.val === 'true';
    }
    return false;
}

function isRequiredButEmpty(responseField: any): boolean {
    if (!responseField) return false;
    if (Array.isArray(responseField)) {
        return responseField.some(field => isRequiredButEmpty(field));
    }
    if (responseField.mustImplement === 'false' || responseField.mustImplement === false) return false;
    const isRequired = responseField.responseRequired === true || responseField.responseRequired === 'true';
    return isRequired && !isResponseAnswered(responseField);
}

function isFieldAnswered(field: any): boolean {
    if (field != null && field.List != null) {
        const items = getShellItemsFromList(field.List);
        if (items.length > 0) {
            const selectedItems = items.filter((item: any) => item && item.selected);
            if (selectedItems.length > 0) {
                return selectedItems.every((item: any) => isShellItemAnswered(item));
            }
            return false;
        }
    }
    return false;
}

function isShellItemAnswered(item: any): boolean {
    if (!item) return false;
    const childItems = getChildItemsFromChildItems(item.ChildItems);
    const requiredChildItems = childItems.filter((child: any) => {
        if (!child) return false;
        if (child.Response) {
            return child.responseRequired === true || child.responseRequired === 'true';
        }
        return child.mustImplement !== 'false' && child.mustImplement !== false;
    });
    const childItemsComplete = requiredChildItems.length === 0
        ? true
        : requiredChildItems.every((child: any) => child.complete === true);
    const childListFieldsComplete = item.ListField ? areAllQuestionsComplete(item.ListField) : true;
    const childListComplete = item.List ? isFieldAnswered(item) : true;
    const responseFieldComplete = item.ResponseField ? !isRequiredButEmpty(item.ResponseField) : true;
    if (item.ListItemResponseField && isRequiredButEmpty(item.ListItemResponseField)) {
        return false;
    }
    const isAnswered = item.shellType === 'DisplayedItem'
        || (item.selected != null && (item.selected === true || item.selected === 'true'))
        || (item.val != null && item.val);
    return !!(isAnswered && childItemsComplete && childListFieldsComplete && childListComplete && responseFieldComplete);
}

function areAllQuestionsComplete(questions: any): boolean {
    function isQuestionComplete(question: any): boolean {
        if (!question) return true;
        if (question.mustImplement === false || question.mustImplement === 'false') return true;
        if (question.overrideReason && question.overrideReason !== '') return true;
        const childItems = getChildItemsFromChildItems(question.ChildItems);
        const requiredChildItems = childItems.filter((item: any) => item && item.mustImplement !== 'false' && item.mustImplement !== false);
        const childItemsComplete = requiredChildItems.length > 0
            ? requiredChildItems.every((item: any) => item.complete === true)
            : true;
        const childListFieldsComplete = question.ListField ? areAllQuestionsComplete(question.ListField) : true;
        const childListComplete = question.List ? isFieldAnswered(question) : true;
        const responseFieldComplete = question.ResponseField ? !isRequiredButEmpty(question.ResponseField) : true;
        return childItemsComplete && childListFieldsComplete && childListComplete && responseFieldComplete;
    }
    if (!questions) return true;
    if (!Array.isArray(questions)) return isQuestionComplete(questions);
    return questions.every((q: any) => isQuestionComplete(q));
}

function hasRequiredInputs(obj: any): boolean {

    /**
     * Check all child items for required Sections and Questions
     */
    function hasRequiredDescendant(item: any): boolean {
        // If this item is required → stop immediately
        if (!hasRequiredInputs(item)) return true;

        // Otherwise, check all children recursively
        return item.ChildItems?.some((child: any) =>
            hasRequiredDescendant(child)
        ) ?? false;
    }

    if (!obj) return false;
    let isOpt = false;

    // The use of minCard is undefined on a Section that has no Question content, and may be ignored
    if(obj.minCard || obj.mustImplement) {
        if(obj.minCard) {
            isOpt = (obj.minCard === '0' || obj.minCard === 0);
        }
        if(!isOpt && obj.mustImplement) {
            isOpt = !(obj.mustImplement === 'true' || obj.mustImplement === true);
        }
    } else {
        // A Section is Optional if it has no required children
        if((obj._isSection != null && obj._isSection == true) || (obj._isQuestion != null && obj._isQuestion == true)) {

            let hasAllOptionalChildren = !obj.ChildItems.some((child: any) =>
                hasRequiredDescendant(child)
            );

            if(hasAllOptionalChildren) {
                isOpt = true; // Override this Section's Required status
            }
        }
    }

    return isOpt;
}

/**
 * A Section/Question is "optional" when minCard === "0".
 * is optional may also be when mustImplement === 'false'
 */
function isOptional(obj: any): boolean {

    /**
     * Check all child items for required Sections and Questions
     */
    function hasRequiredDescendant(item: any): boolean {
        const childItem = item?.Section ? item?.Section : item?.Question;
        if (!childItem) return false;

            // If this question itself is required → stop immediately
        if (!isOptional(childItem)) return true;

            // Otherwise, check all children recursively
        return childItem.ChildItems?.some((child: any) =>
            hasRequiredDescendant(child)
            ) ?? false;
    }

    if (!obj) return false;

    // If this object has an optional parent, it inherits that optional status
    if (obj._hasOptionalParent === true) {
        return true;
    }

    // If minCard = "0", then the item and all descendent questions are optional to answer
    let isOpt = false;

    // The use of minCard is undefined on a Section that has no Question content, and may be ignored
    if(obj.minCard || obj.mustImplement) {
        if(obj.minCard) {
            isOpt = (obj.minCard === '0' || obj.minCard === 0);
        }
        if(!isOpt && obj.mustImplement) {
            isOpt = !(obj.mustImplement === 'true' || obj.mustImplement === true);
        }
    } else {
        // A Section is Optional if it has no required children
        if(obj._isSection != null && obj._isSection == true) {

            let hasAllOptionalChildren = !obj.ChildItems.some((child: any) =>
                hasRequiredDescendant(child)
            );

            if(hasAllOptionalChildren) {
                isOpt = true; // Override this Section's Required status
            }
        }
    }

    return isOpt;
}

/**
 * Determines if an item should be collapsed during "Collapse Complete" operation.
 * ListItemResponseField without responseRequired="true" should be treated as optional (collapsible).
 * ResponseField uses the standard isOptional logic.
 */
function shouldCollapseItem(obj: any, key: string): boolean {
    // Special handling for ListItemResponseField
    if (key === 'ListItemResponseField') {
        // ListItemResponseField is collapsible unless responseRequired="true" is explicitly set
        return !(obj.responseRequired === true || obj.responseRequired === 'true');
    }

    // For all other types (including ResponseField), use standard isOptional logic
    return isOptional(obj);
}

function isChildItemWrapperObject(obj: any): boolean {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    return getWrapperChild(obj) != null;
}

/**
 * Port of propagateFlags() from apeCPHierarchyList.jsp.
 *
 * Walks the translated JSON top-down and stamps `_disabled`, `_readOnly`,
 * `_parentSelected`, `_questionOverride` on each node.  Most importantly:
 * when a node has `readOnly="true"` (e.g. a pre-answered auto-populated
 * Question) the readOnly state propagates to every descendant as
 * `_disabled = true`.  The render path passes `_disabled` through as the
 * `disabled` attribute on the actual <input>/<textarea>, so readOnly inputs
 * are not tabbable — matching the legacy JSP behavior.
 *
 * Must be run BEFORE updateCompleteStatus() on every render so stale
 * `_disabled` flags from previous data are refreshed.
 */
function propagateFlags(translatedJSON: any): void {
    function walk(
        obj: any,
        parentDisabled: boolean,
        parentSelected: boolean,
        parentReadOnly: boolean,
        hasOptionalParent: boolean,
        isSection: boolean,
        isQuestion: boolean,
        parentName: string,
    ): void {

        if (!obj || typeof obj !== 'object') return;
        if (Array.isArray(obj)) {
            obj.forEach(item => {
                walk(item, parentDisabled, parentSelected, parentReadOnly, hasOptionalParent, isSection, isQuestion, parentName );
            });
            return;
        }

        const isParentSelected = parentSelected;

        const selfReadOnly =
            parentReadOnly === true ||
            obj.readOnly === 'true' ||
            obj.readOnly === true;

        const selfDisabled = parentDisabled === true || selfReadOnly;
        const selfOverride = !!(obj.overrideReason && obj.overrideReason !== '');

        // Compute if THIS node is optional based on its own minCard/mustImplement
        const selfIsOptional =
            (obj.minCard === '0' || obj.minCard === 0) ||
            (obj.mustImplement === 'false' || obj.mustImplement === false);

        // Children should be optional if EITHER the parent is optional OR this node is optional
        const childrenHaveOptionalParent = hasOptionalParent || selfIsOptional;

        // Don't stamp flags on bare wrappers like { Section: {...} } —
        // descend through them instead.
        if (!isChildItemWrapperObject(obj)) {
            obj._disabled = selfDisabled;
            obj._parentName = parentName;
            obj._parentSelected = isParentSelected;
            obj._hasOptionalParent = hasOptionalParent; // Use what was passed from parent
            obj._questionOverride = selfOverride;
            obj._readOnly = selfReadOnly;
            obj._isSection = isSection;
            obj._isQuestion = isQuestion;
            obj._hasRequiredInputs = hasRequiredInputs(obj);
        }

        if (obj.Section) {
            walk(obj.Section, selfDisabled, isParentSelected, selfReadOnly, childrenHaveOptionalParent, true, false, (obj.name != null)?obj.name : parentName);
        }

        if (obj.Question) {
            walk(obj.Question, selfDisabled, isParentSelected, selfReadOnly, childrenHaveOptionalParent, false, true, (obj.name != null)?obj.name : parentName);
        }

        if (Array.isArray(obj.ChildItems)) {
            obj.ChildItems.forEach((wrapper: any) => {
                const child = getWrapperChild(wrapper);
                if (child) {
                    walk(child.value, selfDisabled, isParentSelected, selfReadOnly, childrenHaveOptionalParent, wrapper.Section != null, wrapper.Question != null,(obj.name != null)?obj.name : parentName);
                }
            });
        }

        // ✅ ResponseField
        if (obj.ResponseField) {
            const fields = Array.isArray(obj.ResponseField)
                ? obj.ResponseField
                : [obj.ResponseField];

            fields.forEach((f: any) => {
                if (f) {
                    walk(f, selfDisabled, isParentSelected, selfReadOnly, childrenHaveOptionalParent, false, false, (obj.name != null)?obj.name : parentName);
                }
            });
        }

        // ✅ ListField
        if (obj.ListField) {
            walk(obj.ListField, selfDisabled, isParentSelected, selfReadOnly, childrenHaveOptionalParent, false, false, (obj.name != null)?obj.name : parentName);
        }

        // ✅ List (complex case)
        if (obj.List) {
            const items = getShellItemsFromList(obj.List);

            items.forEach((item: any) => {
                if (!item) return;

                item._disabled = selfDisabled;
                item._parentSelected = isParentSelected;
                item._hasOptionalParent = childrenHaveOptionalParent; // Use computed value
                item._questionOverride = !!(item.overrideReason && item.overrideReason !== '');
                item._readOnly = selfReadOnly;

                const itemSelected =
                    item.selected === true || item.selected === 'true';

                const childDisabled = selfDisabled || !itemSelected;
                const childParentSelected = isParentSelected && itemSelected;

                if (Array.isArray(item.ChildItems)) {
                    item.ChildItems.forEach((wrapper: any) => {
                        const child = getWrapperChild(wrapper);
                        if (child) {
                            walk(
                                child.value,
                                childDisabled,
                                childParentSelected,
                                selfReadOnly,
                                childrenHaveOptionalParent, // Children inherit parent's optional status
                                false,
                                false,
                                parentName
                            );
                        }
                    });
                }

                if (item.ResponseField) {
                    const fs = Array.isArray(item.ResponseField)
                        ? item.ResponseField
                        : [item.ResponseField];

                    fs.forEach((f: any) => {
                        if (f) {
                            walk(f,
                                childDisabled,
                                childParentSelected,
                                selfReadOnly,
                                childrenHaveOptionalParent, // Children inherit parent's optional status
                                false,
                                false,
                                (item.name != null)?item.name : parentName);
                        }
                    });
                }

                if (item.ListField) {
                        walk(
                            item.ListField,
                            childDisabled,
                            childParentSelected,
                            selfReadOnly,
                            childrenHaveOptionalParent, // Children inherit parent's optional status
                            false,
                            false,
                            (item.name != null)?item.name : parentName
                        );
                }

                if (item.ListItemResponseField) {
                    const lirf = Array.isArray(item.ListItemResponseField)
                        ? item.ListItemResponseField
                        : [item.ListItemResponseField];

                    lirf.forEach((f: any) => {
                        if (f) {
                                walk(
                                    f,
                                    childDisabled,
                                    childParentSelected,
                                    selfReadOnly,
                                    childrenHaveOptionalParent, // Children inherit parent's optional status
                                    false,
                                    false,
                                    (item.name != null)?item.name : parentName
                                );
                        }
                    });
                }
            });
        }

        // ✅ ListItemResponseField
        if (obj.ListItemResponseField) {
            const lirf = Array.isArray(obj.ListItemResponseField)
                ? obj.ListItemResponseField
                : [obj.ListItemResponseField];

            lirf.forEach((f: any) => {
                if (f) {
                    walk(f, selfDisabled, isParentSelected, selfReadOnly, hasOptionalParent, false, false, (obj.name != null)?obj.name : parentName);
                }
            });
        }

        // ✅ Structural containers
        ['Body', 'FormDesign'].forEach(k => {
            if (obj[k] && typeof obj[k] === 'object') {
                walk(obj[k], selfDisabled, isParentSelected, selfReadOnly, false, obj.Section != null, obj.Question != null, (obj[k].name != null)?obj[k].name : parentName);
            }
        });
    }

    if (!translatedJSON) return;
    for (const key in translatedJSON) {
        if (translatedJSON.hasOwnProperty(key)) {
            walk(translatedJSON[key], false, true, false, false, translatedJSON.Section != null, translatedJSON.Question != null, "");
        }
    }
}

/**
 * Port of updateCompleteStatus() from apeCPHierarchyList.jsp.
 * Traverses the translated JSON and stamps `complete = true/false` on every
 * Section / Question / ShellItem so the render can show a green ✓ for items
 * whose required descendants are all answered.
 */
function updateCompleteStatus(translatedJSON: any): void {
    function traverse(obj: any): void {
        if (!obj) return;
        if (Array.isArray(obj)) {
            obj.forEach(item => traverse(item));
            return;
        }
        if (typeof obj !== 'object') return;

        // Don't stamp completion on a bare wrapper { Section: {...} } — recurse into it
        if (isChildItemWrapperObject(obj)) {
            const child = getWrapperChild(obj);
            if (child) traverse(child.value);
            return;
        }

        // Depth-first: children must have `.complete` computed before parent
        Object.values(obj).forEach(value => {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                traverse(value);
            }
        });

        let isComplete = true;

        if (obj.Section) {
            const sectionItems = getChildItemsFromChildItems(obj.Section.ChildItems);
            const requiredSectionItems = sectionItems.filter((item: any) =>
                item && item.mustImplement !== 'false' && item.mustImplement !== false
                    && item._hideChildrenUntilRepeat !== true);
            isComplete = isComplete && (requiredSectionItems.length === 0
                ? true
                : requiredSectionItems.every((item: any) => item.complete === true));
        }
        if (obj.ChildItems) {
            const childItems = getChildItemsFromChildItems(obj.ChildItems);
            const requiredChildItems = childItems.filter((item: any) =>
                item && item.mustImplement !== 'false' && item.mustImplement !== false
                    && item._hideChildrenUntilRepeat !== true);
            if (requiredChildItems.length > 0) {
                isComplete = isComplete && requiredChildItems.every((item: any) => item.complete === true);
            }
        }
        if (obj.Question) {
            isComplete = isComplete && areAllQuestionsComplete(obj.Question);
        }
        if (obj.ListField) {
            isComplete = isComplete && areAllQuestionsComplete(obj.ListField);
        }
        if (obj.ResponseField) {
            isComplete = isComplete && !isRequiredButEmpty(obj.ResponseField);
        }
        if (obj.ShellItem) {
            const shellItems = Array.isArray(obj.ShellItem) ? obj.ShellItem : [obj.ShellItem];
            isComplete = isComplete && shellItems.every((si: any) => isShellItemAnswered(resolveWrapperObject(si)));
        }
        if (obj.shellType) {
            isComplete = isComplete && isShellItemAnswered(obj);
        }
        if (obj.List) {
            isComplete = isComplete && isFieldAnswered(obj);
        }
        if (obj.Response) {
            isComplete = isComplete && isResponseAnswered(obj);
        }

        // Placeholder repeat rows are never complete
        if (obj._hideChildrenUntilRepeat === true) {
            isComplete = false;
        }

        // Track real completion state (answered without relying on an override).
        // The green ✓ uses this so an overridden Question only shows the check
        // when its required descendants are actually answered.
        obj._completeWithoutOverride = isComplete;

        // Respect explicit override reason — treat as complete
        if (obj.overrideReason && obj.overrideReason !== '') {
            isComplete = true;
        }

        obj.complete = isComplete;
    }

    if (!translatedJSON) return;
    for (const key in translatedJSON) {
        if (translatedJSON.hasOwnProperty(key)) {
            traverse(translatedJSON[key]);
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ──────────────���───────────��─────────────────────────────────────���────────────

// JRC 05/13/26 CO-16108 Returns true when the form is complete
function computeOverallComplete(node: any): boolean {
    if (!node) return true;
    if (Array.isArray(node)) {
        for (const item of node) {
            if (!computeOverallComplete(item)) return false;
        }
        return true;
    }
    if (typeof node !== 'object') return true;

    // If this node IS a wrapper (Section/Question/etc.), trust its own
    // cascaded `.complete` flag and stop.
    const wrapped = getWrapperChild(node);
    if (wrapped && wrapped.value && typeof wrapped.value === 'object') {
        const v = wrapped.value;
        const required = v.mustImplement !== 'false' && v.mustImplement !== false
            && v._hideChildrenUntilRepeat !== true;
        if (required) return v.complete === true;
        return true;
    }

    // Check every wrapped child at this level — required ones must be
    // `.complete === true`. Don't descend past them.
    if (Array.isArray(node.ChildItems)) {
        for (const wrapper of node.ChildItems) {
            const child = getWrapperChild(wrapper);
            if (!child || !child.value || typeof child.value !== 'object') continue;
            const v = child.value;
            const required = v.mustImplement !== 'false' && v.mustImplement !== false
                && v._hideChildrenUntilRepeat !== true;
            if (required && v.complete !== true) return false;
        }
    }

    // Recurse through structural pass-through containers and the loose
    // top-level shape (translatedJSON itself can be a bag of named keys).
    const handled: { [k: string]: boolean } = {
        ChildItems: true, complete: true, _completeWithoutOverride: true,
    };
    for (const key in node) {
        if (!node.hasOwnProperty(key)) continue;
        if (handled[key]) continue;
        if (key.charAt(0) === '_') continue;
        const v = node[key];
        if (v && typeof v === 'object') {
            if (!computeOverallComplete(v)) return false;
        }
    }
    return true;
}

export class ECPHierarchyList extends React.Component<ECPHierarchyProps, ECPHierarchyState> {
    private listRowRef: RefObject<HTMLDivElement>;
    private hierarchyFlatMap: any[] = [];
    private hierarchyIndexSet: Set<number> = new Set();
    private repeatInstanceTracker: any = {};
    private _currentHighlightedIndex?: number;
    private _stickyResizeObserver: ResizeObserver | null = null;
    private _stickyUpdateRaf: number | null = null;
    private _lastCompleteState?: boolean;

    constructor(props: ECPHierarchyProps) {
        super(props);

        this.listRowRef = React.createRef();

        // Initialize and sort JSON data
        let initialData = props.jsonData || {};
        sortJSONByOrder(initialData);
        // Initialize repeat-section trackers and pre-populate minCard instances /
        // placeholder + buttons BEFORE we measure link widths, so repeat buttons
        // are accounted for in the left-alignment calculation.
        this.initRepeatSections(initialData);
        populateLinkCountsByParent(initialData);
        if (initialData && typeof initialData === 'object') {
            initialData._repeatsInitialized = true;
        }

        this.state = {
            translatedJSON: initialData,
            focusedHierarchyIndex: undefined,
            isExpandingOnLoad: false,
            rowCount: 0,
            bHideOptional: false,
            isComplete: false,
            validationErrors: [],
        };

        // Bind methods
        this.onRefresh = this.onRefresh.bind(this);
        this.onExpandAll = this.onExpandAll.bind(this);
        this.onCollapseAll = this.onCollapseAll.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.refresh = this.refresh.bind(this);
        this.setDisabled = this.setDisabled.bind(this);
        this.setHideOptional = this.setHideOptional.bind(this);
        this.getJSONData = this.getJSONData.bind(this);
        this.isComplete = this.isComplete.bind(this);
        this.getValidationErrors = this.getValidationErrors.bind(this);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Public API Methods (called from JSP wrapper)
    // ────────────────────��─��─────────��─────────────���───────���──────────────────

    public refresh() {
        this.onRefresh();
    }

    public expandAll() {
        this.onExpandAll();
    }

    public collapseAll() {
        this.onCollapseAll();
    }

    public collapseComplete() {
        this.collapseAllSections(this.state.translatedJSON, true);
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        }, () => {
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        });
    }

    public setDisabled() {
        // Will trigger re-render with disabled state
        this.forceUpdate();
    }

    public setHideOptional(hide: boolean) {
        this.setState({
            bHideOptional: hide,
            rowCount: -1,
        }, () => {
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        });
    }

    public getJSONData(): any {
        return this.state.translatedJSON;
    }

    public isComplete(): boolean {
        // Prefer the cached value computed during the last render. Fall back
        // to a live computation if render hasn't run yet.
        if (typeof this._lastCompleteState === 'boolean') return this._lastCompleteState;
        const tj = this.state.translatedJSON;
        if (!tj) return false;
        propagateFlags(tj);
        updateCompleteStatus(tj);
        return computeOverallComplete(tj);
    }

    public getValidationErrors(): any[] {
        return this.state.validationErrors || [];
    }

    public debugNodeByOrder(order: string | number): any {
        return this.findNodeByOrder(this.state.translatedJSON, order);
    }

    private findNodeByOrder(obj: any, order: string | number): any {
        if (!obj || typeof obj !== 'object') return null;
        if (Array.isArray(obj)) {
            for (const item of obj) {
                const found = this.findNodeByOrder(item, order);
                if (found) return found;
            }
            return null;
        }
        if (obj.order !== undefined && String(obj.order) === String(order)) {
            return obj;
        }
        for (const key in obj) {
            if (obj.hasOwnProperty(key) && typeof obj[key] === 'object') {
                const found = this.findNodeByOrder(obj[key], order);
                if (found) return found;
            }
        }
        return null;
    }

    componentDidMount() {
        // Expand all on load if requested
        if (this.props.expandAllOnLoad) {
            this.setState({ isExpandingOnLoad: true }, () => {
                queueMicrotask(() => {
                    // Expand all sections
                    this.expandAllSections(this.state.translatedJSON);

                    this.setState({
                        translatedJSON: this.state.translatedJSON,
                        rowCount: -1,
                        isExpandingOnLoad: false,
                    }, () => {
                        // After expand-all finishes the DOM has all rows, so
                        // we can safely move focus to the first one. Run on
                        // the next frame so layout has fully settled.
                        requestAnimationFrame(() => this.focusFirstNavigableRow());
                    });
                });
            });
        } else {
            // No expand-all — schedule a focus on next frame so the initial
            // render has committed.
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        }

        // Compute real-DOM sticky top offsets so multi-line wrapped Section/
        // Question headers stack correctly. See updateStickyTops() for details.
        this.scheduleStickyTopUpdate();

        // NOTE: the `focusin` listener is attached via the `attachContainerRef`
        // callback ref on the scroll container <div>, not here — because that
        // div is conditionally unmounted/remounted whenever `isExpandingOnLoad`
        // toggles. Attaching in componentDidMount would orphan the listener
        // on the first remount.
    }

    /**
     * Move DOM focus to the first navigable row in the hierarchy (typically
     * the topmost Section). Silently no-ops if focus has already been placed
     * on a hierarchy element (e.g. the user clicked something before the
     * mount callback ran) so we don't steal focus from the user.
     */
    private focusFirstNavigableRow(attempt: number = 0) {
        // When the host has opted out of keyboard navigation, do NOT place
        // a programmatic focus on any hierarchy row. Otherwise the first
        // Section would receive focus on mount / expand-all / collapse-all /
        // refresh / hide-optional and show a focus outline the user never
        // asked for.
        if (!this.props.enableKeyboardNavigation) return;

        const container = this.listRowRef.current;
        if (!container) {
            // Container not yet mounted (e.g. spinner is still showing).
            // Retry on the next frame, up to a few times, so the initial
            // focus lands as soon as the list becomes visible.
            if (attempt < 10) {
                requestAnimationFrame(() => this.focusFirstNavigableRow(attempt + 1));
            }
            return;
        }

        // Respect any focus the user has already placed inside the component.
        const active = document.activeElement as HTMLElement | null;
        if (active && container.contains(active)) return;

        const entries = this.getNavigableEntries();
        if (entries.length === 0) {
            // Flat map not populated yet (render hasn't run). Retry.
            if (attempt < 10) {
                requestAnimationFrame(() => this.focusFirstNavigableRow(attempt + 1));
            }
            return;
        }

        // Prefer the first Section/Question header; fall back to the first
        // navigable entry of any kind.
        const headerKeys = new Set(['Section', 'Question']);
        const first = entries.find((e: any) => headerKeys.has(e.key)) || entries[0];
        if (first) {
            this.focusHierarchyRow(first.hierarchyIndex);
        }
    }

    componentWillUnmount() {
        const container = this.listRowRef.current;
        if (container) {
            container.removeEventListener('focusin', this.handleContainerFocusIn, true);
        }
        if (this._stickyResizeObserver) {
            this._stickyResizeObserver.disconnect();
            this._stickyResizeObserver = null;
        }
        if (this._stickyUpdateRaf !== null) {
            cancelAnimationFrame(this._stickyUpdateRaf);
            this._stickyUpdateRaf = null;
        }
    }

    /**
     * When the host JSP replaces `props.jsonData` with a brand-new object
     * reference (e.g. after loading a different form), swap it into state
     * and re-run the repeat-section initialization. The render() path also
     * runs init lazily when the `_repeatsInitialized` sentinel is missing —
     * this hook just makes sure the new data object is adopted as state.
     */
    componentDidUpdate(prevProps: ECPHierarchyProps) {
        if (prevProps.jsonData !== this.props.jsonData && this.props.jsonData) {
            const newData = this.props.jsonData;
            sortJSONByOrder(newData);
            this.initRepeatSections(newData);
            populateLinkCountsByParent(newData);
            if (typeof newData === 'object') newData._repeatsInitialized = true;
            this.setState({ translatedJSON: newData, rowCount: -1 });
        }

        // Rows may have been added, removed, expanded or collapsed — any of
        // which can change which sticky headers exist and how tall they are.
        this.scheduleStickyTopUpdate();
    }

    /**
     * Callback ref for the scroll container. Attaches our capture-phase
     * `focusin` listener when the div mounts and removes it when the div
     * is unmounted. The container is unmounted/remounted every time
     * `isExpandingOnLoad` toggles, so using a callback ref — instead of
     * React.createRef + componentDidMount — guarantees the listener is
     * re-attached on each mount.
     */
    private attachContainerRef = (el: HTMLDivElement | null) => {
        const prev = this.listRowRef.current;
        if (prev && prev !== el) {
            prev.removeEventListener('focusin', this.handleContainerFocusIn, true);
            prev.removeEventListener('keydown', this.onKeyDown as unknown as EventListener, true);
        }
        (this.listRowRef as any).current = el;
        if (el) {
            el.addEventListener('focusin', this.handleContainerFocusIn, true);
            // Attach keydown in the CAPTURE phase so arrow-key navigation
            // runs BEFORE the browser's native default action (e.g. radio
            // groups moving focus/selection between siblings with the same
            // `name`). Without capture, a native radio group hijacks
            // ArrowUp/ArrowDown at the end of a Question's options and the
            // focus wraps back inside the group instead of jumping to the
            // next Section/Question header.
            el.addEventListener('keydown', this.onKeyDown as unknown as EventListener, true);
        }
    }

    /**
     * Resolve the hierarchy index owning the currently focused element and
     * update state so `hierarchyRowSelected` only lights up that single row.
     */
    private handleContainerFocusIn = (ev: FocusEvent) => {
        // When keyboard navigation is disabled, don't track a focused
        // hierarchy row — the custom focus-square highlight is driven off
        // `focusedHierarchyIndex`, and leaving it unset keeps the outline
        // from appearing in response to mouse clicks or native form focus.
        if (!this.props.enableKeyboardNavigation) return;

        const target = ev.target as HTMLElement | null;
        if (!target) return;

        let hIdx = -1;
        // Direct focus on an accordion header (Section/Question/FormDesign)
        if (target.id && target.id.indexOf('eCPHierarchy_') === 0
            && target.id.indexOf('_contentDiv') < 0) {
            const n = parseInt(target.id.substring('eCPHierarchy_'.length), 10);
            if (!isNaN(n)) hIdx = n;
        }
        // Otherwise: focus inside an input row wrapper carrying data-ecp-row-index
        if (hIdx < 0 && typeof target.closest === 'function') {
            const row = target.closest('[data-ecp-row-index]') as HTMLElement | null;
            if (row) {
                const n = parseInt(row.getAttribute('data-ecp-row-index') || '', 10);
                if (!isNaN(n)) hIdx = n;
            }
        }
        if (hIdx < 0) return;

        if (this.state.focusedHierarchyIndex !== hIdx) {
            this.setState({ focusedHierarchyIndex: hIdx });
        }
    }

    // ───────────────────────���─────────────────────────────────���───────────────
    // Event Handlers
    // ─────────────────────────────────────────────────────────────────────────

    private onRefresh() {
        populateLinkCountsByParent(this.state.translatedJSON);
        // Read global bHideOptional flag so that external pages clicking
        // #eCPHierarchyRefresh after toggling window.bHideOptional get the
        // hide-optional state applied automatically.
        const globalHide = !!(window as any).bHideOptional;
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
            bHideOptional: globalHide,
        }, () => {
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        });
    }

    private onExpandAll() {
        this.expandAllSections(this.state.translatedJSON);
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        }, () => {
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        });
    }

    private onCollapseAll() {
        // Check global flag: if CAPeCPCollapseComplete is true, only collapse completed sections
        const collapseCompleteOnly = !!(window as any).CAPeCPCollapseComplete;
        this.collapseAllSections(this.state.translatedJSON, collapseCompleteOnly);
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        }, () => {
            requestAnimationFrame(() => this.focusFirstNavigableRow());
        });
    }

    private expandAllSections(data: any): void {
        if (!data || typeof data !== 'object') return;
        if (Array.isArray(data)) {
            data.forEach((item: any) => this.expandAllSections(item));
            return;
        }
        // Set isExpanded on Section and Question nodes
        if (data.ChildItems !== undefined || data.title !== undefined || data.order !== undefined) {
            data.isExpanded = true;
        }
        // Recurse into all properties
        for (const key in data) {
            if (!data.hasOwnProperty(key)) continue;
            const val = data[key];
            if (val && typeof val === 'object') {
                this.expandAllSections(val);
            }
        }
    }

    private collapseAllSections(data: any, collapseCompleteOnly: boolean = false, parentKey?: string): void {
        if (!data || typeof data !== 'object') return;
        if (Array.isArray(data)) {
            data.forEach((item: any) => this.collapseAllSections(item, collapseCompleteOnly, parentKey));
            return;
        }

        // Collapse Section and Question nodes that have collapsible content
        // (ChildItems, ListField, ResponseField, or ListItemResponseField)
        const hasCollapsibleContent = Array.isArray(data.ChildItems)
            || data.ListField
            || data.ResponseField
            || data.ListItemResponseField;

        if (hasCollapsibleContent && data.isExpanded !== undefined) {
            if (collapseCompleteOnly) {
                // Use the new logic that checks for optional sections with required unanswered fields
                if (this.shouldCollapseSection(data, parentKey)) {
                    data.isExpanded = false;
                }
            } else {
                // Collapse all regardless of completion
                data.isExpanded = false;
            }
        }

        // Recurse into all properties
        // Track structural keys so we can pass context to shouldCollapseSection
        const structuralKeys = ['ResponseField', 'ListItemResponseField', 'ListField', 'Response'];
        for (const key in data) {
            if (!data.hasOwnProperty(key)) continue;
            const val = data[key];
            if (val && typeof val === 'object') {
                // Pass the key if it's a structural key, otherwise undefined
                const keyToPass = structuralKeys.includes(key) ? key : undefined;
                this.collapseAllSections(val, collapseCompleteOnly, keyToPass);
            }
        }
    }

    /**
     * Returns true if a section (or any of its descendants) has at least one
     * answered question — i.e. a selected list item, a filled response, etc.
     * Used to distinguish truly-answered optional sections from those that are
     * "complete" only because every child is optional and unanswered.
     */
    private sectionHasAnyAnswer(data: any): boolean {
        if (!data || typeof data !== 'object') return false;
        if (Array.isArray(data)) {
            return data.some((item: any) => this.sectionHasAnyAnswer(item));
        }
        // Repeat-section placeholders (the "+" row) are purely structural —
        // they exist so the user can add another instance. They carry empty
        // Response/ResponseField/List skeletons which must NOT be counted
        // as answers (or as un-answers).
        if (data._hideChildrenUntilRepeat === true) return false;
        // A shell item that is selected counts as answered
        if (data.selected === true || data.selected === 'true') return true;
        // A response field that has a value counts as answered
        if (isResponseAnswered(data)) return true;
        // An override reason counts as answered
        if (data.overrideReason && data.overrideReason !== '') return true;
        // Recurse into children, but skip non-answer properties like Property
        // (which holds the title/label text, not a user answer)
        const skipKeys = new Set(['Property', 'title', 'ID', 'readOnly', 'mustImplement',
            'minCard', 'maxCard', 'responseRequired', 'shellType', 'complete',
            '_completeWithoutOverride', 'isExpanded', 'hierarchyIndex',
            '_hideChildrenUntilRepeat', '_parentSelected', '_hasOptionalParent']);
        for (const key in data) {
            if (!data.hasOwnProperty(key)) continue;
            if (skipKeys.has(key)) continue;
            const val = data[key];
            if (val && typeof val === 'object') {
                if (this.sectionHasAnyAnswer(val)) return true;
            }
        }
        return false;
    }

    /**
     * Returns true if the section (or any descendant) contains at least one
     * enterable field — a List (radio/checkbox), Response, ResponseField,
     * or ListField.  Sections that contain only DisplayedItems have nothing
     * the user can interact with.
     */
    private sectionHasEnterableFields(data: any): boolean {
        if (!data || typeof data !== 'object') return false;
        if (Array.isArray(data)) {
            return data.some((item: any) => this.sectionHasEnterableFields(item));
        }

        const title = data.title || data.name || '';

        // Repeat placeholders don't count — they aren't real fields for the
        // user to fill in, they're the "+" row that creates a new instance.
        if (data._hideChildrenUntilRepeat === true) return false;
        // These keys indicate user-enterable content
        if (data.List || data.Response || data.ResponseField || data.ListItemResponseField || data.ListField) {
            const fieldTitle = title || 'field';
            return true;
        }

        // Special handling for ChildItems - need to unwrap wrapper objects
        if (Array.isArray(data.ChildItems)) {
            for (const child of data.ChildItems) {
                // ChildItems contains wrapper objects like { Section: {...} } or { Question: {...} }
                const wrapped = getWrapperChild(child);
                if (wrapped) {
                    if (this.sectionHasEnterableFields(wrapped.value)) {
                        return true;
                    }
                }
            }
        }

        // Recurse into other properties, skipping metadata
        for (const key in data) {
            if (!data.hasOwnProperty(key)) continue;
            if (key === 'ChildItems') continue; // Already handled above
            const val = data[key];
            if (val && typeof val === 'object') {
                if (this.sectionHasEnterableFields(val)) return true;
            }
        }
        return false;
    }

    /**
     * Check if section has any unanswered children (optional or required) with enterable fields.
     * This is used during "Collapse Complete" to keep parent sections expanded if they have
     * any children that:
     * 1. Are unanswered (no answer provided)
     * 2. Have enterable fields (user can interact with them)
     *
     * When "Hide Optional" is enabled, optional unanswered children are ignored because they're
     * hidden from view. When "Hide Optional" is disabled, both optional and required unanswered
     * children will keep the parent expanded with one exception, ListItemResponseField.
     * ListItemResponseField without responseRequired="true" is treated as optional and will not block collapse even if unanswered.
     */
    private hasUnansweredChildrenWithEnterableFields(obj: any, key?: string): boolean {
        if (!obj || typeof obj !== 'object') return false;

        // Skip repeat placeholders
        if (obj._hideChildrenUntilRepeat === true) return false;

        // Special case: ListItemResponseField without responseRequired="true" should not block collapse
        // This applies to the field itself AND all its children
        if (key === 'ListItemResponseField') {
            const isRequired = obj.responseRequired === true || obj.responseRequired === 'true';
            if (!isRequired) {
                // Not required, so neither this field nor its children should block collapse
                return false;
            }
        }

        // When "Hide Optional" is enabled, skip optional items entirely
        // because they're not visible and shouldn't affect collapse behavior
        // Use shouldCollapseItem if we have a key to distinguish ListItemResponseField
        if (this.state.bHideOptional) {
            if (key) {
                if (shouldCollapseItem(obj, key)) {
                    return false;
                }
            } else if (isOptional(obj)) {
                return false;
            }
        }

        // Check if this node itself is an unanswered field with enterable content
        const hasEnterableFields = (obj.List || obj.Response || obj.ResponseField || obj.ListItemResponseField || obj.ListField);
        const hasAnswer = this.sectionHasAnyAnswer(obj);

        if (hasEnterableFields && !hasAnswer) {
            return true;
        }

        // Check for Lists with selected items
        if (obj.List) {
            const items = getShellItemsFromList(obj.List);
            const selectedItems = items.filter((item: any) => item && (item.selected === true || item.selected === 'true'));

            // Check if any selected item has unanswered children
            for (const selectedItem of selectedItems) {
                    if (this.hasUnansweredChildrenWithEnterableFields(selectedItem)) {
                        return true;
                }
            }
        }

        // Recurse into ChildItems
        // NOTE: We work directly with obj.ChildItems instead of using getChildItemsFromChildItems
        // because we need to preserve the wrapper information (e.g., { ListItemResponseField: {...} })
        // to pass the correct key context for checking responseRequired
        if (Array.isArray(obj.ChildItems)) {
            for (const wrapper of obj.ChildItems) {
                // Detect the wrapper key before unwrapping so we can pass context
                const wrapperInfo = getWrapperChild(wrapper);
                if (!wrapperInfo) continue;

                const childKey = wrapperInfo.key;
                const childObj = wrapperInfo.value;

                // Skip repeat placeholders
                if (childObj && childObj._hideChildrenUntilRepeat === true) continue;

                if (childObj && this.hasUnansweredChildrenWithEnterableFields(childObj, childKey)) {
                    return true;
                }
            }
        }

        // Recurse into structural containers
        const structuralKeys = ['ResponseField', 'ListItemResponseField', 'ListField', 'Response'];
        for (const key of structuralKeys) {
            // Skip ListItemResponseField if we're inside a selected item (already handled above)
            if (key === 'ListItemResponseField' && obj.selected) {
                continue;
            }
            if (obj[key] && this.hasUnansweredChildrenWithEnterableFields(obj[key], key)) {
                return true;
            }
        }

        return false;
    }

    private shouldCollapseSection(data: any, key?: string): boolean {
        if (!data || typeof data !== 'object') return true;

        const title = data.title || data.name || 'unnamed';
        const isOpt = isOptional(data);

        // For optional sections, check for unanswered enterable fields FIRST
        if (isOpt) {
            const hasAnyAnswer = this.sectionHasAnyAnswer(data);
            const hasEnterableFields = this.sectionHasEnterableFields(data);

            if (hasEnterableFields && !hasAnyAnswer) {
                return false;
            }

            if (!hasAnyAnswer && !hasEnterableFields) {
                return true;
            }

            // For optional sections that have been answered, check if they have any unanswered children
            // (optional or required) with enterable fields. This prevents collapsing parents when
            // they have children that can still be filled in.
            // When "Hide Optional" is enabled, optional children are ignored.
            const hasUnansweredChildren = this.hasUnansweredChildrenWithEnterableFields(data, key);
            if (hasUnansweredChildren) return false;
        }

        // For required sections, must be marked complete to collapse
        if (data.complete !== true && data.complete !== 'true') return false;

        // IMPORTANT: For required sections during "Collapse Complete", also check if there
        // are any unanswered children (optional or required) with enterable fields.
        // Required sections should stay expanded if they have ANY unanswered children so
        // the user can see what's available to fill in. This includes cases where the
        // section is marked complete (all required fields answered) but has optional
        // children that are unanswered.
        // When "Hide Optional" is enabled, optional children are ignored.
        const hasUnansweredChildren = this.hasUnansweredChildrenWithEnterableFields(data, key);

        if (hasUnansweredChildren) {
            return false;
        }

        return true;
    }

    /**
     * Returns true if every enterable field in the subtree has been answered
     * (or carries an overrideReason).  Unlike the Section's own `.complete`
     * flag — which excludes optional questions from its rollup — this walker
     * treats optional and required questions the same: an unanswered
     * optional question makes the whole subtree "not fully answered".
     *
     * This is used by Collapse Complete so an optional Section with a mix of
     * answered and unanswered optional questions stays expanded, letting the
     * user see (and finish) the remaining questions.
     *
     * Rules:
     *   - overrideReason on any node short-circuits that node as answered.
     *   - Response      : must pass isResponseAnswered.
     *   - ResponseField : every entry must pass isResponseAnswered (so an
     *                     empty optional textarea still counts as unanswered).
     *   - List          : must have at least one selected ShellItem AND every
     *                     selected ShellItem must pass isShellItemAnswered
     *                     (catches checkbox-with-required-child-empty).
     *   - Nested objects are recursed into; metadata keys are skipped.
     */
    private sectionFullyAnswered(data: any): boolean {
        if (!data || typeof data !== 'object') return true;
        if (Array.isArray(data)) {
            return data.every((item: any) => this.sectionFullyAnswered(item));
        }

        // An explicit override makes this node (and everything it covers)
        // count as answered regardless of actual inputs.
        if (data.overrideReason && data.overrideReason !== '') return true;

        // Repeat-section placeholders (the "+" row shown at the end of a
        // repeatable section) are structural scaffolding — they exist only
        // so the user can add another instance. They carry empty skeletons
        // of Response/ResponseField/List that would otherwise be detected as
        // unanswered and block Collapse Complete. Treat as answered and
        // don't recurse.
        if (data._hideChildrenUntilRepeat === true) return true;

        // ReadOnly / disabled nodes are auto-populated (or intentionally
        // non-interactive) — the user can't act on them, so they should never
        // block Collapse Complete. Treat as answered and don't recurse.
        if (data._readOnly === true
            || data.readOnly === true || data.readOnly === 'true'
            || data._disabled === true) {
            return true;
        }

        // When Hide Optional is active, optional Sections/Questions aren't
        // rendered — so they shouldn't block Collapse Complete either. Treat
        // any mustImplement="false" node (and everything beneath it) as
        // answered so a section with only hidden-optional unanswered items
        // is allowed to collapse.
        if (this.state.bHideOptional
            && isOptional(data)
        ) {
            return true;
        }

        // Node's own enterable content ----------------------------------------
        if (data.Response && !isResponseAnswered(data)) return false;

        if (data.ResponseField) {
            const rfs = Array.isArray(data.ResponseField)
                ? data.ResponseField
                : [data.ResponseField];
            for (const rf of rfs) {
                if (!rf) continue;
                if (rf.overrideReason && rf.overrideReason !== '') continue;
                if (!isResponseAnswered(rf)) return false;
            }
        }

        // ListItemResponseField: only blocks collapse if responseRequired="true" AND unanswered
        if (data.ListItemResponseField) {
            const lirfs = Array.isArray(data.ListItemResponseField)
                ? data.ListItemResponseField
                : [data.ListItemResponseField];
            for (const lirf of lirfs) {
                if (!lirf) continue;
                if (lirf.overrideReason && lirf.overrideReason !== '') continue;
                // Only check if responseRequired is explicitly true
                const isRequired = lirf.responseRequired === true || lirf.responseRequired === 'true';
                if (isRequired && !isResponseAnswered(lirf)) return false;
            }
        }

        if (data.List) {
            const items = getShellItemsFromList(data.List);
            if (items.length > 0) {
                const selected = items.filter((it: any) => it && it.selected);
                if (selected.length === 0) return false;
                if (!selected.every((it: any) => isShellItemAnswered(it))) return false;
            }
        }

        // Recurse into children -----------------------------------------------
        const skipKeys = new Set(['Property', 'title', 'ID', 'readOnly', 'mustImplement',
            'minCard', 'maxCard', 'responseRequired', 'shellType', 'complete',
            '_completeWithoutOverride', 'isExpanded', 'hierarchyIndex',
            '_hideChildrenUntilRepeat', '_parentSelected', '_hasOptionalParent',
            // Already handled above — don't double-count
            'Response', 'ResponseField', 'ListItemResponseField', 'List']);
        for (const key in data) {
            if (!data.hasOwnProperty(key)) continue;
            if (skipKeys.has(key)) continue;
            const val = data[key];
            if (val && typeof val === 'object') {
                if (!this.sectionFullyAnswered(val)) return false;
            }
        }
        return true;
    }

    private onKeyDown(event: KeyboardEvent) {
        if (!this.props.enableKeyboardNavigation) return;

        // Don't intercept keys when user is typing in an input field
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        const inputType = tagName === 'input' ? ((target as HTMLInputElement).type || '').toLowerCase() : '';
        const isSelectableInput = tagName === 'input' && (inputType === 'radio' || inputType === 'checkbox');
        const isTextualInput = tagName === 'textarea' || target.isContentEditable ||
            (tagName === 'input' && !isSelectableInput && inputType !== 'button' && inputType !== 'submit');

        const key = event.key;
        const isSpace = key === ' ' || key === 'Space' || key === 'Spacebar';
        const isEnter = key === 'Enter';

        // Enter/Space on a radio/checkbox: select (toggle for checkbox, select for radio),
        // then move focus to the first child enterable (e.g. "specify" textbox) if any.
        // This lets the user activate with keyboard AND auto-advances into dependent
        // inputs — matching the legacy JSP behavior and the user's expectation.
        if (isSelectableInput && (isEnter || isSpace)) {
            event.preventDefault();
            event.stopPropagation();
            this.activateSelectableInput(target as HTMLInputElement);
            return;
        }

        // Tab / Shift+Tab: jump to the next (or previous) unanswered Question's
        // first enterable element. Handle this BEFORE the textual-input guard
        // so Tab still navigates when the user is currently typing in a
        // textarea / text / numeric input. Only consume the event if we
        // actually move focus — otherwise let the browser do the default Tab
        // so focus can leave the component entirely.
        if (key === 'Tab') {
            // Handle Date Time focus
            const activeEl = document.activeElement as HTMLElement | null;
            if (activeEl && activeEl.tagName.toLowerCase() === 'input') {
                const t = ((activeEl as HTMLInputElement).type || '').toLowerCase();
                if (t === 'date' || t === 'datetime-local' || t === 'time'
                        || t === 'month' || t === 'week') {
                    return; // let the browser do its thing
                }
            }
            const handled = this.navigateTab(event.shiftKey);
            if (handled) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }

        // If user is typing in a textual input, let them use all keys freely —
        // EXCEPT Arrow Up/Down on a <textarea> / numeric / text input. These
        // fields consume Up/Down for internal cursor or value movement, which
        // "traps" keyboard users inside them. Override Up/Down to exit the
        // field and move to the adjacent row, matching textarea behavior.
        if (isTextualInput) {
            if (key === 'ArrowDown' || key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                // Blur so the cursor/spinner interaction ends; navigateArrow
                // will focus the next row's wrapper (or its non-textual input).
                (target as HTMLElement).blur();
                this.navigateArrow(key === 'ArrowDown' ? 'down' : 'up');
            }
            return;
        }

        // If the currently focused element is a ROW WRAPPER (data-ecp-row-index)
        // whose own enterable is a textual input (textarea / text / number),
        // then ArrowRight / Space / Enter should move focus INTO that input
        // AND select all of its text so the user can type a replacement value
        // right away. Up/Down arrows only land focus on the wrapper (row
        // highlight) so the cursor isn't captured automatically.
        if (key === 'ArrowRight' || isEnter || isSpace) {
            const active = document.activeElement as HTMLElement;
            if (active && active.hasAttribute && active.hasAttribute('data-ecp-row-index')) {
                const own = this.findOwnEnterable(active);
                if (own && ECPHierarchyList.isTextualElement(own)) {
                    event.preventDefault();
                    event.stopPropagation();
                    own.focus({ preventScroll: false });
                    // Select the current contents so typing replaces it.
                    const selectable = own as HTMLInputElement | HTMLTextAreaElement;
                    if (typeof selectable.select === 'function') {
                        try { selectable.select(); } catch (_) { /* ignore */ }
                    }
                    return;
                }
            }
        }

        // Arrow Up/Down: move focus between navigable rows (headers AND input rows)
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            this.navigateArrow(key === 'ArrowDown' ? 'down' : 'up');
            return;
        }

        // Arrow Left/Right: collapse/expand the currently focused accordion
        if (key === 'ArrowLeft' || key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            this.handleLeftRight(key === 'ArrowLeft' ? 'left' : 'right');
            return;
        }

        // Enter/Space on an accordion header: toggle expand/collapse.
        if (isEnter || isSpace) {
            const active = document.activeElement as HTMLElement;
            if (active && active.id && active.id.indexOf('eCPHierarchy_') === 0) {
                event.preventDefault();
                event.stopPropagation();
                this.activateFocusedElement();
            }
            return;
        }
    }

    // ───────────────────��─────────��───────────────��───────────────────────────
    // Navigation Methods
    // ───────────────────────────────────────────────────────────────────────��─

    /**
     * Resolve the currently focused hierarchy index, preferring the DOM activeElement
     * over state so the user's actual focus is always respected.
     */
    private getCurrentHierarchyIndex(): number {
        const active = document.activeElement as HTMLElement;
        if (active) {
            // Accordion header (Section / Question / FormDesign)
            if (active.id && active.id.indexOf('eCPHierarchy_') === 0) {
                const idxStr = active.id.substring('eCPHierarchy_'.length);
                const idx = parseInt(idxStr, 10);
                if (!isNaN(idx)) return idx;
            }
            // Input / response row — walk up to the closest row wrapper that
            // carries data-ecp-row-index (set by ShellItem/ResponseField renders).
            const rowEl = (typeof (active as any).closest === 'function')
                ? (active.closest('[data-ecp-row-index]') as HTMLElement | null)
                : null;
            if (rowEl) {
                const idx = parseInt(rowEl.getAttribute('data-ecp-row-index') || '', 10);
                if (!isNaN(idx)) return idx;
            }
        }
        if (this.state.focusedHierarchyIndex !== undefined) {
            return this.state.focusedHierarchyIndex;
        }
        return -1;
    }

    /**
     * Get the list of flat map entries that represent focusable rows.
     * Includes accordion headers AND interactive input rows (radio/checkbox/
     * textarea/text-input) so Up/Down arrows don't skip over enterable options.
     */
    private getNavigableEntries(): any[] {
        const focusableKeys = new Set([
            'Section', 'Question', 'FormDesign',
            'ShellItem', 'ResponseField', 'ListItemResponseField',
        ]);
        return this.hierarchyFlatMap.filter((e: any) => {
            if (!focusableKeys.has(e.key)) return false;
            const obj = e.obj;
            // DisplayedItem ShellItems are purely informational — not navigable.
            if (e.key === 'ShellItem' && obj && obj.shellType === 'DisplayedItem') return false;

            // ── Model-level disabled checks (apply to ALL row types including
            //    Section / Question accordions — a disabled Question is not
            //    useful to navigate to) ──
            if (obj) {
                if (obj._disabled === true) return false;
                if (obj._parentSelected === false) return false;
            }
            // Component-level disabled prop: only accordion headers stay
            // navigable (so the user can still collapse/expand sections).
            if (this.props.disabled === true
                && e.key !== 'Section' && e.key !== 'Question' && e.key !== 'FormDesign') {
                return false;
            }

            // ── DOM-level disabled check ──
            // For accordion headers (Section/Question/FormDesign): only check
            // if they live inside an ecp-child-disabled container. Do NOT check
            // for enabled interactive elements — a collapsed Question has no
            // content div in the DOM, and even an expanded one may only contain
            // child accordions (no direct inputs). Accordion headers must
            // always be navigable so the user can expand/collapse them.
            if (e.key === 'Section' || e.key === 'Question' || e.key === 'FormDesign') {
                // When Hide Optional is active, an optional Section/Question
                // is still recorded in hierarchyFlatMap (so its index is
                // reserved) but its header is not rendered into the DOM.
                // Such rows must not be navigable — otherwise focus lands
                // nowhere when the user clicks Hide Optional and the first
                // item in the hierarchy happens to be optional.
                if (this.state.bHideOptional && isOptional(obj)) {
                    return false;
                }
                const headerEl = document.getElementById('eCPHierarchy_' + e.hierarchyIndex);
                if (headerEl && typeof headerEl.closest === 'function' && headerEl.closest('.ecp-child-disabled')) {
                    return false;
                }
                return true;
            }

            // For input rows (ShellItem / ResponseField / ListItemResponseField):
            // check the actual rendered DOM.
            const rowEl = document.querySelector(
                '[data-ecp-row-index="' + e.hierarchyIndex + '"]'
            ) as HTMLElement | null;

            if (rowEl) {
                // Skip if this row (or any ancestor) lives inside an
                // `.ecp-child-disabled` container — those are children of
                // unselected ShellItems and should not be navigable.
                if (typeof rowEl.closest === 'function' && rowEl.closest('.ecp-child-disabled')) {
                    return false;
                }

                // Skip if every interactive element in this row is :disabled.
                const anyEnabled = rowEl.querySelector(
                    'textarea:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
                    + 'input:not([type="hidden"]):not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
                    + 'select:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *)'
                );
                if (!anyEnabled) return false;
            }

            return true;
        });
    }

    /**
     * Focus a hierarchy row by index. Updates state and moves DOM focus to
     * either the accordion header or the first input inside the row.
     */
    private focusHierarchyRow(hierarchyIndex: number) {
        this.setState({ focusedHierarchyIndex: hierarchyIndex }, () => {
            const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === hierarchyIndex);
            const accordionKeys = new Set(['Section', 'Question', 'FormDesign']);
            let focusTarget: HTMLElement | null = null;
            if (entry && accordionKeys.has(entry.key)) {
                const el = document.getElementById('eCPHierarchy_' + hierarchyIndex);
                if (el) {
                    el.focus({ preventScroll: true });
                    focusTarget = el;
                }
            } else {
                // Input row — look up the wrapper div (data-ecp-row-index=N) and focus
                // its own interactive element (radio/checkbox/textarea/input/select),
                // skipping any that live inside a nested ShellItem-children block.
                const row = document.querySelector(
                    '[data-ecp-row-index="' + hierarchyIndex + '"]'
                ) as HTMLElement | null;
                if (row) {
                    const own = this.findOwnEnterable(row);
                    if (own) {
                        own.focus({ preventScroll: true });
                        focusTarget = own;
                        // If it's a textual input (textarea / text / number),
                        // select all text so the user can start typing to
                        // overwrite the current value immediately.
                        if (ECPHierarchyList.isTextualElement(own)) {
                            const selectable = own as HTMLInputElement | HTMLTextAreaElement;
                            if (typeof selectable.select === 'function') {
                                try { selectable.select(); } catch (_) { /* ignore */ }
                            }
                        }
                    } else {
                        // No interactive element — focus the row wrapper itself
                        // so the highlight follows the user's position.
                        if (!row.hasAttribute('tabindex')) row.setAttribute('tabindex', '-1');
                        row.focus({ preventScroll: true });
                        focusTarget = row;
                    }
                }
            }
            if (focusTarget) {
                this.centerFocusInContainer(focusTarget, hierarchyIndex);
            }
        });
    }

    /**
     * Find the nearest ancestor (or self) that is the actual scroll container.
     * Walk up from listRowRef checking for overflow:auto/scroll AND actual
     * scrollable content (scrollHeight > clientHeight). If no CSS-overflow
     * ancestor actually scrolls, fall back to the first CSS-overflow ancestor
     * (it may start scrolling once content grows) or the document element.
     */
    private getScrollContainer(): HTMLElement | null {
        const el: HTMLElement | null = this.listRowRef.current;
        if (!el) return null;

        let firstOverflow: HTMLElement | null = null;
        let current: HTMLElement | null = el.parentElement;
        while (current && current !== document.documentElement) {
            try {
                const style = window.getComputedStyle(current);
                const oy = style.overflowY || '';
                if (oy === 'auto' || oy === 'scroll') {
                    if (!firstOverflow) firstOverflow = current;
                    // This one actually has a scrollbar right now
                    if (current.scrollHeight > current.clientHeight + 1) {
                        return current;
                    }
                }
            } catch (_) { /* ignore */ }
            current = current.parentElement;
        }
        // No ancestor is currently scrolling — return the first one with
        // overflow:auto/scroll (it will scroll once content exceeds its height),
        // or the document element as last resort.
        return firstOverflow || document.documentElement;
    }

    /**
     * Scroll the scroll container so that `el` is vertically centered.
     * Uses direct scrollTop assignment so rapid key presses take effect
     * immediately. Also keeps the element horizontally visible.
     */
    private centerFocusInContainer(el: HTMLElement, _hierarchyIndex?: number): void {
        const container = this.getScrollContainer();
        if (!container || !el) return;

        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();

        // How far the element's vertical center is from the container's
        // visible vertical center, in pixels.  Positive = element is below
        // the container's center and we need to scroll DOWN.
        const elementCenterY = eRect.top + eRect.height / 2;
        const containerCenterY = cRect.top + cRect.height / 2;
        const offset = elementCenterY - containerCenterY;

        // Apply that offset to the current scrollTop.
        const desiredScrollTop = container.scrollTop + offset;

        // Clamp to valid scroll range.
        const maxScrollTop = container.scrollHeight - container.clientHeight;
        const clampedScrollTop = Math.max(0, Math.min(desiredScrollTop, maxScrollTop));

        // Always scroll (tiny threshold to avoid sub-pixel jitter).
        if (Math.abs(container.scrollTop - clampedScrollTop) > 1) {
            container.scrollTop = clampedScrollTop;
        }

        // ── Horizontal: keep element visible without re-centering ──
        const leftMargin = 16;
        const rightMargin = 16;
        if (eRect.left < cRect.left + leftMargin) {
            container.scrollLeft += eRect.left - (cRect.left + leftMargin);
        } else if (eRect.right > cRect.right - rightMargin) {
            container.scrollLeft += eRect.right - (cRect.right - rightMargin);
        }
    }

    /**
     * Find the first interactive element that belongs directly to this row —
     * not one nested inside a child ShellItem's children container.
     */
    private findOwnEnterable(row: HTMLElement): HTMLElement | null {
        // Exclude `.categoryAllHide` — ReactAutoExpandTextArea hides a disable
        // checkbox + adjust-size button in that class. Matching one of those
        // would land focus on an invisible control.
        const selector = 'textarea:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="radio"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="checkbox"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="text"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="number"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="date"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="datetime-local"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input[type="time"]:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input:not([type]):not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'select:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *)';
        const candidates = row.querySelectorAll(selector);
        for (let i = 0; i < candidates.length; i++) {
            const el = candidates[i] as HTMLElement;
            // Skip inputs that live inside a *nested* ShellItem children block —
            // those belong to a deeper row and will be reached by further arrowing.
            const nestedRow = el.closest('[data-ecp-row-index]') as HTMLElement | null;
            if (nestedRow && nestedRow !== row) continue;
            // Defensive: skip anything inside .categoryAllHide
            if (el.closest && el.closest('.categoryAllHide')) continue;
            // Skip anything inside an ecp-child-disabled container (children
            // of an unselected ShellItem — visually dimmed + pointer-events:none)
            if (el.closest && el.closest('.ecp-child-disabled')) continue;
            return el;
        }
        return null;
    }

    /**
     * Within a ShellItem row, find the first interactive element inside its
     * `.ecp-shellitem-children` block (e.g. the "specify" textbox that appears
     * under a radio/checkbox option). Returns null if there isn't one.
     */
    private findFirstEnterableInShellItemChildren(rowIndex: number): HTMLElement | null {
        const row = document.querySelector(
            '[data-ecp-row-index="' + rowIndex + '"]'
        ) as HTMLElement | null;
        if (!row) return null;
        const container = row.querySelector('.ecp-shellitem-children') as HTMLElement | null;
        if (!container) return null;
        // NOTE: exclude `.categoryAllHide` — ReactAutoExpandTextArea renders a
        // hidden <input type="checkbox"> and <button> inside a .categoryAllHide
        // table BEFORE its real <textarea>. Without this filter, focus lands on
        // the invisible checkbox and the user never sees a cursor.
        const selector = 'textarea:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'input:not([type="hidden"]):not([disabled]):not(.categoryAllHide):not(.categoryAllHide *),'
            + 'select:not([disabled]):not(.categoryAllHide):not(.categoryAllHide *)';
        const candidates = container.querySelectorAll(selector);
        for (let i = 0; i < candidates.length; i++) {
            const el = candidates[i] as HTMLElement;
            // Double-check: skip anything whose ancestor is .categoryAllHide
            // (the :not(.categoryAllHide *) selector is supported in modern
            // browsers, but belt-and-suspenders for older engines).
            if (el.closest && el.closest('.categoryAllHide')) continue;
            return el;
        }
        return null;
    }

    /**
     * Keyboard activation of a focused radio or checkbox. Toggles the selection
     * (radios can only be selected, never unselected via keyboard — matching
     * the user's requirement) and, once selected, moves focus to the first
     * child enterable (e.g. a "specify" textbox) if one exists so the user
     * can continue answering without reaching for the mouse or Tab key.
     */
    private activateSelectableInput(input: HTMLInputElement) {
        const rowEl = (typeof (input as any).closest === 'function')
            ? (input.closest('[data-ecp-row-index]') as HTMLElement | null)
            : null;
        if (!rowEl) return;
        const rowIndex = parseInt(rowEl.getAttribute('data-ecp-row-index') || '', 10);
        if (isNaN(rowIndex)) return;
        const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === rowIndex);
        if (!entry || entry.key !== 'ShellItem') return;

        const data = entry.obj;
        if (data._disabled === true || this.props.disabled === true) return;

        const isCheckbox = (input.type || '').toLowerCase() === 'checkbox';
        const wasSelected = data.selected === true || data.selected === 'true';
        let newChecked: boolean;
        if (isCheckbox) {
            newChecked = !wasSelected;
        } else {
            // Radio: pressing Enter/Space never UNSELECTS — it only selects.
            newChecked = true;
        }

        this.handleShellItemChange(data, newChecked, isCheckbox, (entry as any).parent);
        // handleShellItemChange will move focus to the first child enterable
        // (e.g. a "specify" textbox) after the re-render when newChecked===true.
    }

    private navigateArrow(direction: 'up' | 'down') {
        const entries = this.getNavigableEntries();
        if (entries.length === 0) return;

        const currentIdx = this.getCurrentHierarchyIndex();
        let targetPos: number;

        if (currentIdx < 0) {
            // No current focus — focus first (or last on ArrowUp)
            targetPos = direction === 'down' ? 0 : entries.length - 1;
        } else {
            const curPos = entries.findIndex((e: any) => e.hierarchyIndex === currentIdx);
            if (curPos < 0) {
                targetPos = direction === 'down' ? 0 : entries.length - 1;
            } else {
                targetPos = direction === 'down'
                    ? Math.min(curPos + 1, entries.length - 1)
                    : Math.max(curPos - 1, 0);
                // Already at the boundary — re-focus the current element so
                // focus doesn't get lost (the caller may have already blurred
                // the previously focused textarea/input before calling us).
            }
        }

        const target = entries[targetPos];
        if (target) {
            this.focusHierarchyRow(target.hierarchyIndex);
        }
    }

    private handleLeftRight(direction: 'left' | 'right') {
        const currentIdx = this.getCurrentHierarchyIndex();
        if (currentIdx < 0) return;

        const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === currentIdx);
        if (!entry) return;

        if (entry.key !== 'Section' && entry.key !== 'Question') return;

        const isCurrentlyExpanded = entry.obj.isExpanded !== false;

        if (direction === 'right' && !isCurrentlyExpanded) {
            // Expand
            entry.obj.isExpanded = true;
            this.setState({ translatedJSON: this.state.translatedJSON, rowCount: -1 });
        } else if (direction === 'left' && isCurrentlyExpanded) {
            // Collapse
            entry.obj.isExpanded = false;
            this.setState({ translatedJSON: this.state.translatedJSON, rowCount: -1 });
        } else if (direction === 'left' && !isCurrentlyExpanded) {
            // Already collapsed — move focus up to the parent accordion (if any)
            const entries = this.getNavigableEntries();
            const curPos = entries.findIndex((e: any) => e.hierarchyIndex === currentIdx);
            if (curPos > 0) {
                this.focusHierarchyRow(entries[curPos - 1].hierarchyIndex);
            }
        }
    }

    /**
     * Tab navigation: jump to the first enterable element (radio/checkbox/textarea/
     * input/select) inside the next (or previous for Shift+Tab) UNANSWERED Question.
     * Matches the old JSP onCustomTabNavigation behavior.
     *
     * Returns true if we handled the key (caller should preventDefault), false if
     * there's no next unanswered question so the browser can do default Tab.
     */
    private navigateTab(isShiftTab: boolean): boolean {
        const questions: Array<{ obj: any; ancestors: any[] }> = [];
        this.collectQuestions(this.state.translatedJSON, [], questions);

        if (questions.length === 0) {
            return false;
        }

        const currentQIdx = this.findCurrentQuestionPos(questions);

        const step = isShiftTab ? -1 : 1;
        let startIdx: number;
        if (currentQIdx < 0) {
            // No Question currently focused — the user may be on a Section
            // header or other non-Question element. Use the focused element's
            // hierarchyIndex to find the nearest question in the search
            // direction so Tab starts from where the user is, not from the top.
            const focusedHIdx = this.getCurrentHierarchyIndex();
            if (focusedHIdx >= 0) {
                // Look up the focused element's data object so we can check
                // ancestry relationships (works even when children are collapsed
                // and therefore not in the flat map).
                const focusedEntry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === focusedHIdx);
                const focusedObj = focusedEntry ? focusedEntry.obj : null;

                // Helper: get the effective hierarchyIndex for a question,
                // even if the question itself is inside a collapsed section.
                // Walks the ancestors (innermost first) looking for one that
                // IS rendered in the flat map, so collapsed questions still
                // get a usable position from their parent Section header.
                const getQuestionPosition = (q: { obj: any; ancestors: any[] }): number => {
                    let hIdx = this.findHierarchyIndexForObject(q.obj);
                    if (hIdx >= 0) return hIdx;
                    for (let j = q.ancestors.length - 1; j >= 0; j--) {
                        hIdx = this.findHierarchyIndexForObject(q.ancestors[j]);
                        if (hIdx >= 0) return hIdx;
                    }
                    return -1;
                };

                let nearestIdx = -1;
                for (let i = 0; i < questions.length; i++) {
                    const q = questions[i];
                    // Direct ancestry check: if focusedObj IS one of this
                    // question's ancestors (or the question itself), this
                    // question is "inside" the focused section — always a
                    // valid starting point for forward Tab.
                    const isInside = focusedObj && (
                        q.obj === focusedObj ||
                        q.ancestors.indexOf(focusedObj) >= 0
                    );
                    const qPos = getQuestionPosition(q);

                    if (!isShiftTab) {
                        if (isInside || qPos >= focusedHIdx) {
                            nearestIdx = i;
                            break;
                        }
                    } else {
                        // Shift+Tab: want the LAST question at or before focused
                        if (isInside || (qPos >= 0 && qPos <= focusedHIdx)) {
                            nearestIdx = i;
                        }
                    }
                }
                startIdx = nearestIdx >= 0
                    ? nearestIdx
                    : (isShiftTab ? questions.length - 1 : 0);
            } else {
                startIdx = isShiftTab ? questions.length - 1 : 0;
            }
        } else {
            startIdx = currentQIdx + step;
        }

        for (let i = startIdx; i >= 0 && i < questions.length; i += step) {
            const q = questions[i];

            if (this.isQuestionDisabled(q.obj)) continue;
            if (this.isRepeatPlaceholder(q)) continue;
            if (this.isQuestionHiddenByOptional(q)) continue;
            if (this.isQuestionAnswered(q.obj)) continue;

            let needsRender = false;
            q.ancestors.forEach((a: any) => {
                if (a && a.isExpanded === false) {
                    a.isExpanded = true;
                    needsRender = true;
                }
            });
            if (q.obj.isExpanded === false) {
                q.obj.isExpanded = true;
                needsRender = true;
            }

            const focusFirstEnterable = () => {
                const hIdx = this.findHierarchyIndexForObject(q.obj);
                if (hIdx < 0) return false;
                const el = this.findFirstEnterableInQuestion(hIdx);
                if (el) {
                    if (el === document.activeElement) return false;
                    el.focus({ preventScroll: true });
                    this.scrollEnterableIntoView(el, hIdx);
                    this.setFocusedHierarchyFromElement(el, hIdx);
                    return true;
                }
                return false;
            };

            if (needsRender) {
                this.setState({ translatedJSON: this.state.translatedJSON, rowCount: -1 }, () => {
                    requestAnimationFrame(() => {
                        if (!focusFirstEnterable()) {
                            this.navigateTabFrom(i, isShiftTab, questions);
                        }
                    });
                });
            } else {
                if (!focusFirstEnterable()) {
                    continue;
                }
            }
            return true;
        }

        return false;
    }

    /**
     * Continuation helper used when the previous candidate Question had no
     * enterable elements after expansion — keeps searching from the next
     * position in the same direction.
     */
    private navigateTabFrom(startPos: number, isShiftTab: boolean, questions: Array<{ obj: any; ancestors: any[] }>) {
        const step = isShiftTab ? -1 : 1;
        for (let i = startPos + step; i >= 0 && i < questions.length; i += step) {
            const q = questions[i];
            if (this.isQuestionDisabled(q.obj)) continue;
            if (this.isRepeatPlaceholder(q)) continue;
            if (this.isQuestionHiddenByOptional(q)) continue;
            if (this.isQuestionAnswered(q.obj)) continue;

            const hIdx = this.findHierarchyIndexForObject(q.obj);
            if (hIdx < 0) continue;
            const el = this.findFirstEnterableInQuestion(hIdx);
            if (el) {
                el.focus({ preventScroll: true });
                this.scrollEnterableIntoView(el, hIdx);
                this.setFocusedHierarchyFromElement(el, hIdx);
                return;
            }
        }
    }

    /**
     * When Hide Optional is active, a Question (or any of its Section/Question
     * ancestors) marked mustImplement="false" is not rendered — so Tab must
     * skip it instead of trying to focus an element that doesn't exist in
     * the DOM. Returns true if this question is currently hidden by the
     * Hide Optional filter.
     */
    private isQuestionHiddenByOptional(q: { obj: any; ancestors: any[] }): boolean {
        if (!this.state.bHideOptional) return false;
        if (isOptional(q.obj)) return true;
        for (const a of q.ancestors) {
            if (a && isOptional(a)) return true;
        }
        return false;
    }

    /**
     * A Question (or an ancestor of it) flagged with _hideChildrenUntilRepeat
     * is a repeat-section "+" placeholder — a structural clone that only
     * exists so the user can click "+" to add another instance. It has no
     * real inputs to fill in, so Tab must never focus or expand into it.
     */
    private isRepeatPlaceholder(q: { obj: any; ancestors: any[] }): boolean {
        if (q.obj && q.obj._hideChildrenUntilRepeat === true) return true;
        for (const a of q.ancestors) {
            if (a && a._hideChildrenUntilRepeat === true) return true;
        }
        return false;
    }

    /**
     * Depth-first walk of the data tree collecting every Question node along
     * with its chain of Section/Question ancestors (so Tab can expand them).
     */
    private collectQuestions(node: any, ancestors: any[], out: Array<{ obj: any; ancestors: any[] }>): void {
        if (!node || typeof node !== 'object') return;

        if (Array.isArray(node)) {
            node.forEach(item => this.collectQuestions(item, ancestors, out));
            return;
        }

        // If this node wraps a Question, record it
        if (node.Question) {
            const questions = Array.isArray(node.Question) ? node.Question : [node.Question];
            questions.forEach((q: any) => {
                if (!q) return;
                out.push({ obj: q, ancestors: ancestors.slice() });
                // Recurse into the Question's own children (Questions can nest)
                this.collectQuestions(q, ancestors.concat([q]), out);
            });
        }

        // Recurse into Section children
        if (node.Section) {
            const sections = Array.isArray(node.Section) ? node.Section : [node.Section];
            sections.forEach((s: any) => {
                if (!s) return;
                this.collectQuestions(s, ancestors.concat([s]), out);
            });
        }

        // Walk ChildItems (wrappers around Section / Question / ShellItem / etc.)
        if (Array.isArray(node.ChildItems)) {
            node.ChildItems.forEach((wrapper: any) => {
                if (!wrapper) return;
                this.collectQuestions(wrapper, ancestors, out);
            });
        }

        // FormDesign.Body / List etc. — generic recursion
        ['Body', 'FormDesign', 'List', 'ListField'].forEach(k => {
            if (node[k] && typeof node[k] === 'object') {
                this.collectQuestions(node[k], ancestors, out);
            }
        });

        // ShellItem.ChildItems may contain Questions too
        if (node.ShellItem && typeof node.ShellItem === 'object') {
            this.collectQuestions(node.ShellItem, ancestors, out);
        }
    }

    /**
     * Find the position (in the collected questions list) of the Question
     * that currently contains the DOM focus. Returns -1 if none.
     */
    private findCurrentQuestionPos(questions: Array<{ obj: any; ancestors: any[] }>): number {
        const active = document.activeElement as HTMLElement;
        if (!active) {
            return -1;
        }

        // Walk up the DOM looking for either a Question header (id=eCPHierarchy_N
        // where the flat-map entry's key is Question) or an eCPHierarchy_N_contentDiv
        // belonging to a Question.
        let el: HTMLElement | null = active;
        while (el) {
            if (el.id) {
                // Header element
                const m1 = el.id.match(/^eCPHierarchy_(\d+)$/);
                if (m1) {
                    const hIdx = parseInt(m1[1], 10);
                    const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === hIdx);
                    if (entry && entry.key === 'Question') {
                        return -1; // Focus is already on a Question
                    }
                }
                // Content div
                const m2 = el.id.match(/^eCPHierarchy_(\d+)_contentDiv$/);
                if (m2) {
                    const hIdx = parseInt(m2[1], 10);
                    const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === hIdx);
                    if (entry && entry.key === 'Question') {
                        return questions.findIndex(q => q.obj === entry.obj);
                    }
                }
            }
            el = el.parentElement;
        }
        // Fallback: use data-ecp-row-index on the closest ancestor row wrapper
        // to find which flat-map entry the active element belongs to, then walk
        // BACKWARDS through the flat map to the nearest Question ancestor.
        // This handles textareas and numeric inputs inside ResponseField rows
        // whose _contentDiv ancestor may not be reached by the ID-based walk above.
        if (typeof (active as any).closest === 'function') {
            const row = (active as any).closest('[data-ecp-row-index]') as HTMLElement | null;
            if (row) {
                const rowHIdx = parseInt(row.getAttribute('data-ecp-row-index') || '', 10);
                if (!isNaN(rowHIdx)) {
                    // Walk backwards from rowHIdx looking for the nearest Question.
                    for (let i = this.hierarchyFlatMap.length - 1; i >= 0; i--) {
                        const entry = this.hierarchyFlatMap[i];
                        if (entry.hierarchyIndex > rowHIdx) continue;
                        if (entry.key === 'Question') {
                            const qPos = questions.findIndex((q: any) => q.obj === entry.obj);
                            if (qPos >= 0) return qPos;
                        }
                    }
                }
            }
        }

        return -1;
    }

    /**
     * Find the hierarchyIndex for a given data object by consulting the flat map.
     * Only works for objects that are currently rendered (i.e. their ancestors
     * are expanded). Callers must ensure that before calling.
     */
    private findHierarchyIndexForObject(obj: any): number {
        const entry = this.hierarchyFlatMap.find((e: any) => e.obj === obj);
        return entry ? entry.hierarchyIndex : -1;
    }

    /**
     * Find the first enterable element inside a Question's content div.
     * Matches the selector used by the old JSP rowHasEnterableElement.
     */
    private findFirstEnterableInQuestion(hierarchyIndex: number): HTMLElement | null {
        const selector = [
            'textarea:not([disabled])',
            'input[type="radio"]:not([disabled])',
            'input[type="checkbox"]:not([disabled])',
            'input:not([type="hidden"]):not([disabled]):not([type="radio"]):not([type="checkbox"])',
            'select:not([disabled])',
        ].join(', ');

        const contentDiv = document.getElementById('eCPHierarchy_' + hierarchyIndex + '_contentDiv');
        if (contentDiv) {
            // Use querySelectorAll + iterate so we can check ancestor classes
            // that simple CSS :not() selectors can't handle (e.g. element
            // inside a .categoryAllHide or .ecp-child-disabled container).
            const candidates = contentDiv.querySelectorAll(selector);

            // Filter to valid candidates (not inside hidden/disabled containers).
            const validCandidates: HTMLElement[] = [];
            for (let i = 0; i < candidates.length; i++) {
                const el = candidates[i] as HTMLElement;
                if (el.closest && el.closest('.categoryAllHide')) continue;
                if (el.closest && el.closest('.ecp-child-disabled')) continue;
                validCandidates.push(el);
            }

            if (validCandidates.length === 0) {
                return null;
            }

            // PREFERRED: find the first unanswered required field. This handles
            // the case where a radio/checkbox IS selected but its required child
            // textarea/input is still empty — Tab should land on that child, not
            // on the already-selected radio.
            //
            // An "unanswered required" field is a textarea or text/number input
            // that is empty AND lives inside a ShellItem-children block whose
            // parent option IS selected (i.e. not inside ecp-child-disabled).
            for (let i = 0; i < validCandidates.length; i++) {
                const el = validCandidates[i] as HTMLInputElement | HTMLTextAreaElement;
                const tag = el.tagName.toLowerCase();
                const inputType = (tag === 'input' ? (el.type || '').toLowerCase() : '');

                // Only consider textual inputs (textarea, text, number) — these
                // are the enterables that hold response values.
                const isTextual = tag === 'textarea'
                    || inputType === 'text' || inputType === 'number'
                    || inputType === '' || inputType === 'search';
                if (!isTextual) continue;

                // Must be inside a shellitem-children block (child of a selected
                // option) — otherwise it's a standalone ResponseField.
                const childrenContainer = el.closest('.ecp-shellitem-children');
                if (!childrenContainer) continue;

                // Must be empty (unanswered).
                const value = el.value || '';
                if (value.trim() !== '') continue;

                return el;
            }

            // SECONDARY PREFERENCE: skip over radio/checkbox options that are
            // already `checked`. When a parent option is selected and exposes
            // a nested list of child radios/checkboxes, the first valid
            // candidate returned by querySelectorAll is the already-checked
            // parent radio — focusing it is useless because the answer is
            // the UN-checked child the user still has to pick. Prefer the
            // first candidate that isn't already selected.
            for (let i = 0; i < validCandidates.length; i++) {
                const el = validCandidates[i] as HTMLInputElement | HTMLTextAreaElement;
                const tag = el.tagName.toLowerCase();
                if (tag !== 'input') {
                    // Textareas / selects are never "checked"; first one wins.
                    return el;
                }
                const inputType = ((el as HTMLInputElement).type || '').toLowerCase();
                if (inputType === 'radio' || inputType === 'checkbox') {
                    if ((el as HTMLInputElement).checked) continue;
                }
                return el;
            }

            // Fallback: return the first valid candidate.
            return validCandidates[0];
        }
        return null;
    }

    /**
     * Resolve the hierarchyIndex that should visually be highlighted given the
     * element that just received keyboard focus. We prefer the closest
     * `[data-ecp-row-index]` wrapper (ShellItem row, ResponseField row, etc.)
     * because that's the row the user actually interacted with; falling back
     * to the passed-in `fallbackHIdx` (usually the Question header) only when
     * the enterable doesn't live inside a marked row wrapper.
     *
     * Why this matters: Tab moves DOM focus to a radio/textarea INSIDE the
     * Question's content div, but the Question's own header element has
     * `id="eCPHierarchy_<qIdx>"`. If we had set
     * `focusedHierarchyIndex = <qIdx>`, the Question header would light up
     * with the `hierarchyRowSelected` CSS class even though the caret /
     * keyboard focus is on the input one row below.
     */
    private setFocusedHierarchyFromElement(el: HTMLElement, fallbackHIdx: number): void {
        let rowHIdx = -1;
        if (typeof el.closest === 'function') {
            const row = el.closest('[data-ecp-row-index]') as HTMLElement | null;
            if (row) {
                const n = parseInt(row.getAttribute('data-ecp-row-index') || '', 10);
                if (!isNaN(n)) rowHIdx = n;
            }
        }
        const hIdx = rowHIdx >= 0 ? rowHIdx : fallbackHIdx;
        if (this.state.focusedHierarchyIndex !== hIdx) {
            this.setState({ focusedHierarchyIndex: hIdx });
        }
    }

    /**
     * Scroll the scroll-container so BOTH the Question's sticky header and the
     * freshly-focused enterable element are visible. We do this manually (with
     * preventScroll on .focus()) because:
     *
     *   1. The default browser "scroll into view on focus" is vertical-only in
     *      some engines, but a deeply-indented Question can also be clipped to
     *      the right edge of the scroll container. The user explicitly asked
     *      for Tab to adjust the horizontal scroll.
     *   2. The sticky Section/Question headers cover the top of the scroll
     *      container; a naive scrollIntoView would land the input UNDER the
     *      stacked headers. We account for the total sticky-header height so
     *      the input clears the headers.
     */
    private scrollEnterableIntoView(el: HTMLElement, questionHierarchyIndex: number): void {
        this.centerFocusInContainer(el);
    }

    /**
     * A Question is considered disabled (and thus skipped by Tab) if _disabled is
     * true or it has been marked disabled by selection propagation.
     */
    private isQuestionDisabled(q: any): boolean {
        if (!q) return true;
        if (q._disabled === true) return true;
        if (q._parentSelected === false) return true;
        return false;
    }

    /**
     * A Question is "answered" (and thus skipped by Tab) if:
     *   - The completeness calculation determined ALL required descendants
     *     are answered (complete === true). This is computed by
     *     updateCompleteStatus() before every render.
     *   - The user provided an override reason (making it intentionally blank)
     *
     * NOTE: We intentionally do NOT check hasAnswerInChildren() here.
     * A question where the user selected a radio option but left its required
     * child textarea empty is NOT complete — Tab must still visit it.
     */
    private isQuestionAnswered(q: any): boolean {
        if (!q) return false;
        if (q.complete === true || q.complete === 'true') return true;
        if (q.overrideReason && q.overrideReason !== '') return true;
        return false;
    }

    /**
     * Activate the currently focused element.
     * For Section/Question headers: toggle expand/collapse.
     */
    private activateFocusedElement() {
        const currentIdx = this.getCurrentHierarchyIndex();
        if (currentIdx < 0) return;

        const entry = this.hierarchyFlatMap.find((e: any) => e.hierarchyIndex === currentIdx);
        if (!entry) return;

        if (entry.key === 'Section' || entry.key === 'Question') {
            // Check if this Section/Question has a + repeat button
            if (entry.key === 'Section' && isRepeatable(entry.obj, 'Section')) {
                // Check if this is a placeholder showing a plus button
                if (entry.obj._hideChildrenUntilRepeat === true) {
                    this.handleRepeatPlus(entry.obj, 'Section');
                } else {
                    this.handleRepeatMinus(entry.obj, 'Section');
                }
                return; // Don't toggle expand after adding a repeat instance
            } else if (entry.key === 'Question' && isRepeatable(entry.obj, 'Question')) {
                // Check if this is a placeholder showing a plus button
                if (entry.obj._hideChildrenUntilRepeat === true) {
                    this.handleRepeatPlus(entry.obj, 'Question');
                } else {
                    this.handleRepeatMinus(entry.obj, 'Section');
                }
                return; // Don't toggle expand after adding a repeat instance
            }

            this.toggleExpand(entry.obj);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render Methods
    // ─────────────────────────────────────────────────────────────────────────

    private renderHierarchy(data: any, parentKey: string = '', level: number = 0, accumulatedLinkWidth: number = 0, stickyDepth: number = 0): React.ReactNode {
        // Reset flat map on first call (level 0)
        if (level === 0) {
            this.hierarchyFlatMap = [];
            this.hierarchyIndexSet.clear();
        }

        if (!data || typeof data !== 'object') {
            return null;
        }

        const elements: React.ReactNode[] = [];

        // Special handling for root level with FormDesign.
        // The FormDesign header itself is intentionally NOT rendered here —
        // the host page displays the form title elsewhere (e.g. in a page
        // breadcrumb / tab header). We skip the header but still recurse
        // into FormDesign.Body so all Sections/Questions render normally.
        // We also skip adding FormDesign to the flat map so it isn't
        // reachable by Arrow-Up/Down keyboard navigation.
        if (data.FormDesign && level === 0) {
            const formDesign = data.FormDesign;
            if (formDesign.Body) {
                elements.push(this.renderHierarchy(formDesign.Body, 'Body', level + 1, accumulatedLinkWidth, stickyDepth));
            }

            return <>{elements}</>;
        }

        // Collect renderable children from BOTH sources:
        //   1) data.ChildItems — wrappers like { Question: {...} }
        //   2) Direct properties on `data` itself (e.g. data.ListField,
        //      data.Question, data.ResponseField, data.Section …)
        //
        // Some JSON payloads place siblings directly on the parent object
        // rather than inside ChildItems (a Question may have a direct
        // `.ListField` radio group AND a direct `.Question` sub-question).
        // Rendering only ChildItems caused those direct-property siblings to
        // be skipped or routed through a different code path that produced an
        // inconsistent indent, making siblings look like children of each
        // other. Gathering everything into one ordered list guarantees they
        // all render at the SAME level with the same accumulatedLinkWidth.
        const collected: Array<{ key: string; value: any }> = [];

        if (Array.isArray(data.ChildItems)) {
            data.ChildItems.forEach((wrapper: any) => {
                const child = getWrapperChild(wrapper);
                if (child) collected.push(child);
            });
        }

        // Pull direct-property children (anything in `childTypes`) off `data`.
        // Skip `data.ChildItems` which we already consumed above.
        childTypes.forEach((type) => {
            const val = data[type];
            if (val == null) return;
            const arr = Array.isArray(val) ? val : [val];
            arr.forEach((v: any) => {
                if (v && typeof v === 'object') collected.push({ key: type, value: v });
            });
        });

        // Preserve JSON `order` field so mixed ChildItems + direct-property
        // siblings interleave in the author's intended order. Items without
        // an `order` fall to the end, maintaining insertion order among
        // themselves (Array.prototype.sort is stable in modern engines).
        collected.sort((a, b) => {
            const oa = getOrderNum(a.value);
            const ob = getOrderNum(b.value);
            if (oa !== ob) return oa - ob;
            // Same _originalOrder slot (a repeatable section and its clones
            // / placeholder): keep source first, instances in numeric order,
            // placeholder last.
            return getRepeatSubOrder(a.value) - getRepeatSubOrder(b.value);
        });

        collected.forEach((child, idx) => {
            const { key, value } = child;
            const hierarchyIndex = this.hierarchyFlatMap.length;

            this.hierarchyFlatMap.push({
                hierarchyIndex,
                obj: value,
                key,
                level,
            });
            this.hierarchyIndexSet.add(hierarchyIndex);

            if (this.state.bHideOptional && shouldCollapseItem(value, key)) { // Check for any children of optional items that have unanswered Required responses
                return;
            }

            const content = this.renderElementContent(value, key, hierarchyIndex, level, idx, accumulatedLinkWidth, stickyDepth);
            if (content) {
                elements.push(content);
            }
        });

        return elements.length > 0 ? <>{elements}</> : null;
    }

    private renderElementContent(data: any, key: string, hierarchyIndex: number, level: number, index: number, accumulatedLinkWidth: number, stickyDepth: number = 0): React.ReactNode {
        // Render the inner content based on element type
        if (key === 'Section') {
            return this.renderSection(data, hierarchyIndex, level, index, accumulatedLinkWidth, stickyDepth);
        } else if (key === 'Question') {
            return this.renderQuestion(data, hierarchyIndex, level, index, accumulatedLinkWidth, stickyDepth);
        } else if (key === 'ShellItem') {
            return this.renderShellItem(data, hierarchyIndex, level, index, stickyDepth);
        } else if (key === 'ResponseField' || key === 'ListItemResponseField') {
            return this.renderResponseField(data, hierarchyIndex, level, index, key);
        } else if (key === 'ListField') {
            return this.renderListField(data, hierarchyIndex, level, index, stickyDepth, accumulatedLinkWidth);
        } else if (key === 'Footer') {
            return this.renderFooter(data, hierarchyIndex, level, index);
        }
        return null;
    }

    private renderFormDesign(data: any, hierarchyIndex: number, level: number): React.ReactNode {
        const title = data.formTitle || 'eCP Form';
        return (
            <div
                key={`formdesign-${hierarchyIndex}`}
                id={`eCPHierarchy_${hierarchyIndex}`}
                className="ecpSectionHeader ecp-sticky-header"
                style={{ top: '0px', zIndex: 25 }}
            >
                {this.renderDebugOrder(data)}
                <label>{title}</label>
            </div>
        );
    }

    private renderSection(data: any, hierarchyIndex: number, level: number, index: number, accumulatedLinkWidth: number, stickyDepth: number = 0): React.ReactNode {
        const isPlaceholder = data._hideChildrenUntilRepeat === true;
        // Placeholders render only the header row (with the + button) and no
        // expandable content beneath — even if isExpanded is true.
        const isExpanded = !isPlaceholder && data.isExpanded !== false;
        const isComplete = this.isElementComplete(data);
        const optional = isOptional(data);
        const showCheck = isComplete && !optional && !isPlaceholder;
        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const cssClass = `ecpSections ecp-sticky-header${isFocused ? ' hierarchyRowSelected' : ''}`;

        // No accumulated link-width offset on the header — visual nesting
        // comes from the parent's .ecp-content paddingLeft (matching the JSP).
        const indent = 0;

        const repeatButton = this.renderRepeatButton(data, 'Section');
        const hasToggle = !isPlaceholder;
        const instanceLabel = (!isPlaceholder && data._instanceNumber)
            ? ` (${data._instanceNumber})`
            : '';

        return (
            // IMPORTANT: wrapper must NOT have marginLeft — sticky headers must span the full
            // scroll-container width so their opaque background covers scrolling content on
            // the left side. Indent is applied as paddingLeft on the header and content div.
            <div key={`section-${hierarchyIndex}`} className="ecp-section-wrapper">
                <div
                    id={`eCPHierarchy_${hierarchyIndex}`}
                    className={cssClass}
                    onClick={() => { if (hasToggle) this.toggleExpand(data); }}
                    {...(this.props.enableKeyboardNavigation ? { tabIndex: 0 } : {})}
                    style={{
                        top: this.getStickyTop(stickyDepth) + 'px',
                        paddingLeft: (indent + 8) + 'px',
                        zIndex: this.getStickyZIndex(stickyDepth),
                    }}
                >
                    {this.renderDebugOrder(data)}
                    {this.renderLinks(data)}
                    {repeatButton}
                    {hasToggle ? (
                        <span
                            className={isExpanded
                                ? 'ui-accordion-header-icon ui-icon ui-icon-triangle-1-s'
                                : 'ui-accordion-header-icon ui-icon ui-icon-triangle-1-e'}
                            style={{ width: '16px', marginTop: '1px' }}
                        />
                    ) : (
                        // Placeholder row: no toggle triangle (there are no
                        // children to expand) but reserve the same 16px so
                        // the title stays aligned with non-placeholder rows.
                        <span style={{ display: 'inline-block', width: '16px' }} />
                    )}
                    {this.renderTitleLabel(data)}
                    {instanceLabel && (
                        <span className="grayText" style={{ marginLeft: '4px', fontSize: '9pt' }}>
                            {instanceLabel}
                        </span>
                    )}
                    {showCheck && (
                        <span className="ecp-complete-indicator greenText" title="Complete">{'\u2713'}</span>
                    )}
                </div>
                {isExpanded && (
                    <div id={`eCPHierarchy_${hierarchyIndex}_contentDiv`} className="ecp-content" style={this.getContentStyle(data, 'Section')}>
                        {this.renderHierarchy(data, 'Section', level + 1, 0, stickyDepth + 1)}
                    </div>
                )}
            </div>
        );
    }

    private renderQuestion(data: any, hierarchyIndex: number, level: number, index: number, accumulatedLinkWidth: number, stickyDepth: number = 0): React.ReactNode {
        const isPlaceholder = data._hideChildrenUntilRepeat === true;
        const isExpanded = !isPlaceholder && data.isExpanded !== false;
        const isComplete = this.isElementComplete(data);
        const optional = isOptional(data);
        const isRequired = !optional;
        const hasOverride = data.overrideReason && data.overrideReason !== '';
        // Show the green ✓ when the Question is truly complete — either it has
        // no override at all, or it's overridden AND all required descendants
        // are actually answered. A Question that is ONLY complete because of
        // the override (no real answers) suppresses the ✓; the strike-through
        // red "Required" label communicates the overridden-but-empty state.
        const reallyAnswered = data._completeWithoutOverride === true;
        const showCheck = isComplete
            && !optional
            && (!hasOverride || reallyAnswered)
            && !isPlaceholder;
        const isDisabled = data._disabled === true
            || this.props.disabled === true
            || data._parentSelected === false;
        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const cssClass = `ecpQuestions ecp-sticky-header${isFocused ? ' hierarchyRowSelected' : ''}`;

        // No accumulated link-width offset on the header — visual nesting
        // comes from the parent's .ecp-content paddingLeft (matching the JSP).
        const indent = 0;

        const repeatButton = this.renderRepeatButton(data, 'Question');
        const hasToggle = !isPlaceholder;
        const instanceLabel = (!isPlaceholder && data._instanceNumber)
            ? ` (${data._instanceNumber})`
            : '';

        return (
            // IMPORTANT: wrapper must NOT have marginLeft — sticky headers must span the full
            // scroll-container width so their opaque background covers scrolling content on
            // the left side. Indent is applied as paddingLeft on the header and content div.
            <div key={`question-${hierarchyIndex}`} className="ecp-question-wrapper">
                <div
                    id={`eCPHierarchy_${hierarchyIndex}`}
                    className={cssClass}
                    onClick={() => { if (hasToggle) this.toggleExpand(data); }}
                    {...(this.props.enableKeyboardNavigation ? { tabIndex: 0 } : {})}
                    style={{
                        top: this.getStickyTop(stickyDepth) + 'px',
                        paddingLeft: (indent + 8) + 'px',
                        zIndex: this.getStickyZIndex(stickyDepth),
                    }}
                >
                    {/* Left group: links + repeat +/- + arrow + title + checkmark.
                        No fixed height — the row grows vertically when the title
                        label wraps to multiple lines. `alignItems: flex-start`
                        keeps the toggle triangle / links anchored to the first
                        line so they don't drift to the middle of wrapped text. */}
                    <div className="flex-container flex-grow" style={{ alignItems: 'flex-start', minWidth: 0 }}>
                        {this.renderDebugOrder(data)}
                        {this.renderLinks(data)}
                        {repeatButton}
                        {hasToggle ? (
                            <span
                                className={isExpanded
                                    ? 'ui-accordion-header-icon ui-icon ui-icon-triangle-1-s'
                                    : 'ui-accordion-header-icon ui-icon ui-icon-triangle-1-e'}
                                style={{ width: '16px', marginTop: '1px' }}
                            />
                        ) : (
                            <span style={{ display: 'inline-block', width: '16px' }} />
                        )}
                        {this.renderTitleLabel(data)}
                        {instanceLabel && (
                            <span className="grayText" style={{ marginLeft: '4px', fontSize: '9pt' }}>
                                {instanceLabel}
                            </span>
                        )}
                        {showCheck && (
                            <span className="ecp-complete-indicator greenText" title="Complete">{'\u2713'}</span>
                        )}
                    </div>
                    {/* Right group: Required label */}
                    {isRequired && !isPlaceholder && (
                        <div className="align-obj-right">
                            {isDisabled ? (
                                <span
                                    className="disabledText"
                                    style={{
                                        textDecoration: hasOverride ? 'line-through' : 'none',
                                        fontSize: '9pt',
                                        color: '#999',
                                        cursor: 'default',
                                        pointerEvents: 'none',
                                    }}
                                    title="Required (disabled)"
                                    aria-disabled={true}
                                >
                                    Required
                                </span>
                            ) : (
                                <a
                                    href="#"
                                    className={hasOverride ? 'redText' : 'blackText'}
                                    style={{
                                        textDecoration: hasOverride ? 'line-through' : 'none',
                                        fontSize: '9pt',
                                    }}
                                    title={hasOverride ? 'Required Question has been overridden' : 'Question Override'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.questionOverride(data, hasOverride);
                                    }}
                                >
                                    Required
                                </a>
                            )}
                        </div>
                    )}
                </div>
                {isExpanded && (
                    <div id={`eCPHierarchy_${hierarchyIndex}_contentDiv`} className="ecp-content" style={this.getContentStyle(data, 'Question')}>
                        {this.renderHierarchy(data, 'Question', level + 1, 0, stickyDepth + 1)}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Get the INITIAL sticky `top` offset for a header based on the number of
     * sticky ancestor headers. This is just a sensible starting value used on
     * first render — `updateStickyTops()` walks the actual DOM after mount and
     * overwrites it with the real cumulative pixel height of ancestor headers.
     * That way, Section/Question headers whose title wraps onto multiple lines
     * still stack flush against their parents instead of leaving a gap or
     * overlapping.
     *
     * stickyDepth counts only Section/Question sticky ancestors (NOT ShellItem
     * rows which are not sticky).
     */
    private getStickyTop(stickyDepth: number): number {
        const headerHeight = 22; // Matches CSS min-height on .ecp-sticky-header rows.
        return stickyDepth * headerHeight;
    }

    /**
     * Debounced scheduler that recomputes sticky top offsets on the next
     * animation frame. Called from componentDidMount / componentDidUpdate
     * and from the container ResizeObserver.
     */
    private scheduleStickyTopUpdate = () => {
        if (this._stickyUpdateRaf !== null) return;
        this._stickyUpdateRaf = requestAnimationFrame(() => {
            this._stickyUpdateRaf = null;
            this.updateStickyTops();
        });
    }

    /**
     * Walk every `.ecp-sticky-header` inside the scroll container and set its
     * inline `top` to the sum of the measured heights of its sticky-header
     * ancestors. This keeps the stacked-sticky-headers layout pixel-correct
     * even when a header's title wraps onto multiple lines (and therefore
     * grows taller than the 22px baseline assumed by `getStickyTop`).
     *
     * Also (re)installs a ResizeObserver so wrapping changes caused by
     * window/column resize trigger another pass.
     */
    private updateStickyTops() {
        const container = this.listRowRef.current;
        if (!container) return;

        const headers = container.querySelectorAll<HTMLElement>('.ecp-sticky-header');
        if (headers.length === 0) return;

        headers.forEach((header) => {
            let top = 0;
            // Walk up through ANCESTOR .ecp-section-wrapper /
            // .ecp-question-wrapper nodes. We start ABOVE the header's own
            // wrapper (header.parentElement.parentElement) because the
            // immediate parent wrapper's first child is this same header —
            // including it would make a header offset itself by its own
            // height and leave a gap at the top of the scroll container
            // where no sticky header covers the scrolling content.
            const ownWrapper = header.parentElement;
            let el: HTMLElement | null = ownWrapper ? ownWrapper.parentElement : null;
            while (el && el !== container) {
                if (
                    el.classList.contains('ecp-section-wrapper')
                    || el.classList.contains('ecp-question-wrapper')
                ) {
                    const parentHeader = el.firstElementChild as HTMLElement | null;
                    if (
                        parentHeader
                        && parentHeader !== header
                        && parentHeader.classList.contains('ecp-sticky-header')
                    ) {
                        top += parentHeader.offsetHeight;
                    }
                }
                el = el.parentElement;
            }
            const desired = top + 'px';
            if (header.style.top !== desired) {
                header.style.top = desired;
            }
        });

        // (Re)install a ResizeObserver so any future height change (e.g. the
        // window widens and a wrapped title becomes single-line again) reruns
        // this computation. We observe each current sticky header; the next
        // update recycles by disconnecting first.
        if (typeof ResizeObserver !== 'undefined') {
            if (this._stickyResizeObserver) {
                this._stickyResizeObserver.disconnect();
            } else {
                this._stickyResizeObserver = new ResizeObserver(() => {
                    this.scheduleStickyTopUpdate();
                });
            }
            headers.forEach((h) => this._stickyResizeObserver!.observe(h));
        }
    }

    /**
     * Z-index for sticky headers, driven by stickyDepth so ancestor headers
     * always paint above their descendant headers.
     *
     * Without this, a descendant Section (CSS z-index 20) would paint OVER an
     * ancestor Question (CSS z-index 15) during scroll transitions — making
     * Header3 visually cover Header2 as it approaches its sticky top offset.
     *
     * Higher stickyDepth (deeper nesting) = LOWER z-index, so the top-most
     * ancestor header is always in front.
     */
    private getStickyZIndex(stickyDepth: number): number {
        return 100 - stickyDepth;
    }


    /**
     * Toggle the "overridden" state of a Required Question when user clicks the Required label
     */
    /**
     * Show the Question Override dialog - matches old JSP's questionOverride() function.
     * Allows user to enter a reason to mark a required question as complete without answering.
     */
    private questionOverride(data: any, hasOverrideReason: boolean) {
        if (!data) return;

        const self = this;
        const $ = (window as any).jQuery || (window as any).$;
        if (!$) {
            // Fallback if jQuery not available
            if (hasOverrideReason) {
                data.overrideReason = '';
            } else {
                data.overrideReason = 'User override';
            }
            self.setState({ translatedJSON: self.state.translatedJSON, rowCount: -1 });
            return;
        }

        const dialogContent = $('<div>');
        dialogContent.append($('<p>').text('Required Question to remain unanswered and marked as completed.'));
        dialogContent.append($('<p>').append($('<b>').text(this.getTitle(data))));
        dialogContent.append($('<p>').text('Reason question will not be answered:'));
        const $textarea = $('<textarea>', {
            class: 'textShortcuts',
            style: 'width: 95%; height: 100px; padding: 5px;',
            placeholder: 'Enter reason...',
            text: hasOverrideReason ? data.overrideReason : ''
        });
        dialogContent.append($textarea);

        const $dialog = $('<div>').append(dialogContent);

        const refreshHierarchy = () => {
            self.setState({ translatedJSON: self.state.translatedJSON, rowCount: -1 });
            if (self.props.onSave) {
                self.props.onSave(self.state.translatedJSON);
            }
        };

        const dialogButtons: any[] = [
            {
                text: 'Mark question complete',
                click: function() {
                    data.overrideReason = $textarea.val();
                    $(this).dialog('close');
                    refreshHierarchy();
                }
            }
        ];

        if (hasOverrideReason) {
            dialogButtons.push({
                text: 'Remove reason and reinstate question',
                click: function() {
                    const dlg = this;
                    const CopiaAlert = (window as any).CopiaAlert;
                    if (typeof CopiaAlert === 'function') {
                        CopiaAlert(
                            "Question Override Reason will be lost and Question will be re-enabled and an answer required for the eCP Form",
                            "Confirm Removal",
                            () => {
                                data.overrideReason = '';
                                $(dlg).dialog('close');
                                refreshHierarchy();
                            }
                        );
                    } else {
                        if (confirm("Question Override Reason will be lost and Question will be re-enabled and an answer required for the eCP Form")) {
                            data.overrideReason = '';
                            $(dlg).dialog('close');
                            refreshHierarchy();
                        }
                    }
                }
            });
        } else {
            dialogButtons.push({
                text: 'Cancel',
                click: function() {
                    $(this).dialog('close');
                }
            });
        }

        $dialog.dialog({
            title: 'Question Override',
            modal: true,
            buttons: dialogButtons
        });
    }

    /**
     * Render Link elements (ECP external help links) as icons to the left of the arrow
     * Uses maxLinkCount to reserve fixed width so arrows align even when some siblings have different link counts
     */
    private renderLinks(data: any): React.ReactNode {
        const linkData = data?.Link;
        const maxLinkCount = data?.maxLinkCount || 0;

        // Calculate fixed width based on maxLinkCount (the max among all siblings)
        // This ensures all rows at this level reserve the same space, aligning arrows
        const reservedWidth = maxLinkCount * 18; // Each link = 18px

        // If no maxLinkCount, don't render anything (not even empty space)
        if (maxLinkCount === 0) return null;

        const links = linkData ? (Array.isArray(linkData) ? linkData : [linkData]) : [];
        // capIconPath from props, fallback to window.capIconPath (set by JSP)
        const capIconPath = this.props.capIconPath || (window as any).capIconPath || '';

        return (
            <span
                className="ecp-links-container"
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',  // RIGHT-ALIGN links within container
                    width: reservedWidth + 'px',  // FIXED WIDTH for alignment
                    minWidth: reservedWidth + 'px',
                    marginRight: '4px'
                }}
            >
                {links.map((link: any, idx: number) => {
                    if (!link) return null;
                    const url = this.buildECPLinkURL(link);
                    const linkText = (link.LinkText && link.LinkText.val) ? link.LinkText.val : (link.title || 'Link');
                    if (!url) return null;
                    return (
                        <img
                            key={`link-${idx}`}
                            src={capIconPath}
                            className="ecp-link-icon"
                            style={{ height: '16px', width: '16px', marginRight: '2px', cursor: 'pointer' }}
                            title={`CAP Explanatory Note(s) - ${linkText}\n${url}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (url.indexOf('https://Protocol.cap.org/notes/') === 0) {
                                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                                    if (newWindow) {
                                        newWindow.opener = null;
                                    }
                                } else {
                                    console.warn('[eCP link] blocked unexpected URL', url);
                                }
                            }}
                        />
                    );
                })}
            </span>
        );
    }

    /**
     * Build the URL for an ECP Link object
     */
    private buildECPLinkURL(link: any): string {
        if (!link) return '';
        // Check LinkURI property first (most common in ECP data)
        if (link.LinkURI && link.LinkURI.val) {
            return 'https://Protocol.cap.org/notes/' + link.LinkURI.val + '.html';
        }
        // Direct href/url property
        if (link.href) return link.href;
        if (link.url) return link.url;
        if (link.val) return link.val;
        // Build from baseURI + lineage + version (as per original JSP)
        if (link.baseURI && link.lineage) {
            const baseURI = link.baseURI;
            const lineage = link.lineage;
            const version = link.version || '';
            return `https://${baseURI}/cap/cgi/lookup?_lineage=${lineage}&_version=${version}`;
        }
        return '';
    }

    /**
     * Check if an element is complete. The `complete` flag is stamped onto every
     * Section / Question / ShellItem by updateCompleteStatus() before each render
     * (matching the legacy apeCPHierarchyList.jsp behavior), so we simply trust it.
     * A section/question only becomes complete once ALL of its required descendant
     * enterabilities are answered.
     */
    private isElementComplete(data: any): boolean {
        if (!data) return false;
        return data.complete === true || data.complete === 'true';
    }

    /**
     * Recursively check if any descendant has an answer (selected ShellItem or non-empty Response)
     */
    private hasAnswerInChildren(data: any): boolean {
        if (!data) return false;

        // Check ShellItem selection
        if (data.shellType && data.shellType !== 'DisplayedItem') {
            if (data.selected === true || data.selected === 'true') return true;
        }

        // Check Response values
        if (data.Response) {
            const r = data.Response;
            if (r.string && r.string.val && String(r.string.val).trim() !== '') return true;
            if (r.int && r.int.val && String(r.int.val).trim() !== '') return true;
            if (r.integer && r.integer.val && String(r.integer.val).trim() !== '') return true;
            if (r.decimal && r.decimal.val && String(r.decimal.val).trim() !== '') return true;
            if (r.date && r.date.val) return true;
            if (r.dateTime && r.dateTime.val) return true;
            if (r.boolean && (r.boolean.val === true || r.boolean.val === 'true')) return true;
        }

        // Recurse into ChildItems
        if (Array.isArray(data.ChildItems)) {
            for (const wrapper of data.ChildItems) {
                const child = getWrapperChild(wrapper);
                if (child && this.hasAnswerInChildren(child.value)) return true;
            }
        }

        // Recurse into List
        if (data.List && this.hasAnswerInChildren(data.List)) return true;

        return false;
    }

    private renderShellItem(data: any, hierarchyIndex: number, level: number, index: number, stickyDepth: number = 0): React.ReactNode {
        const shellType = data.shellType;
        if (shellType === 'DisplayedItem') {
            return this.renderDisplayedItem(data, hierarchyIndex, level);
        }

        // Fallback for ShellItems not in a ListField context (should be rare)
        const title = this.getTitle(data);
        const isSelected = data.selected === true || data.selected === 'true';
        const isDisabled = data._disabled === true || this.props.disabled === true;

        return (
            <div
                key={`shellitem-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className="flex-container-inline"
                style={{ marginTop: '0px', paddingLeft: '0px', fontWeight: 'normal', marginBottom: '3px', height: '100%' }}
            >
                {this.renderDebugOrder(data)}
                <label style={{ cursor: isDisabled ? 'default' : 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) => this.handleShellItemChange(data, e.target.checked, true, null)}
                        style={{ marginRight: '6px' }}
                    />
                    <span>{title}</span>
                </label>
            </div>
        );
    }

    private renderDisplayedItem(data: any, hierarchyIndex: number, level: number): React.ReactNode {
        const title = this.getTitle(data);
        return (
            <div
                key={`displayed-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className="ecp-displayed-item"
                style={{ paddingLeft: '16px' }}
            >
                {this.renderDebugOrder(data)}
                {title}
            </div>
        );
    }

    private renderResponseField(data: any, hierarchyIndex: number, level: number, index: number, key: string = 'ResponseField'): React.ReactNode {
        // Check what type of response this is
        if (data.Response) {
            if (data.Response.string) {
                return this.renderStringResponse(data, hierarchyIndex, level, key);
            } else if (data.Response.int || data.Response.integer || data.Response.decimal) {
                return this.renderNumericResponse(data, hierarchyIndex, level, key);
            } else if (data.Response.dateTime) {
                return this.renderDateTimeResponse(data, hierarchyIndex, level, 'dateTime', key);
            } else if (data.Response.date) {
                return this.renderDateTimeResponse(data, hierarchyIndex, level, 'date', key);
            } else if (data.Response.time) {
                return this.renderDateTimeResponse(data, hierarchyIndex, level, 'time', key);
            } else if (data.Response.HTML != null || data.Response.html != null) {
                return this.renderHtmlResponse(data, hierarchyIndex, level, key);
            } else if (data.Response.XML != null || data.Response.xml != null) {
                return this.renderXmlResponse(data, hierarchyIndex, level, key);
            }
        }

        // Fallback placeholder
        return (
            <div
                key={`response-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className="flex-container-inline"
                style={{ marginTop: '0px', paddingLeft: '0px', fontWeight: 'normal', marginBottom: '3px', height: '100%' }}
            >
                {this.renderDebugOrder(data)}
                <label className="grayText">Response Field (type: {data.Response ? Object.keys(data.Response)[0] : 'unknown'})</label>
            </div>
        );
    }

    private renderListField(data: any, hierarchyIndex: number, level: number, index: number, stickyDepth: number = 0, accumulatedLinkWidth: number = 0): React.ReactNode {
        // ListField contains a List with ShellItems (radio or checkbox options).
        // Visual indentation is provided by the parent's .ecp-content paddingLeft
        // (CSS nesting), so no additional accumulated indent is needed here.
        const listBaseIndent = data._insideShellItem
            ? 0
            : 0;

        // Check if there's a List inside
        if (data.List) {
            const list = data.List;
            const maxSelections = list.maxSelections || data.maxSelections || null;

            // Render the List's ChildItems (which are ShellItems)
            if (list.ChildItems && Array.isArray(list.ChildItems)) {

                return (
                    <div
                        key={`listfield-${hierarchyIndex}`}
                        id={`${hierarchyIndex}`}
                        className="ecp-list-field"
                        style={{ marginTop: '0px', paddingLeft: listBaseIndent + 'px', fontWeight: 'normal', marginBottom: '3px' }}
                    >
                        {list.ChildItems.map((wrapper: any, idx: number) => {
                            const child = getWrapperChild(wrapper);
                            if (!child || child.key !== 'ShellItem') return null;

                            const shellItem = child.value;
                            const itemHierarchyIndex = this.hierarchyFlatMap.length;

                            this.hierarchyFlatMap.push({
                                hierarchyIndex: itemHierarchyIndex,
                                obj: shellItem,
                                key: 'ShellItem',
                                level: level + 1,
                                parent: list,
                            });
                            this.hierarchyIndexSet.add(itemHierarchyIndex);

                            // Render as radio or checkbox based on maxSelections
                            return this.renderShellItemWithContext(
                                shellItem,
                                list,
                                maxSelections,
                                itemHierarchyIndex,
                                level + 1,
                                idx,
                                stickyDepth
                            );
                        })}
                    </div>
                );
            }
        }

        // Fallback if no List found
        return (
            <div
                key={`listfield-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                className="ecp-list-field"
                style={{ marginTop: '0px', paddingLeft: '0px', marginBottom: '3px' }}
            >
                {this.renderHierarchy(data, 'ListField', level)}
            </div>
        );
    }

    private renderShellItemWithContext(
        data: any,
        parentList: any,
        maxSelections: string | null,
        hierarchyIndex: number,
        level: number,
        index: number,
        stickyDepth: number = 0
    ): React.ReactNode {
        const shellType = data.shellType;

        if (shellType === 'DisplayedItem') {
            return this.renderDisplayedItem(data, hierarchyIndex, level);
        }

        const title = this.getTitle(data);
        const isSelected = data.selected === true || data.selected === 'true';
        const isDisabled = data._disabled === true || this.props.disabled === true;

        // Use checkboxes if maxSelections is not null and not "1"
        const useCheckbox = maxSelections != null && maxSelections !== "1";
        const inputType = useCheckbox ? 'checkbox' : 'radio';

        let inputName = useCheckbox ? undefined : `radio_${parentList.name || parentList.order || hierarchyIndex}`;
        inputName = inputName + hierarchyIndex + "_" + level + "_" + index;

        // Check if this ShellItem has child elements (ResponseField, Question, etc.)
        const hasChildren = data.ChildItems && Array.isArray(data.ChildItems) && data.ChildItems.length > 0;

        // Focus highlight is 100% state-driven. The container `focusin` handler
        // keeps `focusedHierarchyIndex` in sync with whichever row actually has
        // DOM focus, so exactly one element in the whole tree carries the
        // `ecp-focused-*` class at any time — avoiding the stacked/duplicated
        // boxes that :focus-within produced when the inner String row AND the
        // outer ShellItem label both temporarily matched during re-renders.
        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const labelClass = 'ecp-option-label' + (isFocused ? ' ecp-focused-label' : '');

        return (
            <div
                key={`shellitem-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className="ecp-response-row"
                // No inline paddingLeft — horizontal indent comes from the
                // surrounding `.ecp-list-field` container (renderListField)
                // which already positions the group to align with sibling
                // Question / Section headers. The row keeps the 8px of left
                // padding supplied by the `.ecp-response-row` CSS rule so the
                // radio input ends up exactly on the sibling header's left edge.
                style={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
                {this.renderDebugOrder(data)}
                <label className={labelClass} style={{ cursor: isDisabled ? 'default' : 'pointer' }}>
                    <input
                        type={inputType}
                        name={inputName}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) => this.handleShellItemChange(data, e.target.checked, useCheckbox, parentList)}
                    />
                    <span>{title}</span>
                </label>

                {/* Render child elements (e.g., specify text, sub-questions) - disabled if parent not selected */}
                {hasChildren && (
                    <div className={`ecp-shellitem-children ${!isSelected ? 'ecp-child-disabled' : ''}`}>
                        {this.renderShellItemChildren(data, level + 1, !isSelected, 0, stickyDepth)}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Render children of a ShellItem - typically ResponseField (specify text) or sub-Questions
     * The disableChildren flag visually disables them when parent is not selected
     * accumulatedLinkWidth defaults to 0 since ShellItem children are typically leaf nodes
     */
    private renderShellItemChildren(parentShellItem: any, level: number, disableChildren: boolean, accumulatedLinkWidth: number = 0, stickyDepth: number = 0): React.ReactNode {
        if (!parentShellItem.ChildItems || !Array.isArray(parentShellItem.ChildItems)) return null;

        const elements: React.ReactNode[] = [];
        parentShellItem.ChildItems.forEach((wrapper: any, idx: number) => {
            const child = getWrapperChild(wrapper);
            if (!child) return;

            // Mark child as disabled based on parent selection (used by render methods)
            if (disableChildren) {
                child.value._parentSelected = false;
            } else {
                child.value._parentSelected = true;
            }

            // Marker so Section/Question renderers skip their level-based
            // marginLeft. The surrounding `.ecp-shellitem-children` container
            // already supplies the visual indent, and the sibling numeric /
            // ResponseField has no level-based indent — so without this flag
            // a nested Question/Section would appear visually pushed further
            // right than its sibling inputs.
            child.value._insideShellItem = true;

            const hierarchyIndex = this.hierarchyFlatMap.length;
            this.hierarchyFlatMap.push({
                hierarchyIndex,
                obj: child.value,
                key: child.key,
                level,
            });
            this.hierarchyIndexSet.add(hierarchyIndex);

            // Respect Hide Optional — optional sub-Questions/Sections nested
            // inside a ShellItem's ChildItems must be hidden the same way the
            // main renderHierarchy path hides them. Without this check, an
            // optional Question living under a ListItem (e.g. a decimal
            // dimension with an "Additional Dimension" optional follow-up)
            // keeps rendering even after the user clicks Hide Optional.
            // Note: we still push the entry into the flat map above so that
            // hierarchy indexing / focus-tracking stays consistent.
            if (this.state.bHideOptional && shouldCollapseItem(child.value, child.key)) {
                return;
            }

            const content = this.renderElementContent(child.value, child.key, hierarchyIndex, level, idx, accumulatedLinkWidth, stickyDepth);
            if (content) elements.push(content);
        });

        return elements.length > 0 ? <>{elements}</> : null;
    }

    /**
     * Parses a maxLength value from the JSON data which may be a number or a
     * numeric string (e.g. "4000"). Returns undefined if not present/invalid.
     */
    private parseMaxLength(...candidates: any[]): number | undefined {
        for (const c of candidates) {
            if (c == null) { continue; }
            const n = typeof c === 'number' ? c : parseInt(String(c), 10);
            if (!isNaN(n) && n > 0) {
                return n;
            }
        }
        return undefined;
    }

    private renderStringResponse(data: any, hierarchyIndex: number, level: number, key: string = 'ResponseField'): React.ReactNode {
        const value = data.Response?.string?.val || '';
        const isParentUnselected = data._parentSelected === false;
        const isDisabled = data._disabled === true || this.props.disabled === true || isParentUnselected;

        // ListItemResponseField: only required if responseRequired="true" is explicitly set
        // ResponseField: uses existing logic (required by default unless optional)
        const isRequired = key === 'ListItemResponseField'
            ? !isDisabled && (data.responseRequired === true || data.responseRequired === 'true')
            : !isDisabled && (data.responseRequired === true || data.responseRequired === 'true') || (!isParentUnselected && data._hasOptionalParent !== true);

        const isEmpty = !value || value.trim() === '';
        const isInvalid = isRequired && isEmpty && !isDisabled;
        const maxLength = this.parseMaxLength(
            data.Response?.string?.maxLength,
            data.Response?.maxLength,
            data.maxLength,
        );

        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const rowClass = 'ecp-response-row' + (isFocused ? ' ecp-focused-row' : '');

        return (
            <div
                key={`string-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className={rowClass}
            >
                {this.renderDebugOrder(data)}
                <ReactAutoExpandTextArea
                    id={`textarea_${hierarchyIndex}`}
                    text={value}
                    css={`textShortcuts${isInvalid ? ' invalidInput' : ''}`}
                    style={{ width: '700px', maxWidth: '700px', minHeight: '25px' }}
                    disabled={isDisabled}
                    maxLength={maxLength}
                    placeholder={isRequired && !isDisabled ? 'Required' : ''}
                    row={data}
                    rowIndex={hierarchyIndex}
                    onChange={(rowObj, newText, rowIndex) => this.handleStringResponseChange(data, newText)}
                    onBlur={(rowObj, text, rowIndex) => {
                        // Trigger save on blur
                        if (this.props.onSave) {
                            this.props.onSave(this.state.translatedJSON);
                        }
                    }}
                />
            </div>
        );
    }

    // JRC 05/13/26 CO-16108 Render HTML
    private renderHtmlResponse(data: any, hierarchyIndex: number, level: number, key: string = 'ResponseField'): React.ReactNode {
        const raw = data.Response?.HTML != null ? data.Response.HTML : data.Response?.html;
        const markup = extractTypedMarkup(raw);
        const isParentUnselected = data._parentSelected === false;
        const isDisabled = data._disabled === true || this.props.disabled === true || isParentUnselected;

        // ListItemResponseField: only required if responseRequired="true" is explicitly set
        // ResponseField: uses existing logic (required by default unless optional)
        const isRequired = key === 'ListItemResponseField'
            ? !isDisabled && (data.responseRequired === true || data.responseRequired === 'true')
            : !isDisabled && (data.responseRequired === true || data.responseRequired === 'true') || (!isParentUnselected && data._hasOptionalParent !== true);

        const isEmpty = !markup || markup.trim() === '';
        const isInvalid = isRequired && isEmpty && !isDisabled;

        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const rowClass = 'ecp-response-row' + (isFocused ? ' ecp-focused-row' : '');

        // Read-only render path: show the rendered HTML.
        if (isDisabled) {
            return (
                <div
                    key={`html-${hierarchyIndex}`}
                    id={`${hierarchyIndex}`}
                    data-ecp-row-index={hierarchyIndex}
                    className={rowClass}
                >
                    {this.renderDebugOrder(data)}
                    <div
                        className={'ecp-html-readonly' + (isInvalid ? ' invalidInput' : '')}
                        style={{
                            border: '1px solid #ccc',
                            padding: '6px 8px',
                            width: '700px',
                            maxWidth: '700px',
                            minHeight: '25px',
                            background: '#fafafa',
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeSdcHtml(markup) }}
                    />
                </div>
            );
        }

        // Editable: textarea of source markup.
        return (
            <div
                key={`html-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className={rowClass}
            >
                {this.renderDebugOrder(data)}
                <textarea
                    id={`html_${hierarchyIndex}`}
                    className={'textShortcuts' + (isInvalid ? ' invalidInput' : '')}
                    style={{
                        width: '700px',
                        maxWidth: '700px',
                        minHeight: '60px',
                        fontFamily: 'Consolas, Menlo, monospace',
                        fontSize: '9pt',
                    }}
                    spellCheck={false}
                    placeholder={isRequired ? 'Required (XHTML markup)' : 'XHTML markup'}
                    value={markup}
                    onChange={(e) => this.handleHtmlResponseChange(data, e.target.value)}
                    onBlur={() => {
                        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
                    }}
                />
            </div>
        );
    }

    // JRC 05/13/26 CO-16108 Render XML
    private renderXmlResponse(data: any, hierarchyIndex: number, level: number, key: string = 'ResponseField'): React.ReactNode {
        const raw = data.Response?.XML != null ? data.Response.XML : data.Response?.xml;
        const markup = extractTypedMarkup(raw);
        const isParentUnselected = data._parentSelected === false;
        const isDisabled = data._disabled === true || this.props.disabled === true || isParentUnselected;

        // ListItemResponseField: only required if responseRequired="true" is explicitly set
        // ResponseField: uses existing logic (required by default unless optional)
        const isRequired = key === 'ListItemResponseField'
            ? !isDisabled && (data.responseRequired === true || data.responseRequired === 'true')
            : !isDisabled && (data.responseRequired === true || data.responseRequired === 'true') || (!isParentUnselected && data._hasOptionalParent !== true);

        const isEmpty = !markup || markup.trim() === '';
        const isInvalid = isRequired && isEmpty && !isDisabled;

        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const rowClass = 'ecp-response-row' + (isFocused ? ' ecp-focused-row' : '');

        return (
            <div
                key={`xml-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className={rowClass}
            >
                {this.renderDebugOrder(data)}
                <textarea
                    id={`xml_${hierarchyIndex}`}
                    className={'textShortcuts' + (isInvalid ? ' invalidInput' : '')}
                    style={{
                        width: '700px',
                        maxWidth: '700px',
                        minHeight: '60px',
                        fontFamily: 'Consolas, Menlo, monospace',
                        fontSize: '9pt',
                    }}
                    spellCheck={false}
                    readOnly={isDisabled}
                    disabled={isDisabled}
                    placeholder={isRequired && !isDisabled ? 'Required (XML)' : 'XML'}
                    value={markup}
                    onChange={(e) => this.handleXmlResponseChange(data, e.target.value)}
                    onBlur={() => {
                        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
                    }}
                />
            </div>
        );
    }

    private renderNumericResponse(data: any, hierarchyIndex: number, level: number, key: string = 'ResponseField'): React.ReactNode {
        // Determine the response object and whether it's integer or decimal.
        // The JSP legacy supports int, integer and decimal — same precedence.
        const isDecimal = data.Response?.decimal != null;
        const isInteger = data.Response?.int != null || data.Response?.integer != null;
        const response = data.Response?.int || data.Response?.integer || data.Response?.decimal;
        const value = response?.val != null ? String(response.val) : '';

        const isParentUnselected = data._parentSelected === false;
        const isDisabled = data._disabled === true || this.props.disabled === true || isParentUnselected;

        // ListItemResponseField: only required if responseRequired="true" is explicitly set
        // ResponseField: uses existing logic (required by default unless optional)
        const isRequired = key === 'ListItemResponseField'
            ? !isDisabled && (data.responseRequired === true || data.responseRequired === 'true')
            : !isDisabled && (data.responseRequired === true || data.responseRequired === 'true') || (!isParentUnselected && data._hasOptionalParent !== true);

        // Parse min/max ranges. minInclusive / maxInclusive come from the JSON.
        const min = response && response.minInclusive != null && response.minInclusive !== ''
            ? Number(response.minInclusive) : null;
        const max = response && response.maxInclusive != null && response.maxInclusive !== ''
            ? Number(response.maxInclusive) : null;

        const rangeCmp = ECPHierarchyList.isInputInRange(value, min, max);
        const isOutOfRange = rangeCmp !== 0 && value !== '';
        const isEmptyRequired = isRequired && (value === '' || value == null);
        const isInvalid = isOutOfRange || isEmptyRequired;

        // Tooltip matches the legacy format ("Int range 0 - 100" / "Decimal range 0.0 - no max")
        const rangeLabel = isDecimal ? 'Decimal' : 'Integer';
        const title = `${rangeLabel} range ${min !== null ? min : 'no min'} - ${max !== null ? max : 'no max'}`;

        // Text displayed after the input — units / TextAfterResponse
        const textAfter = (data.TextAfterResponse && data.TextAfterResponse.val) ? String(data.TextAfterResponse.val) : '';
        const units = (data.ResponseUnits && data.ResponseUnits.val) ? String(data.ResponseUnits.val) : '';
        const showUnits = units !== '' && units !== textAfter;

        // Build the out-of-range error message
        let rangeMsg = '';
        if (isOutOfRange) {
            if (min !== null && max !== null) rangeMsg = `Out of range (${min} - ${max})`;
            else if (min !== null) rangeMsg = `Out of range (≥ ${min})`;
            else if (max !== null) rangeMsg = `Out of range (≤ ${max})`;
            else rangeMsg = 'Out of range';
        }

        const inputClass = isInvalid ? 'invalidInput' : '';

        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const rowClass = 'flex-container-inline ecp-response-row' + (isFocused ? ' ecp-focused-row' : '');

        return (
            <div
                key={`numeric-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className={rowClass}
                style={{ marginTop: '0px', paddingLeft: '0px', fontWeight: 'normal', marginBottom: '3px', height: '100%' }}
            >
                {this.renderDebugOrder(data)}
                <input
                    type="text"
                    inputMode={isDecimal ? 'decimal' : 'numeric'}
                    value={value}
                    disabled={isDisabled}
                    className={inputClass}
                    title={title}
                    placeholder={isRequired ? 'Required' : ''}
                    onKeyDown={(e) => {
                        if (isDecimal) {
                            ECPHierarchyList.keyDownRestrictToDecimals(e, value);
                        } else if (isInteger) {
                            ECPHierarchyList.keyDownRestrictToInts(e, value);
                        }
                    }}
                    onChange={(e) => this.handleNumericResponseChange(data, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '100px' }}
                />
                {textAfter !== '' && (
                    <span style={{ marginLeft: '5px' }}>{textAfter}</span>
                )}
                {showUnits && (
                    <span style={{ marginLeft: '5px' }}>{units}</span>
                )}
                {isOutOfRange && (
                    <label className="redText" style={{ paddingLeft: '5px' }}>{rangeMsg}</label>
                )}
            </div>
        );
    }

    /**
     * Returns true when `el` is a "textual" form field — one where the user
     * types/edits free-form text and pressing Up/Down would normally move the
     * internal caret or spin a number value instead of navigating rows. Covers:
     *   - <textarea>
     *   - <input type="text" | "number" | "search" | "email" | "tel" | "url" | "password">
     *   - <input> with no type (defaults to text)
     *   - contentEditable elements
     * Radio/checkbox/button inputs are NOT textual — those are "selectable".
     */
    private static isTextualElement(el: HTMLElement): boolean {
        if (!el) return false;
        if ((el as any).isContentEditable === true) return true;
        const tag = el.tagName.toLowerCase();
        if (tag === 'textarea') return true;
        if (tag === 'input') {
            const t = ((el as HTMLInputElement).type || '').toLowerCase();
            const textual = ['', 'text', 'number', 'search', 'email', 'tel', 'url', 'password'];
            return textual.indexOf(t) !== -1;
        }
        return false;
    }

    /**
     * Returns:
     *   0  — value is within [min, max] (or empty / non-numeric sentinel)
     *  -1  ��� value < min
     *   1  — value > max
     * Matches the legacy isInputInRange() behavior from apeCPHierarchyList.jsp.
     */
    private static isInputInRange(value: any, min: number | null, max: number | null): number {
        if (value === null || value === undefined || value === '') return 0;
        const n = Number(value);
        if (isNaN(n)) return 0; // non-numeric text is filtered separately
        if (min !== null && n < min) return -1;
        if (max !== null && n > max) return 1;
        return 0;
    }

    /**
     * Prevent keystrokes that would produce a non-integer. Allows navigation /
     * editing keys, digits, and a leading '-' (only when min allows negatives).
     */
    private static keyDownRestrictToInts(e: React.KeyboardEvent<HTMLInputElement>, currentValue: string): void {
        const key = e.key;
        // Always allow editing/navigation keys
        const allowedControl = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End',
        ];
        if (allowedControl.indexOf(key) !== -1) return;
        // Allow common shortcuts (copy/paste/cut/select-all/undo/redo)
        if ((e.ctrlKey || e.metaKey) && key.length === 1) return;
        // Allow digits
        if (/^[0-9]$/.test(key)) return;
        // Allow a single leading '-'
        if (key === '-') {
            const input = e.target as HTMLInputElement;
            const caret = input.selectionStart || 0;
            if (caret === 0 && (currentValue || '').indexOf('-') === -1) return;
        }
        e.preventDefault();
    }

    /**
     * Prevent keystrokes that would produce a non-decimal number. Allows a
     * single '.' in addition to the integer rules.
     */
    private static keyDownRestrictToDecimals(e: React.KeyboardEvent<HTMLInputElement>, currentValue: string): void {
        const key = e.key;
        const allowedControl = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End',
        ];
        if (allowedControl.indexOf(key) !== -1) return;
        if ((e.ctrlKey || e.metaKey) && key.length === 1) return;
        if (/^[0-9]$/.test(key)) return;
        if (key === '.') {
            // Only allow one decimal point
            if ((currentValue || '').indexOf('.') === -1) return;
        }
        if (key === '-') {
            const input = e.target as HTMLInputElement;
            const caret = input.selectionStart || 0;
            if (caret === 0 && (currentValue || '').indexOf('-') === -1) return;
        }
        e.preventDefault();
    }

    // JRC 05/13/26 CO-16108 Render a date / dateTime / time
    private renderDateTimeResponse(
        data: any,
        hierarchyIndex: number,
        level: number,
        kind: DateTimeKind,
        key: string = 'ResponseField',
    ): React.ReactNode {
        const responseObj = data.Response[kind];
        const value = responseObj && responseObj.val != null ? String(responseObj.val) : '';

        const isParentUnselected = data._parentSelected === false;
        const isDisabled = data._disabled === true || this.props.disabled === true || isParentUnselected;

        // ListItemResponseField: only required if responseRequired="true" is explicitly set
        // ResponseField: uses existing logic (required by default unless optional)
        const isRequired = key === 'ListItemResponseField'
            ? !isDisabled && (data.responseRequired === true || data.responseRequired === 'true')
            : !isDisabled && (data.responseRequired === true || data.responseRequired === 'true') || (!isParentUnselected && data._hasOptionalParent !== true);

        const min = responseObj && responseObj.minInclusive != null && responseObj.minInclusive !== ''
            ? String(responseObj.minInclusive) : undefined;
        const max = responseObj && responseObj.maxInclusive != null && responseObj.maxInclusive !== ''
            ? String(responseObj.maxInclusive) : undefined;

        const textAfter = (data.TextAfterResponse && data.TextAfterResponse.val)
            ? String(data.TextAfterResponse.val) : '';
        const units = (data.ResponseUnits && data.ResponseUnits.val)
            ? String(data.ResponseUnits.val) : '';
        const showUnits = units !== '' && units !== textAfter;

        const tooltipKindLabel = kind === 'date' ? 'Date'
            : kind === 'time' ? 'Time'
            : 'Date / Time';
        const title = `${tooltipKindLabel}${min ? ' min ' + min : ''}${max ? ' max ' + max : ''}`;

        const isFocused = this.state.focusedHierarchyIndex === hierarchyIndex;
        const rowClass = 'flex-container-inline ecp-response-row'
            + (isFocused ? ' ecp-focused-row' : '');

        return (
            <div
                key={`datetime-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                data-ecp-row-index={hierarchyIndex}
                className={rowClass}
                style={{ marginTop: '0px', paddingLeft: '0px', fontWeight: 'normal', marginBottom: '3px', height: '100%' }}
            >
                {this.renderDebugOrder(data)}
                <ReactDateTimeInput
                    id={`datetime_${hierarchyIndex}`}
                    kind={kind}
                    value={value}
                    disabled={isDisabled}
                    required={isRequired}
                    min={min}
                    max={max}
                    title={title}
                    showClearButton={this.props.showDateTimeClearButton}
                    militaryTime={this.props.militaryTime}
                    allowMilitaryTimeEntry={this.props.allowMilitaryTimeEntry}
                    onChange={(newVal: string) => this.handleDateTimeResponseChange(data, kind, newVal)}
                    onBlur={() => {
                        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
                    }}
                />
                {textAfter !== '' && (
                    <span style={{ marginLeft: '5px' }}>{textAfter}</span>
                )}
                {showUnits && (
                    <span style={{ marginLeft: '5px' }}>{units}</span>
                )}
            </div>
        );
    }

    private handleDateTimeResponseChange(data: any, kind: DateTimeKind, value: string) {
        if (!data.Response || !data.Response[kind]) return;
        data.Response[kind].val = value;
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        });
        if (this.props.onSave) {
            this.props.onSave(this.state.translatedJSON);
        }
    }

    private renderFooter(data: any, hierarchyIndex: number, level: number, index: number): React.ReactNode {
        const text = data.Property?.val || '';
        if (!text || text === '{no text}') return null;

        return (
            <div
                key={`footer-${hierarchyIndex}`}
                id={`${hierarchyIndex}`}
                className="ecp-footer"
            >
                {this.renderDebugOrder(data)}
                {text}
            </div>
        );
    }

    private getTitle(obj: any): string {
        // Matches legacy getECPTitle() in apeCPHierarchyList.jsp:
        //   - prefer `title`
        //   - else use `Property.val` (unless it's the sentinel "{no text}")
        //   - else return "" (blank header)
        // We intentionally do NOT fall back to `obj.name` — `name` holds the
        // internal XML identifier (e.g. "S_22370", "Q_22371") which is not
        // intended to be shown to the user. A Section authored without a
        // title must render as a blank header.
        if (obj.title) {
            return String(obj.title).replace(/^[+?]\s*/, '');
        }
        if (obj.Property?.val && obj.Property.val !== '{no text}') {
            return obj.Property.val;
        }
        return '';
    }

    /**
     * When the `eCPFormDebug` prop is true, render a small red label showing
     * the raw `order` value from the XML for this row. Used to help form
     * authors correlate on-screen rows with their source XML. Returns null
     * when debug mode is off or the object has no order.
     */
    private renderDebugOrder(data: any): React.ReactNode {
        if (!this.props.eCPFormDebug) return null;
        if (!data || data.order == null) return null;
        return (
            <span
                key="debug-order"
                className="redText"
                style={{ marginRight: '6px', fontSize: '9pt', fontWeight: 'normal' }}
                title="eCPFormDebug: order"
            >
                {data.order}
            </span>
        );
    }

    /**
     * Render the header title for a Section/Question. When the item is
     * optional (mustImplement="false"), the title is shown in gray — matching
     * the legacy apeCPHierarchyList.jsp behavior. When it also has minCard=0,
     * a Font Awesome info badge is appended (legacy getECPTitleWithIndicator).
     */
    private renderTitleLabel(data: any): React.ReactNode {
        const title = this.getTitle(data);
        const optional = isOptional(data);

        // JRC 05/13/26 CO-16108 Display HTML Strings
        const titleHtmlProp = findPropertyByName(data, 'titleHTML');
        const titleHtmlMarkup = titleHtmlProp ? extractPropertyHtml(titleHtmlProp) : '';
        const hasTitleHtml = titleHtmlMarkup.trim() !== '';

        const labelInner: React.ReactNode = hasTitleHtml
            ? (
                <span
                    className="ecp-titlehtml"
                    style={{ fontWeight: 'normal' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSdcHtml(titleHtmlMarkup) }}
                />
            )
            : title;

        if (optional) {
            return (
                <label className="grayText">
                    <span className="ecp-optional-field-container">
                        <span className="ecp-optional-title">{labelInner}</span>
                        <i
                            className="fas fa-circle-info ecp-optional-badge"
                            title="Optional field - not required for completion"
                            aria-label="Optional"
                        />
                    </span>
                </label>
            );
        }
        if (optional) {
            return <label className="grayText">{labelInner}</label>;
        }
        return <label>{labelInner}</label>;
    }

    private toggleExpand(obj: any) {
        // FIX: isExpanded may be undefined initially. The UI treats undefined as "expanded"
        // (via `data.isExpanded !== false`), so a naive `!obj.isExpanded` would set it to
        // `true` on the first click, producing no visual change and requiring a second click.
        // Instead, derive the current visible state the same way the UI does, then invert it.
        const isCurrentlyExpanded = obj.isExpanded !== false;
        obj.isExpanded = !isCurrentlyExpanded;
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        });
    }

    private handleShellItemChange(data: any, checked: boolean, isCheckbox: boolean, parentList: any) {
        // Resolve the list object that holds the siblings
        const listObj = (parentList && parentList.List) ? parentList.List : parentList;

        if (isCheckbox) {
            // Checkbox logic
            data.selected = checked;

            if (checked && listObj) {
                const siblings = getShellItemsFromList(listObj);

                // selectionDeselectsSiblings: if THIS item has the flag, uncheck all others
                if (data.selectionDeselectsSiblings === true || data.selectionDeselectsSiblings === 'true') {
                    siblings.forEach((sibling: any) => {
                        if (sibling !== data) {
                            sibling.selected = false;
                        }
                    });
                } else {
                    // If any OTHER sibling has selectionDeselectsSiblings and is selected,
                    // uncheck that sibling (because we just checked a normal item)
                    siblings.forEach((sibling: any) => {
                        if (sibling !== data &&
                            (sibling.selectionDeselectsSiblings === true || sibling.selectionDeselectsSiblings === 'true')) {
                            sibling.selected = false;
                        }
                    });
                }

                // Check maxSelections limit
                if (listObj.maxSelections) {
                    const maxSel = parseInt(listObj.maxSelections);
                    if (!isNaN(maxSel) && maxSel > 0) {
                        const selectedCount = siblings.filter((item: any) =>
                            item.selected === true || item.selected === 'true').length;
                        if (selectedCount > maxSel) {
                            data.selected = false;
                            console.warn('[ECP] Max selections exceeded:', maxSel);
                        }
                    }
                }
            }
        } else {
            // Radio logic - unselect all others in the group, select this one
            if (checked && listObj) {
                const shellItems = getShellItemsFromList(listObj);
                shellItems.forEach((item: any) => {
                    item.selected = false;
                });
                data.selected = true;
            }
        }

        const nowSelected = data.selected === true || data.selected === 'true';

        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        }, () => {
            // If the item is now selected and has a child enterable (e.g. a
            // "specify" textbox that appears beneath a radio/checkbox), move
            // focus there so the user can continue answering without having
            // to click again. This matches the keyboard-activation behavior
            // and the legacy JSP flow.
            if (!nowSelected) return;
            const rowIndex = this.findHierarchyIndexForObject(data);
            if (rowIndex < 0) return;
            requestAnimationFrame(() => {
                const childEl = this.findFirstEnterableInShellItemChildren(rowIndex);
                if (!childEl) return;
                childEl.focus({ preventScroll: false });
                const tag = childEl.tagName.toLowerCase();
                if (tag === 'textarea' || tag === 'input') {
                    try {
                        const inp = childEl as HTMLInputElement;
                        const len = (inp.value || '').length;
                        inp.setSelectionRange(len, len);
                    } catch (e) { /* some input types don't support selection */ }
                }
            });
        });

        // Notify parent
        if (this.props.onSave) {
            this.props.onSave(this.state.translatedJSON);
        }
    }

    private handleStringResponseChange(data: any, value: string) {
        if (data.Response && data.Response.string) {
            data.Response.string.val = value;
            this.setState({
                translatedJSON: this.state.translatedJSON,
                rowCount: -1,
            });

            // Notify parent
            if (this.props.onSave) {
                this.props.onSave(this.state.translatedJSON);
            }
        }
    }

    // JRC 05/13/26 CO-16108 Handle HTML
    private handleHtmlResponseChange(data: any, value: string) {
        if (!data.Response) return;
        // Preserve whichever case the upstream used.
        if (data.Response.HTML != null || data.Response.html == null) {
            data.Response.HTML = value;
        } else {
            data.Response.html = value;
        }
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        });
        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
    }

    // JRC 05/13/26 CO-16108 Handle XML
    private handleXmlResponseChange(data: any, value: string) {
        if (!data.Response) return;
        if (data.Response.XML != null || data.Response.xml == null) {
            data.Response.XML = value;
        } else {
            data.Response.xml = value;
        }
        this.setState({
            translatedJSON: this.state.translatedJSON,
            rowCount: -1,
        });
        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
    }

    private handleNumericResponseChange(data: any, value: string) {
        const response = data.Response?.int || data.Response?.integer || data.Response?.decimal;
        if (response) {
            response.val = value;
            this.setState({
                translatedJSON: this.state.translatedJSON,
                rowCount: -1,
            });

            // Notify parent
            if (this.props.onSave) {
                this.props.onSave(this.state.translatedJSON);
            }
        }
    }

    // ───────────────────────────────────────��─────────────────────────────────
    // ReactJSONHierarchy Callback Methods (matching original JSP)
    // ──────────────────────────────────��─────────────��───────────────────��────

    // ─────────────────────────────────────────────────────────────────────────
    // Repeat Section/Question support (minCard / maxCard)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Scan the loaded JSON and (1) detect existing repeat instances (saved
     * data from the backend), and (2) pre-populate required minCard instances
     * and a trailing "+" placeholder so the user can add more. Matches the
     * behavior of initRepeatTrackersFromExistingData + prePopulateMinCardInstances
     * in apeCPHierarchyList.jsp.
     */
    private initRepeatSections(root: any): void {
        this.repeatInstanceTracker = {};
        this.detectExistingRepeats(root);
        this.prePopulateMinCard(root);
    }

    /**
     * Quick scan of the tree: returns true if any Section/Question carries a
     * maxCard that makes it repeatable. Used by render() to decide whether
     * lazy repeat-section init needs to run (skips walking unused parts).
     */
    private hasAnyRepeatable(node: any): boolean {
        if (!node || typeof node !== 'object') return false;
        if (Array.isArray(node)) {
            for (const item of node) if (this.hasAnyRepeatable(item)) return true;
            return false;
        }
        const wrap = getWrapperChild(node);
        if (wrap && isRepeatable(wrap.value, wrap.key)) return true;
        if (Array.isArray(node.ChildItems)) {
            for (const w of node.ChildItems) {
                const c = getWrapperChild(w);
                if (c && isRepeatable(c.value, c.key)) return true;
                if (c && c.value && this.hasAnyRepeatable(c.value)) return true;
            }
        }
        for (const k in node) {
            if (!node.hasOwnProperty(k)) continue;
            if (k === 'ChildItems') continue;
            const v = node[k];
            if (v && typeof v === 'object' && this.hasAnyRepeatable(v)) return true;
        }
        return false;
    }

    /** Group siblings by sGuid/name and mark multi-instance groups as repeat instances. */
    private detectExistingRepeats(node: any): void {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) {
            node.forEach((item: any) => this.detectExistingRepeats(item));
            return;
        }

        if (Array.isArray(node.ChildItems)) {
            const groups: { [key: string]: any[] } = {};

            node.ChildItems.forEach((wrapper: any) => {
                const wrapped = getWrapperChild(wrapper);
                if (!wrapped) return;
                const { key, value } = wrapped;

                if (!isRepeatable(value, key)) return;

                const groupKey = value.sGuid || value.name;
                if (!groupKey) return;

                if (!groups[groupKey]) groups[groupKey] = [];
                groups[groupKey].push(value);
            });

            for (const gk in groups) {
                const group = groups[gk];
                const firstObj = group[0];
                const minCard = firstObj.minCard != null ? parseInt(firstObj.minCard, 10) || 0 : 1;
                const originalOrder = firstObj.order;

                // Separate instances with data from empty ones
                // Also treat user-added instances (with _isRepeatInstance flag) as instances with data
                // to preserve them on re-render, even if they don't have form data yet
                const instancesWithData: any[] = [];
                const emptyInstances: any[] = [];

                group.forEach((g: any) => {
                    if (hasUserEnteredData(g) || g._isRepeatInstance === true) {
                        instancesWithData.push(g);
                    } else {
                        emptyInstances.push(g);
                    }
                });

                // Always create tracker if there are ANY instances (including placeholders)
                // This prevents duplicate placeholders on re-render
                if (group.length > 0) {
                    this.repeatInstanceTracker[originalOrder] = {
                        count: instancesWithData.length,  // Only count instances with data
                        instances: instancesWithData.map((g: any) => g.order),
                        nextInstanceNumber: instancesWithData.length + 1,
                        source: JSON.parse(JSON.stringify(firstObj))  // Store pristine template
                    };

                    if (instancesWithData.length > 0) {
                        instancesWithData.forEach((g: any, idx: number) => {
                            g._isRepeatInstance = true;
                            g._isTopLevelRepeat = true;
                            g._originalOrder = originalOrder;
                            g._instanceNumber = idx + 1;
                            g._isRequiredMinCardInstance = (idx < minCard);
                            g.isExpanded = true;
                            delete g._hideChildrenUntilRepeat;
                        });

                        firstObj._hasRestoredRepeatInstances = true;
                    } else if (minCard >= 1 && emptyInstances.length > 0) {
                        // No data exists, but minCard >= 1, so the first empty instance should be shown as required
                        const firstEmpty = emptyInstances[0];
                        firstEmpty._isRepeatInstance = true;
                        firstEmpty._isTopLevelRepeat = true;
                        firstEmpty._originalOrder = originalOrder;
                        firstEmpty._instanceNumber = 1;
                        firstEmpty._isRequiredMinCardInstance = true;
                        firstEmpty.isExpanded = true;
                        delete firstEmpty._hideChildrenUntilRepeat;

                        // Update tracker to reflect this first instance
                        this.repeatInstanceTracker[originalOrder].count = 1;
                        this.repeatInstanceTracker[originalOrder].instances = [firstEmpty.order];
                        this.repeatInstanceTracker[originalOrder].nextInstanceNumber = 2;

                        firstObj._hasRestoredRepeatInstances = true;

                        // Mark remaining empty instances (if any) as placeholders
                        emptyInstances.slice(1).forEach((g: any) => {
                            g._hideChildrenUntilRepeat = true;
                            g._isRepeatInstance = false;
                            g._originalOrder = originalOrder;
                        });
                    } else {
                        // minCard = 0, mark all empty instances as placeholders
                        emptyInstances.forEach((g: any) => {
                            g._hideChildrenUntilRepeat = true;
                            g._isRepeatInstance = false;
                            g._originalOrder = originalOrder;
                        });
                    }
                }
            }

            node.ChildItems.forEach((wrapper: any) => {
                const child = getWrapperChild(wrapper);
                if (child && child.value) this.detectExistingRepeats(child.value);
            });
        }

        for (const k in node) {
            if (!node.hasOwnProperty(k)) continue;
            if (k === 'ChildItems') continue;
            const v = node[k];
            if (v && typeof v === 'object') this.detectExistingRepeats(v);
        }
    }

    /**
     * For every repeatable Section/Question that hasn't already been handled,
     * ensure minCard required instances exist and append a "+" placeholder.
     */
    private prePopulateMinCard(node: any): void {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) {
            node.forEach((item: any) => this.prePopulateMinCard(item));
            return;
        }

        if (Array.isArray(node.ChildItems)) {
            const snapshot = [...node.ChildItems];
            snapshot.forEach((wrapper: any) => {
                const child = getWrapperChild(wrapper);
                if (!child || !child.value) return;
                const obj = child.value;
                const key = child.key;
                if (!isRepeatable(obj, key)) {
                    // Still descend into non-repeatable containers — repeatable
                    // Sections/Questions are commonly nested inside ordinary ones
                    // (e.g. S_50595 maxCard=10 lives inside S_18063 "TUMOR").
                    this.prePopulateMinCard(obj);
                    return;
                }

                // Skip placeholder sections — they've already been set up and shouldn't be
                // processed as if they were repeatable sections themselves
                if (obj._hideChildrenUntilRepeat === true) {
                    return;
                }

                const maxCard = obj.maxCard;
                // Default minCard to 1 (first instance always present / required).
                const minCardRaw = obj.minCard != null ? obj.minCard : '1';
                const minInstances = parseInt(minCardRaw, 10) || 0;
                const originalOrder = (obj._originalOrder != null ? obj._originalOrder : obj.order);

                // Already processed as a cloned instance — just recurse.
                if (obj._isRepeatInstance === true && !obj._hasRestoredRepeatInstances) {
                    this.prePopulateMinCard(obj);
                    return;
                }

                const isUnlimited = (maxCard === '0' || maxCard === 0);
                const maxRepeats = isUnlimited ? Infinity : parseInt(maxCard as any, 10);

                if (obj._hasRestoredRepeatInstances === true) {
                    // Already initialized from saved data — ensure placeholder present.
                    this.ensureTrailingPlaceholder(node, originalOrder, key, obj, isUnlimited, maxRepeats);
                } else if (minInstances === 0) {
                    // minCard=0 — hide children until user clicks + .
                    obj._hideChildrenUntilRepeat = true;
                    setHideChildrenFlag(obj, true);
                    obj._isRequiredMinCardInstance = false;
                    obj._originalOrder = originalOrder;
                    if (!this.repeatInstanceTracker[originalOrder]) {
                        this.repeatInstanceTracker[originalOrder] = {
                            count: 0, instances: [], nextInstanceNumber: 1,
                            source: JSON.parse(JSON.stringify(obj))  // Store pristine template
                        };
                    }
                } else {
                    // minCard >= 1 — first instance is required (can't be removed).
                    if (this.repeatInstanceTracker[originalOrder]) {
                        // Already processed by detectExistingRepeats in another pass
                        this.ensureTrailingPlaceholder(node, originalOrder, key, obj, isUnlimited, maxRepeats);
                    } else {
                        obj._isRepeatInstance = true;
                        obj._isTopLevelRepeat = true;
                        obj._originalOrder = originalOrder;
                        obj._instanceNumber = 1;
                        obj._isRequiredMinCardInstance = true;
                        obj.isExpanded = true;
                        delete obj._hideChildrenUntilRepeat;

                        this.repeatInstanceTracker[originalOrder] = {
                            count: 1, instances: [originalOrder], nextInstanceNumber: 2,
                            source: JSON.parse(JSON.stringify(obj))  // Store pristine template
                        };

                        // Add additional required instances to reach minCard.
                        for (let i = 1; i < minInstances; i++) {
                            const rootForOrders = this.state ? this.state.translatedJSON : node;
                            const startOrderResolved = parseInt(originalOrder, 10) + (i * 0.1); //getMaxOrder(rootForOrders) + 1;
                            const { clone } = deepCloneWithNewOrders(obj, key, startOrderResolved);
                            clone._isRepeatInstance = true;
                            clone._isTopLevelRepeat = true;
                            clone._originalOrder = originalOrder;
                            clone._instanceNumber = this.repeatInstanceTracker[originalOrder].nextInstanceNumber;
                            clone._isRequiredMinCardInstance = true;
                            clone.isExpanded = true;
                            delete clone._hideChildrenUntilRepeat;

                            this.repeatInstanceTracker[originalOrder].count++;
                            this.repeatInstanceTracker[originalOrder].nextInstanceNumber++;
                            this.repeatInstanceTracker[originalOrder].instances.push(clone.order);

                            const insertAfter = this.findLastInstanceIndex(node, originalOrder);
                            if (insertAfter !== -1) {
                                node.ChildItems.splice(insertAfter + 1, 0, { [key]: clone });
                            }
                        }

                        this.ensureTrailingPlaceholder(node, originalOrder, key, obj, isUnlimited, maxRepeats);
                    }
                }

                this.prePopulateMinCard(obj);
            });
        }

        for (const k in node) {
            if (!node.hasOwnProperty(k)) continue;
            if (k === 'ChildItems') continue;
            const v = node[k];
            if (v && typeof v === 'object') this.prePopulateMinCard(v);
        }
    }

    /** Locate the index of the last existing instance of `originalOrder` in ChildItems. */
    private findLastInstanceIndex(parent: any, originalOrder: any): number {
        if (!parent || !Array.isArray(parent.ChildItems)) return -1;
        for (let i = parent.ChildItems.length - 1; i >= 0; i--) {
            const c = getWrapperChild(parent.ChildItems[i]);
            if (!c || !c.value) continue;
            if (c.value._originalOrder === originalOrder || c.value.order === originalOrder) return i;
        }
        return -1;
    }

    /** Ensure there's a trailing `_hideChildrenUntilRepeat` placeholder after the last instance. */
    private ensureTrailingPlaceholder(
        parent: any, originalOrder: any, key: string, sourceObj: any,
        isUnlimited: boolean, maxRepeats: number,
    ): void {
        if (!Array.isArray(parent.ChildItems)) return;

        // Ensure tracker exists before proceeding
        if (!this.repeatInstanceTracker[originalOrder]) {
            console.warn('No tracker found for originalOrder in ensureTrailingPlaceholder:', originalOrder);
            // Initialize tracker with source
            this.repeatInstanceTracker[originalOrder] = {
                count: 0,
                instances: [],
                nextInstanceNumber: 1,
                source: JSON.parse(JSON.stringify(sourceObj))
            };
        }

        const tracker = this.repeatInstanceTracker[originalOrder];
        const count = tracker.count;
        const canAddMore = isUnlimited || count < maxRepeats;

        const hasPlaceholder = parent.ChildItems.some((w: any) => {
            const c = getWrapperChild(w);
            return c && c.value && c.value._hideChildrenUntilRepeat === true
                && (c.value._originalOrder === originalOrder || c.value.order === originalOrder);
        });
        if (!canAddMore || hasPlaceholder) return;

        // Find the highest decimal order currently in use for this originalOrder
        const baseOrder = parseFloat(originalOrder) || 0;
        let maxDecimal = 0;

        parent.ChildItems.forEach((w: any) => {
            const c = getWrapperChild(w);
            if (c && c.value &&
                !c.value._hideChildrenUntilRepeat &&
                (c.value._originalOrder === originalOrder || c.value.order === originalOrder)) {
                const currentOrder = parseFloat(c.value.order) || 0;
                const decimal = currentOrder - baseOrder;
                if (decimal > maxDecimal) {
                    maxDecimal = decimal;
                }
            }
        });

        // Always increment from the highest found (never reuse a deleted section's order)
        const startOrder = baseOrder + maxDecimal + 0.01;

        // Ensure tracker.source exists to prevent undefined errors
        if (!tracker.source) {
            // Store source if it doesn't exist yet
            tracker.source = JSON.parse(JSON.stringify(sourceObj));
        }

        const { clone } = deepCloneWithNewOrders(tracker.source, key, startOrder);
        clone._hideChildrenUntilRepeat = true;
        setHideChildrenFlag(clone, true);
        clone._isRepeatInstance = false;
        clone._isTopLevelRepeat = false;
        clone._isRequiredMinCardInstance = false;
        clone._originalOrder = originalOrder;
        clone.isExpanded = true;

        const lastIdx = this.findLastInstanceIndex(parent, originalOrder);
        const insertIdx = lastIdx !== -1 ? lastIdx + 1 : parent.ChildItems.length;
        parent.ChildItems.splice(insertIdx, 0, { [key]: clone });
    }

    /**
     * Find the parent ChildItems array that contains `targetObj` so we can
     * mutate it when adding / removing repeat instances.
     */
    private findParentOfObject(root: any, targetObj: any): any | null {
        if (!root || typeof root !== 'object') return null;
        if (Array.isArray(root.ChildItems)) {
            for (const w of root.ChildItems) {
                const c = getWrapperChild(w);
                if (c && c.value === targetObj) return root;
            }
            for (const w of root.ChildItems) {
                const c = getWrapperChild(w);
                if (c && c.value) {
                    const found = this.findParentOfObject(c.value, targetObj);
                    if (found) return found;
                }
            }
        }
        for (const k in root) {
            if (!root.hasOwnProperty(k)) continue;
            if (k === 'ChildItems') continue;
            const v = root[k];
            if (v && typeof v === 'object') {
                const found = this.findParentOfObject(v, targetObj);
                if (found) return found;
            }
        }
        return null;
    }

    /**
     * Remove all placeholder sections with matching originalOrder.
     * This cleans up stale placeholders after reordering.
     */
    private removeOldPlaceholders(parent: any, originalOrder: any): void {
        if (!parent || !Array.isArray(parent.ChildItems)) return;

        // Remove all placeholders with this originalOrder in reverse order to avoid index issues
        // Only check _originalOrder (not current order) because placeholders may have stale orders
        for (let i = parent.ChildItems.length - 1; i >= 0; i--) {
            const w = parent.ChildItems[i];
            const c = getWrapperChild(w);
            if (!c || !c.value) continue;
            const v = c.value;

            // Check if this is a placeholder for this originalOrder
            if (v._hideChildrenUntilRepeat === true && v._originalOrder === originalOrder) {
                parent.ChildItems.splice(i, 1);
            }
        }
    }

    /**
     * Reorder repeat sections to have sequential decimal values after deletion.
     * First section gets originalOrder, second gets originalOrder + 0.01, etc.
     */
    private reorderRepeatSections(parent: any, originalOrder: any): void {
        if (!parent || !Array.isArray(parent.ChildItems)) return;

        const baseOrder = parseFloat(originalOrder) || 0;
        const instances: any[] = [];

        // Collect all non-placeholder instances with this originalOrder
        parent.ChildItems.forEach((w: any) => {
            const c = getWrapperChild(w);
            if (!c || !c.value) return;
            const v = c.value;
            if (v._originalOrder !== originalOrder && v.order !== originalOrder) return;
            if (v._hideChildrenUntilRepeat === true) return;
            instances.push(v);
        });

        // Reorder: first gets baseOrder, second gets baseOrder + 0.01, etc.
        instances.forEach((v: any, idx: number) => {
            const newOrder = baseOrder + (idx * 0.01);
            v.order = parseFloat(newOrder.toFixed(2));

            // Calculate decimal offset for this instance
            const decimalOffset = idx * 0.01;

            // Update all descendant orders with the decimal offset
            this.updateDescendantOrders(v, decimalOffset);
        });

        // Update tracker instances array
        const tracker = this.repeatInstanceTracker[originalOrder];
        if (tracker) {
            tracker.instances = instances.map((v: any) => v.order);
        }
    }

    /**
     * Recursively update orders of all descendants to maintain proper ordering.
     * Applies the parent's decimal offset to all child base orders.
     */
    private updateDescendantOrders(obj: any, parentDecimalOffset: number): void {
        if (!obj || typeof obj !== 'object') return;
        if (Array.isArray(obj)) {
            obj.forEach((item: any) => this.updateDescendantOrders(item, parentDecimalOffset));
            return;
        }

        if (Array.isArray(obj.ChildItems)) {
            obj.ChildItems.forEach((wrapper: any) => {
                const child = getWrapperChild(wrapper);
                if (child && child.value && child.value.order != null) {
                    const currentChildOrder = parseFloat(child.value.order) || 0;
                    const childBase = Math.floor(currentChildOrder);

                    // Apply parent's decimal offset to child's base order
                    // E.g., if parent is 980.02 and child base is 1000, result is 1000.02
                    child.value.order = parseFloat((childBase + parentDecimalOffset).toFixed(2));

                    // Recursively update descendants with same decimal offset
                    this.updateDescendantOrders(child.value, parentDecimalOffset);
                }
            });
        }

        for (const k in obj) {
            if (obj.hasOwnProperty(k) && k !== 'ChildItems') {
                this.updateDescendantOrders(obj[k], parentDecimalOffset);
            }
        }
    }

    /**
     * Renumber every repeat instance under `parent` that shares `originalOrder`
     * so that `_instanceNumber` goes 1, 2, 3, … in ChildItems order. Also
     * rebuilds the tracker's `instances` array and `nextInstanceNumber`.
     */
    private renumberRepeatInstances(parent: any, originalOrder: any): void {
        if (!parent || !Array.isArray(parent.ChildItems)) return;
        const instances: any[] = [];
        parent.ChildItems.forEach((w: any) => {
            const c = getWrapperChild(w);
            if (!c || !c.value) return;
            const v = c.value;
            if (v._originalOrder !== originalOrder && v.order !== originalOrder) return;
            if (v._hideChildrenUntilRepeat === true) return;
            instances.push(v);
        });
        instances.forEach((v: any, idx: number) => {
            v._instanceNumber = idx + 1;
        });
        const tracker = this.repeatInstanceTracker[originalOrder];
        if (tracker) {
            tracker.count = instances.length;
            tracker.instances = instances.map((v: any) => v.order);
            tracker.nextInstanceNumber = instances.length + 1;
        }
    }

    /**
     * User clicked the "+" button on a placeholder (or first minCard=0 instance).
     * Reveals the children and adds a new trailing placeholder (if allowed).
     */
    private handleRepeatPlus(currentObject: any, key: string): void {
        if (this.props.disabled) return;
        const maxCard = currentObject.maxCard;
        const isUnlimited = (maxCard === '0' || maxCard === 0);
        const maxRepeats = isUnlimited ? Infinity : parseInt(maxCard as any, 10);
        const originalOrder = currentObject._originalOrder != null ? currentObject._originalOrder : currentObject.order;
        if (!this.repeatInstanceTracker[originalOrder]) {
            this.repeatInstanceTracker[originalOrder] = {
                count: 0,
                instances: [],
                nextInstanceNumber: 1,
                source: JSON.parse(JSON.stringify(currentObject))
            };
        }
        const tracker = this.repeatInstanceTracker[originalOrder];
        const canAddMore = isUnlimited || tracker.count < maxRepeats;
        if (!canAddMore) {
            alert(`Maximum instances (${maxRepeats}) reached for "${currentObject.title || currentObject.name}"`);
            return;
        }

        // Reveal this instance.
        currentObject._hideChildrenUntilRepeat = false;
        setHideChildrenFlag(currentObject, false);
        if (Array.isArray(currentObject.ChildItems)) {
            currentObject.ChildItems.forEach((w: any) => {
                const c = getWrapperChild(w);
                if (c && c.value) c.value.isExpanded = true;
            });
        }

        // Mark as a repeat instance (so it gets a "−" button next render).
        if (!currentObject._isRepeatInstance) {
            currentObject._isRepeatInstance = true;
            currentObject._isTopLevelRepeat = true;
            currentObject._originalOrder = originalOrder;
            currentObject._instanceNumber = tracker.nextInstanceNumber;
            currentObject._isRequiredMinCardInstance = false;
            tracker.count++;
            tracker.nextInstanceNumber++;
            tracker.instances.push(currentObject.order);
        }

        // Initialize nested repeatable sections within the newly revealed instance.
        // This ensures that child sections with minCard=0 or other repeat settings
        // get properly set up with their own placeholders and tracking.
        this.prePopulateMinCard(currentObject);

        // Add a new placeholder below (if still allowed).
        const parent = this.findParentOfObject(this.state.translatedJSON, currentObject);
        if (parent) {
            this.ensureTrailingPlaceholder(parent, originalOrder, key, currentObject, isUnlimited, maxRepeats);
            this.renumberRepeatInstances(parent, originalOrder);
        }

        updateCompleteStatus(this.state.translatedJSON);
        populateLinkCountsByParent(this.state.translatedJSON);
        this.setState({ translatedJSON: this.state.translatedJSON, rowCount: -1 });
        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
    }

    /**
     * User clicked the "−" button. Removes this instance and, if needed,
     * adds a trailing "+" placeholder so the user can still add another one.
     */
    private handleRepeatMinus(currentObject: any, key: string): void {
        if (this.props.disabled) return;
        if (currentObject._isRequiredMinCardInstance === true) return;

        // Show confirmation dialog with customizable message
        const confirmMessage = this.props.repeatRemoveConfirmMessage ||
            'Are you sure you want to delete this row and all of its children?';
        const confirmTitle = this.props.repeatRemoveConfirmMessageTitle ||
            'Alert';

        // Find hierarchy indices before showing the dialog
        // so we can restore focus after user confirms or cancels
        const parent = this.findParentOfObject(this.state.translatedJSON, currentObject);
        const parentHierarchyIndex = parent ? this.findHierarchyIndexForObject(parent) : -1;
        const currentHierarchyIndex = this.findHierarchyIndexForObject(currentObject);

        const showConfirmPrompt = (window as any).showConfirmPrompt;
        if (typeof showConfirmPrompt === 'function') {
            showConfirmPrompt(confirmMessage,
                () => {  // Clicked Yes
                    this.applyRepeatMinus(currentObject, key, () => {
                        // After state update completes, restore focus to the parent
                        if (parentHierarchyIndex >= 0) {
                            this.focusHierarchyRow(parentHierarchyIndex);
                        }
                    });
                },
                () => {  // Click No - restore focus to the current item
                    if (currentHierarchyIndex >= 0) {
                        this.focusHierarchyRow(currentHierarchyIndex);
                    }
                }, confirmTitle);
        } else {
            if (window.confirm(confirmMessage)) {
                this.applyRepeatMinus(currentObject, key, () => {
                    // After state update completes, restore focus to the parent
                    if (parentHierarchyIndex >= 0) {
                        this.focusHierarchyRow(parentHierarchyIndex);
                    }
                });
            } else {
                // User cancelled - restore focus to the current item
                if (currentHierarchyIndex >= 0) {
                    this.focusHierarchyRow(currentHierarchyIndex);
                }
            }
        }
    }

    private applyRepeatMinus(currentObject: any, key: string, focusCallback?: () => void): void {
        const originalOrder = currentObject._originalOrder != null ? currentObject._originalOrder : currentObject.order;
        const tracker = this.repeatInstanceTracker[originalOrder];
        if (!tracker) return;

        const parent = this.findParentOfObject(this.state.translatedJSON, currentObject);
        if (!parent || !Array.isArray(parent.ChildItems)) return;

        // Remove this wrapper from parent.
        const removeIdx = parent.ChildItems.findIndex((w: any) => {
            const c = getWrapperChild(w);
            return c && c.value === currentObject;
        });
        if (removeIdx === -1) return;
        parent.ChildItems.splice(removeIdx, 1);

        const instIdx = tracker.instances.indexOf(currentObject.order);
        if (instIdx !== -1) {
            tracker.instances.splice(instIdx, 1);
            tracker.count = Math.max(0, tracker.count - 1);
        }

        // Reorder remaining sections: originalOrder, originalOrder + 0.01, originalOrder + 0.02, etc.
        this.reorderRepeatSections(parent, originalOrder);

        // Remove all old placeholders before adding a new one
        this.removeOldPlaceholders(parent, originalOrder);

        // Ensure placeholder at the end (if we can still add more).
        const maxCard = currentObject.maxCard;
        const isUnlimited = (maxCard === '0' || maxCard === 0);
        const maxRepeats = isUnlimited ? Infinity : parseInt(maxCard as any, 10);
        this.ensureTrailingPlaceholder(parent, originalOrder, key, currentObject, isUnlimited, maxRepeats);
        this.renumberRepeatInstances(parent, originalOrder);

        updateCompleteStatus(this.state.translatedJSON);
        populateLinkCountsByParent(this.state.translatedJSON);
        this.setState({ translatedJSON: this.state.translatedJSON, rowCount: -1 }, () => {
            if (focusCallback) {
                focusCallback();
            }
        });
        if (this.props.onSave) this.props.onSave(this.state.translatedJSON);
    }

    /**
     * Render the +/- repeat button for a Section/Question header.
     * Returns null when the object is not repeatable.
     */
    private renderRepeatButton(data: any, key: string): React.ReactNode {
        if (!isRepeatable(data, key)) return null;
        const isPlaceholder = data._hideChildrenUntilRepeat === true;
        const isRepeatInstance = data._isRepeatInstance === true;
        const isRequired = data._isRequiredMinCardInstance === true;
        const showPlus = isPlaceholder;
        const showMinus = !isPlaceholder && isRepeatInstance && !isRequired;
        if (!showPlus && !showMinus) return null;

        const maxCard = data.maxCard;
        const isUnlimited = (maxCard === '0' || maxCard === 0);
        const maxRepeats = isUnlimited ? Infinity : parseInt(maxCard as any, 10);
        const originalOrder = data._originalOrder != null ? data._originalOrder : data.order;
        const tracker = this.repeatInstanceTracker[originalOrder];
        const count = tracker ? tracker.count : 0;
        const canAddMore = isUnlimited || count < maxRepeats;

        const formDisabled = this.props.disabled === true;
        const title = showPlus
            ? (canAddMore
                ? `Add instance`
                : `Maximum instances reached (${maxRepeats})`)
            : `Remove this instance`;
        const disabled = formDisabled || (showPlus && !canAddMore);

        const iconClass = showPlus
            ? 'ui-icon ui-icon-plus'
            : 'ui-icon ui-icon-minus';

        return (
            <button
                type="button"
                className="ui-button ui-widget ui-corner-all ecp-repeat-button"
                data-repeat-button={showPlus ? 'plus' : 'minus'}
                disabled={disabled}
                title={title}
                style={{
                    padding: 0,
                    fontSize: '9pt',
                    marginRight: '2px',
                    marginLeft: 0,
                    width: '22px',
                    height: '22px',
                    lineHeight: 1,
                    verticalAlign: 'middle',
                    flex: '0 0 auto',
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabled) return;
                    if (showPlus) this.handleRepeatPlus(data, key);
                    else this.handleRepeatMinus(data, key);
                }}
            >
                <span
                    className={iconClass}
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        margin: 0,
                        position: 'relative',
                    }}
                />
            </button>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ReactJSONHierarchy Callback Methods (matching original JSP)
    // ──────────────────────────────────��─────────────────────────────────��────

    private getCollapsedStyle(obj: any, key: string, isFocused: boolean): React.CSSProperties {
        if (key === 'Footer') {
            return { marginTop: '0px', paddingLeft: '0px', fontWeight: "bold", marginBottom: "40px" };
        } else if (key === "Question" || key === "Section") {
            return { marginBottom: "0px", marginTop: "0px", paddingLeft: '0px', fontWeight: "bold" };
        } else {
            return { marginTop: '0px', paddingLeft: '0px', fontWeight: "normal", marginBottom: "0px", height: '100%' };
        }
    }

    private getExpandedStyle(obj: any, key: string, isFocused: boolean): React.CSSProperties {
        if (key === 'Footer') {
            return { marginTop: '0px', paddingLeft: '0px', fontWeight: "bold", marginBottom: "40px" };
        } else if (key === "Question" || key === "Section") {
            return { marginBottom: "3px", marginTop: "0px", paddingLeft: '0px', fontWeight: "bold" };
        } else {
            return { marginTop: '0px', paddingLeft: '0px', fontWeight: "normal", marginBottom: "3px", height: '100%' };
        }
    }

    private getContentStyle(obj: any, key: string, indentLeft: number = 0): React.CSSProperties {
        const maxLinkCountWidth = obj.maxLinkCount ? (obj.maxLinkCount * 18) : 0;
        const isPlaceholder = obj._hideChildrenUntilRepeat === true;
        const isRepeatInstance = obj._isRepeatInstance === true && obj._isTopLevelRepeat === true;
        const repeatButtonWidth = (obj.hasRepeatButton && (isPlaceholder || isRepeatInstance)) ? 24 : 0;
        const totalWidth = maxLinkCountWidth + repeatButtonWidth;

        // Match the header layout: the header has 8px CSS paddingLeft + a 16px
        // triangle icon before the title text.  Children in the content div must
        // start at least at the triangle's right edge (8 + 16 = 24px) so they
        // appear visually indented under the parent's title.  When links or
        // repeat buttons are present their reserved width is added on top.
        const basePad = 24;
        return (totalWidth > 0)
            ? { paddingLeft: (totalWidth + basePad) + 'px', marginTop: '0px', fontWeight: "normal" }
            : { paddingLeft: basePad + 'px', marginTop: '0px', fontWeight: "normal" };
    }

    private allowFocusOnRow(obj: any, key: string): boolean {
        if (key === "Footer") {
            return false;
        } else if (key === "ShellItem") {
            const shellItem = obj["ShellItem"];
            if (shellItem && shellItem["shellType"] === "DisplayedItem") {
                return false;
            }
            return true;
        }
        return true;
    }

    private canDisplayRow(row: any, key: string, parentObj: any, parentKey: string, arrayIndex: number): boolean {
        const actualObj = row[key];

        if (parentObj && parentObj._hideChildrenUntilRepeat === true) {
            return false;
        }

        if (!this.state.bHideOptional) return true;
        if (!actualObj) return true;

        if (actualObj.mustImplement !== 'false' && actualObj.mustImplement !== false) {
            return true;
        }

        return false;
    }

    private displayOverride(row: any, key: string, parentObj: any, parentKey: string, arrayIndex: number, hierarchyIndex: number, disabled: boolean): any {
        return this.getECPHierarchyDisplay(row, parentObj, key, arrayIndex, disabled, false, parentKey, hierarchyIndex);
    }

    private getECPHierarchyDisplay(row: any, parentObj: any, key: string, arrayIndex: number, disabled: boolean, report: boolean, parentKey: string, hierarchyIndex: number): any {
        if (key === '') return null;

        let currentObject = (arrayIndex > -1 && Array.isArray(row[key])) ? row[key][arrayIndex] : row[key];
        const wrapped = getWrapperChild(currentObject);
        if (wrapped) {
            currentObject = wrapped.value;
            key = wrapped.key;
        }
        if (!currentObject) return null;

        const resolvedParent = resolveWrapperObject(parentObj);

        let elementDisabled = disabled
            || currentObject._disabled === true
            || currentObject._parentSelected === false
            || (resolvedParent && resolvedParent.shellType && !resolvedParent.selected);

        const elements: any[] = [];

        if (this.props.eCPFormDebug) {
            elements.push(
                <label key="debug" className="redText" style={{ marginRight: '10px' }}>
                    {currentObject.order}
                </label>
            );
        }

        if (key === "FormDesign") {
            elements.push(
                <div key="label" className="flex-container flex-row flex-grow" style={{ height: '16px' }}>
                    <label>{currentObject["formTitle"]}</label>
                </div>
            );
        } else if (key === "Section") {
            elements.push(
                <div key="label" className="flex-container flex-row flex-grow" style={{ height: '16px' }}>
                    <label>{this.getECPTitleWithIndicator(currentObject)}</label>
                    {this.getCompleteIndicator(currentObject, key, elementDisabled)}
                </div>
            );
        } else if (key === "Question") {
            elements.push(
                <div key="label" className="flex-container flex-grow">
                    <label>{this.getECPTitleWithIndicator(currentObject)}</label>
                    {this.getCompleteIndicator(currentObject, key, elementDisabled)}
                </div>
            );
            elements.push(this.getRequiredLabel(currentObject, elementDisabled));
        } else if (key === 'Footer') {
            if (currentObject['Property'] && currentObject['Property'].val) {
                elements.push(
                    <label key="footer" className="grayText">
                        {currentObject['Property'].val}
                    </label>
                );
            }
        } else if (key === "ShellItem") {
            if (currentObject["shellType"] === "DisplayedItem") {
                elements.push(
                    <label key="displayed" className="grayText" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                        {this.getECPTitle(currentObject)}
                    </label>
                );
            } else {
                const isSelected = currentObject.selected === true || currentObject.selected === 'true';
                if (report && !isSelected) return null;

                const maxSelections = resolvedParent ? resolvedParent.maxSelections : null;

                if (maxSelections != null && maxSelections !== "1") {
                    elements.push(this.addCheckboxOption(currentObject, resolvedParent, 'checkBoxOption_' + currentObject.order, isSelected, elementDisabled, report, hierarchyIndex));
                } else {
                    elements.push(this.addRadioOption(currentObject, resolvedParent, 'radioOption_' + currentObject.order, isSelected, elementDisabled, report, hierarchyIndex));
                }
            }
        } else if (key === 'ResponseField' || key === "ListItemResponseField") {
            if (currentObject.Response && currentObject.Response.string) {
                const displayValue = currentObject.Response.string.val || "";
                const isResponseRequired = boolVal(currentObject.responseRequired);
                const useParentSelection = resolvedParent && resolvedParent.shellType != null;
                const isParentSelected = resolvedParent && resolvedParent.selected === true;
                const isDisabled = disabled
                    || currentObject._disabled === true
                    || currentObject._parentSelected === false
                    || (useParentSelection && !isParentSelected);
                const isEmpty = !displayValue || displayValue.trim() === '';
                const required = !isDisabled && isResponseRequired;
                const maxLength = this.parseMaxLength(
                    currentObject.Response.string.maxLength,
                    currentObject.Response.maxLength,
                    currentObject.maxLength,
                );

                elements.push(
                    <ReactAutoExpandTextArea
                        key="textarea"
                        id={"textArea_" + currentObject.Response.order}
                        text={displayValue}
                        css={'textShortcuts' + (required && isEmpty ? ' invalidInput' : '')}
                        style={{ width: '700px', height: '25px', minHeight: '25px', minWidth: '700px', maxWidth: '700px' }}
                        disabled={isDisabled}
                        maxLength={maxLength}
                        row={currentObject.Response.string}
                        dataKey='val'
                        onChange={() => {
                            this.setState({ rowCount: -1 });
                            if (this.props.onSave) {
                                this.props.onSave(this.state.translatedJSON);
                            }
                        }}
                    />
                );
            }
        }

        return (
            <div className="flex-container flex-grow" id={"HRow_" + currentObject.order} style={{ marginRight: '5px' }}>
                {elements}
            </div>
        );
    }

    private getECPTitle(obj: any): string {
        if (obj && obj.title) {
            return String(obj.title).replace(/^[+?]\s*/, '');
        }
        return '';
    }

    private getECPTitleWithIndicator(obj: any): string {
        if (!obj || !obj.title) return '';
        const title = String(obj.title);
        const firstChar = title.charAt(0);
        if (firstChar === '+' || firstChar === '?') {
            return title.substring(1).trim();
        }
        return title;
    }

    private getCompleteIndicator(obj: any, key: string, disabled: boolean): any {
        if (obj.complete === true) {
            return (
                <span key="complete" className="greenText" style={{ marginLeft: '10px' }} title="Complete">
                    ✓
                </span>
            );
        }
        return null;
    }

    private getRequiredLabel(obj: any, disabled: boolean): any {
        const isRequired = !isOptional(obj);
        if (!isRequired) return null;

        const hasOverride = obj.overrideReason && obj.overrideReason !== '';
        const className = hasOverride ? 'redText' : 'blackText';
        const style: React.CSSProperties = {
            marginLeft: '10px',
            textDecoration: hasOverride ? 'line-through' : 'none',
            fontSize: '9pt'
        };

        return (
            <span key="required" className={className} style={style} title={hasOverride ? 'Required Question has been overridden' : 'Required'}>
                Required
            </span>
        );
    }

    private addRadioOption(obj: any, parentObj: any, radioID: string, isSelected: boolean, disabled: boolean, report: boolean, hierarchyIndex: number): any {
        const title = this.getECPTitle(obj);
        const inputId = radioID + "_Input";
        const name = parentObj ? `radio_${parentObj.name || parentObj.order || hierarchyIndex}` : `radio_${hierarchyIndex}`;

        return (
            <label key="radio" style={{ cursor: disabled ? 'default' : 'pointer', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', display: 'flex', alignItems: 'center' }}>
                <input
                    type="radio"
                    id={inputId}
                    name={name}
                    checked={isSelected}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!disabled) {
                            this.activateRadioSelection(obj, parentObj);
                        }
                    }}
                    style={{ marginRight: '6px' }}
                />
                <span>{title}</span>
            </label>
        );
    }

    private addCheckboxOption(obj: any, parentObj: any, checkBoxID: string, isSelected: boolean, disabled: boolean, report: boolean, hierarchyIndex: number): any {
        const title = this.getECPTitle(obj);
        const inputId = checkBoxID + "_Input";

        return (
            <label key="checkbox" style={{ cursor: disabled ? 'default' : 'pointer', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', display: 'flex', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    id={inputId}
                    checked={isSelected}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!disabled) {
                            this.activateCheckboxSelection(obj, parentObj, e.target.checked);
                        }
                    }}
                    style={{ marginRight: '6px' }}
                />
                <span>{title}</span>
            </label>
        );
    }

    private activateRadioSelection(obj: any, parentObj: any) {
        // Unselect all siblings
        const shellItems = getShellItemsFromList(parentObj);
        shellItems.forEach((item: any) => {
            item.selected = false;
        });

        // Select this one
        obj.selected = true;

        // Update state and notify
        this.setState({ rowCount: -1 });
        if (this.props.onSave) {
            this.props.onSave(this.state.translatedJSON);
        }
    }

    private activateCheckboxSelection(obj: any, parentObj: any, checked: boolean) {
        obj.selected = checked;

        // Check maxSelections limit
        if (checked && parentObj && parentObj.maxSelections) {
            const maxSel = parseInt(parentObj.maxSelections);
            if (!isNaN(maxSel) && maxSel > 0) {
                const shellItems = getShellItemsFromList(parentObj);
                const selectedCount = shellItems.filter((item: any) => item.selected === true || item.selected === 'true').length;

                if (selectedCount > maxSel) {
                    obj.selected = false;
                    console.warn('[ECP] Max selections exceeded:', maxSel);
                    return;
                }
            }
        }

        // Update state and notify
        this.setState({ rowCount: -1 });
        if (this.props.onSave) {
            this.props.onSave(this.state.translatedJSON);
        }
    }

    // ────────���─────────────────────────────────���──────────────────────────────
    // Render
    // ───────────────────��─────────────────────────────────────────────────────

    /**
     * Render hidden companion buttons for each of the legacy DOM-ID hooks
     * (refreshID, expandedID, collapseID, collapseCompleteID, disableID).
     *
     * The original ReactJSONHierarchy used to render these so that external
     * HTML/JSP pages could trigger hierarchy actions by calling
     * `document.getElementById('eCPExpand').click()` etc. Preserving that
     * API is essential — many host pages (including apeCPHierarchyList_NEW.jsp)
     * still go through this path when `ecpHierarchyComponent` reference
     * isn't yet populated.
     */
    private renderHiddenControlButtons(): React.ReactNode {
        const hiddenStyle: React.CSSProperties = {
            position: 'absolute',
            width: 0,
            height: 0,
            padding: 0,
            margin: 0,
            border: 0,
            overflow: 'hidden',
            opacity: 0,
            pointerEvents: 'none',
        };
        // The original ReactJSONHierarchy rendered buttons at BOTH the prop-
        // named IDs (refreshID/expandedID/collapseID/...) AND the legacy
        // `{id}_expandAll`, `{id}_collapseAll`, `{id}_collapseComplete`
        // convention IDs. Host pages (apeCPHierarchyList_NEW.jsp, Copia
        // screens, etc.) use whichever was most convenient at the time, so
        // we render both forms to stay compatible.
        const baseId = this.props.id;
        const onCollapseCompleteClick = () => this.collapseComplete();
        return (
            <span aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                {this.props.refreshID && (
                    <button type="button" tabIndex={-1} id={this.props.refreshID}
                        style={hiddenStyle} onClick={this.onRefresh}>refresh</button>
                )}
                {baseId && (
                    <button type="button" tabIndex={-1} id={baseId + '_refresh'}
                        style={hiddenStyle} onClick={this.onRefresh}>refresh</button>
                )}
                {this.props.expandedID && (
                    <button type="button" tabIndex={-1} id={this.props.expandedID}
                        style={hiddenStyle} onClick={this.onExpandAll}>expand</button>
                )}
                {baseId && (
                    <button type="button" tabIndex={-1} id={baseId + '_expandAll'}
                        style={hiddenStyle} onClick={this.onExpandAll}>expand</button>
                )}
                {this.props.collapseID && (
                    <button type="button" tabIndex={-1} id={this.props.collapseID}
                        style={hiddenStyle} onClick={this.onCollapseAll}>collapse</button>
                )}
                {baseId && (
                    <button type="button" tabIndex={-1} id={baseId + '_collapseAll'}
                        style={hiddenStyle} onClick={this.onCollapseAll}>collapse</button>
                )}
                {this.props.collapseCompleteID && (
                    <button type="button" tabIndex={-1} id={this.props.collapseCompleteID}
                        style={hiddenStyle} onClick={onCollapseCompleteClick}>collapseComplete</button>
                )}
                {baseId && (
                    <button type="button" tabIndex={-1} id={baseId + '_collapseComplete'}
                        style={hiddenStyle} onClick={onCollapseCompleteClick}>collapseComplete</button>
                )}
                {this.props.disableID && (
                    <input type="checkbox" tabIndex={-1} id={this.props.disableID}
                        style={hiddenStyle} checked={!!this.props.disabled}
                        readOnly={true} onChange={() => {/* state driven by props */}} />
                )}
            </span>
        );
    }

    /**
     * Root render. Runs completeness calculation on every render (so the
     * green ✓ reflects the latest data), then outputs a scroll container
     * that holds the whole hierarchy. A loading spinner is shown while
     * `isExpandingOnLoad` is true (during the expand-all-on-load pass).
     */
    render(): React.ReactNode {
        const { translatedJSON, isExpandingOnLoad } = this.state;

        // Lazy init for repeat sections. Re-runs whenever the data tree has
        // been wiped/refilled (e.g. the host JSP calls initializeECPHierarchy
        // again — which clears and repopulates translatedJSON in place). We
        // use a sentinel directly on translatedJSON so it travels with the
        // data: if the data is cleared, the sentinel is cleared too, and we
        // re-run the init. This also rescues the very-first render when the
        // constructor ran against empty data.
        if (translatedJSON && !translatedJSON._repeatsInitialized && this.hasAnyRepeatable(translatedJSON)) {
            this.initRepeatSections(translatedJSON);
            populateLinkCountsByParent(translatedJSON);
            translatedJSON._repeatsInitialized = true;
        }

        // Keep `complete` flags fresh for every render.
        // Run propagateFlags FIRST so readOnly/disabled state is stamped on
        // every node before completeness is computed and before the render
        // path reads `_disabled` to decide whether inputs are tabbable.
        propagateFlags(translatedJSON);
        updateCompleteStatus(translatedJSON);

        // JRC 05/13/26 CO-16108 Applied OnComplete
        const formComplete = translatedJSON ? computeOverallComplete(translatedJSON) : false;
        if (this._lastCompleteState !== formComplete) {
            this._lastCompleteState = formComplete;
            if (this.props.onComplete) {
                const cb = this.props.onComplete;
                setTimeout(() => cb(formComplete), 0);
            }
        }

        const outerClass = 'ecp-hierarchy-container'
            + (this.props.css ? ' ' + this.props.css : '')
            + (this.props.divContainerCSS ? ' ' + this.props.divContainerCSS : '');
        const outerStyle: React.CSSProperties = {
            position: 'relative',
            ...(this.props.divContainerStyle || {}),
            ...(this.props.style || {}),
        };

        return (
            <div
                id={this.props.id}
                className={outerClass}
                style={outerStyle}
            >
                {this.renderHiddenControlButtons()}
                {(() => {
                    // JRC 05/13/26 CO-16108 Add hidden Complete ID
                    const completeId = this.props.hiddenFormCompleteID
                        || (this.props.id ? this.props.id + '_formComplete' : null);
                    if (!completeId) return null;
                    return (
                        <input
                            type="hidden"
                            id={completeId}
                            value={formComplete ? 'true' : 'false'}
                            data-complete={formComplete ? 'true' : 'false'}
                            readOnly={true}
                        />
                    );
                })()}
                {isExpandingOnLoad ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '120px',
                            width: '100%',
                        }}
                    >
                        <div className="ecp-loading-spinner" />
                    </div>
                ) : (
                    <div
                        ref={this.attachContainerRef}
                        tabIndex={-1}
                        style={{ outline: 'none', width: '100%', height: '100%' }}
                    >
                        {this.renderHierarchy(translatedJSON)}
                    </div>
                )}
            </div>
        );
    }
}

// ───────────────────────────────────────────────────��──────────────────��──────
// Factory Functions
// ─────────────────��─────────────────���─────────────────────────────��───────────

/**
 * Creates an ECP Hierarchy List component and renders it to the DOM
 */
export function createECPHierarchyList(
    id: string,
    props: ECPHierarchyProps,
    refCallback?: (component: ECPHierarchyList) => void
) {
    ReactDOM.render(
        getECPHierarchyList(props, refCallback),
        document.getElementById(id) as HTMLElement
    );
}

/**
 * Gets an ECP Hierarchy List component (for embedding in other React components)
 */
export function getECPHierarchyList(
    props: ECPHierarchyProps,
    refCallback?: (component: ECPHierarchyList) => void
) {

    return <ECPHierarchyList {...props} ref={refCallback} />;
}

/**
 * Calculate maxLinkCount for each parent node by examining all its children.
 * This ensures proper alignment - all accordion arrows appear at the same horizontal position
 * even when some siblings have 0 links, some have 1, some have 2 or 3, etc.
 * Each link takes 18px width.
 */
function populateLinkCountsByParent(node: any): void {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
        node.forEach((item: any) => populateLinkCountsByParent(item));
        return;
    }

    if (Array.isArray(node.ChildItems)) {
        let maxLinks = 0;

        node.ChildItems.forEach((wrapper: any) => {
            const child = getWrapperChild(wrapper);
            if (!child) return;

            // Count links
            const count = child.value && child.value.Link
                ? (Array.isArray(child.value.Link) ? child.value.Link.length : 1)
                : 0;
            if (count > maxLinks) maxLinks = count;

            // Mark hasRepeatButton ONLY on the actually-repeatable child
            // (maxCard > 1 or maxCard = 0). Do NOT propagate to siblings —
            // previously we did, which made every sibling reserve 24px of
            // layout space for a button it wouldn't render.
            const maxCard = child.value && child.value.maxCard;
            const isRepeatable = maxCard && (maxCard !== '1' && maxCard !== 1) &&
                (child.key === 'Section' || child.key === 'Question');
            if (isRepeatable && typeof child.value === 'object') {
                child.value.hasRepeatButton = true;
                wrapper.hasRepeatButton = true;
            }

            // Recurse into child
            populateLinkCountsByParent(child.value);
        });

        // maxLinkCount still propagates to all siblings for alignment.
        node.maxLinkCount = maxLinks;

        node.ChildItems.forEach((wrapper: any) => {
            if (!wrapper || typeof wrapper !== 'object') return;
            wrapper.maxLinkCount = maxLinks;
            const child = getWrapperChild(wrapper);
            if (child && typeof child.value === 'object') {
                child.value.maxLinkCount = maxLinks;
            }
        });
    }

    // Recurse into other properties
    for (const key in node) {
        if (!node.hasOwnProperty(key)) continue;
        if (key === 'ChildItems' || key === 'Link' || key === 'maxLinkCount' || key === 'hasRepeatButton') continue;
        const val = node[key];
        if (val && typeof val === 'object') {
            populateLinkCountsByParent(val);
        }
    }
}

